function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var React = require('react');
var operators = require('rxjs/operators');
var reactRouterDom = require('react-router-dom');
var queryString = _interopDefault(require('query-string'));
var jsxRuntime = require('react/jsx-runtime');
var ReactDOM = require('react-dom');

function useBehaviorSubject(subject) {
  var _React$useState = React.useState(subject.getValue()),
    state = _React$useState[0],
    setState = _React$useState[1];
  React.useEffect(function () {
    var subscription = subject.pipe(operators.skip(1)).subscribe(setState);
    return function () {
      return subscription.unsubscribe();
    };
  }, [subject]);
  var setNewValue = React.useCallback(function (newValue) {
    subject.next(typeof newValue === 'function' ? newValue(subject.getValue()) : newValue);
  }, [subject]);
  return [state, setNewValue];
}

function useLocalStorage(_ref) {
  var key = _ref.key,
    initialValue = _ref.initialValue;
  var _React$useState = React.useState(function () {
      try {
        var item = window.localStorage.getItem(key);
        return item ? JSON.parse(item) : initialValue;
      } catch (error) {
        return initialValue;
      }
    }),
    storedValue = _React$useState[0],
    setStoredValue = _React$useState[1];
  var setValue = React.useCallback(function (value) {
    setStoredValue(function (prevValue) {
      var valueToStore = value instanceof Function ? value(prevValue) : value;
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
      return valueToStore;
    });
  }, [key]);
  return [storedValue, setValue];
}

function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : String(i);
}
function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor);
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  _setPrototypeOf(subClass, superClass);
}
function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);
  if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function () {
      if (i >= o.length) return {
        done: true
      };
      return {
        done: false,
        value: o[i++]
      };
    };
  }
  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function useQueryState(name, fallbackValue) {
  var location = reactRouterDom.useLocation();
  var history = reactRouterDom.useHistory();
  var queryParams = React.useMemo(function () {
    return queryString.parse(location.search, {
      parseNumbers: true
    });
  }, [location.search]);
  var queryParamsRef = React.useRef(queryParams);
  var value = queryParams[name] || fallbackValue;
  var setValue = React.useCallback(function (newValue) {
    var curQueryParams = queryParamsRef.current;
    var curValue = curQueryParams[name];
    var updatedValue = typeof newValue === 'function' ? newValue(curValue) : newValue;
    if (updatedValue !== curValue) {
      var _extends2;
      history.replace(_extends({}, history.location, {
        search: queryString.stringify(_extends({}, curQueryParams, (_extends2 = {}, _extends2[name] = newValue, _extends2)), {
          skipNull: true
        })
      }));
    }
  }, [history, name]);
  React.useEffect(function () {
    queryParamsRef.current = queryParams;
  }, [queryParams]);
  return [value, setValue];
}

function useSessionStorage(_ref) {
  var key = _ref.key,
    initialValue = _ref.initialValue;
  var _React$useState = React.useState(function () {
      try {
        var item = window.sessionStorage.getItem(key);
        return item ? JSON.parse(item) : initialValue;
      } catch (e) {
        return initialValue;
      }
    }),
    storedValue = _React$useState[0],
    setStoredValue = _React$useState[1];
  var setValue = React.useCallback(function (value) {
    try {
      var valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {}
  }, [key, storedValue]);
  return [storedValue, setValue];
}

var _extendStatics = function extendStatics(d, b) {
  _extendStatics = Object.setPrototypeOf || {
    __proto__: []
  } instanceof Array && function (d, b) {
    d.__proto__ = b;
  } || function (d, b) {
    for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p];
  };
  return _extendStatics(d, b);
};
function __extends(d, b) {
  if (typeof b !== "function" && b !== null) throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
  _extendStatics(d, b);
  function __() {
    this.constructor = d;
  }
  d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}
var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return _assign.apply(this, arguments);
};
function __rest$1(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __awaiter$1(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}
function __generator(thisArg, body) {
  var _ = {
      label: 0,
      sent: function sent() {
        if (t[0] & 1) throw t[1];
        return t[1];
      },
      trys: [],
      ops: []
    },
    f,
    y,
    t,
    g;
  return g = {
    next: verb(0),
    "throw": verb(1),
    "return": verb(2)
  }, typeof Symbol === "function" && (g[Symbol.iterator] = function () {
    return this;
  }), g;
  function verb(n) {
    return function (v) {
      return step([n, v]);
    };
  }
  function step(op) {
    if (f) throw new TypeError("Generator is already executing.");
    while (_) try {
      if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
      if (y = 0, t) op = [op[0] & 2, t.value];
      switch (op[0]) {
        case 0:
        case 1:
          t = op;
          break;
        case 4:
          _.label++;
          return {
            value: op[1],
            done: false
          };
        case 5:
          _.label++;
          y = op[1];
          op = [0];
          continue;
        case 7:
          op = _.ops.pop();
          _.trys.pop();
          continue;
        default:
          if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
            _ = 0;
            continue;
          }
          if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
            _.label = op[1];
            break;
          }
          if (op[0] === 6 && _.label < t[1]) {
            _.label = t[1];
            t = op;
            break;
          }
          if (t && _.label < t[2]) {
            _.label = t[2];
            _.ops.push(op);
            break;
          }
          if (t[2]) _.ops.pop();
          _.trys.pop();
          continue;
      }
      op = body.call(thisArg, _);
    } catch (e) {
      op = [6, e];
      y = 0;
    } finally {
      f = t = 0;
    }
    if (op[0] & 5) throw op[1];
    return {
      value: op[0] ? op[1] : void 0,
      done: true
    };
  }
}
function getDefaultExportFromCjs(x) {
  return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}
var NODE_ENV = process.env.NODE_ENV;
var invariant = function invariant(condition, format, a, b, c, d, e, f) {
  if (NODE_ENV !== 'production') {
    if (format === undefined) {
      throw new Error('invariant requires an error message argument');
    }
  }
  if (!condition) {
    var error;
    if (format === undefined) {
      error = new Error('Minified exception occurred; use the non-minified dev environment ' + 'for the full error message and additional helpful warnings.');
    } else {
      var args = [a, b, c, d, e, f];
      var argIndex = 0;
      error = new Error(format.replace(/%s/g, function () {
        return args[argIndex++];
      }));
      error.name = 'Invariant Violation';
    }
    error.framesToPop = 1;
    throw error;
  }
};
var invariant_1 = invariant;
var invariant$1 = /*@__PURE__*/getDefaultExportFromCjs(invariant_1);
var MapContext = React.createContext(null);
function useGoogleMap() {
  invariant$1(!!React.useContext, 'useGoogleMap is React hook and requires React version 16.8+');
  var map = React.useContext(MapContext);
  invariant$1(!!map, 'useGoogleMap needs a GoogleMap available up in the tree');
  return map;
}
function reduce(obj, fn, acc) {
  return Object.keys(obj).reduce(function reducer(newAcc, key) {
    return fn(newAcc, obj[key], key);
  }, acc);
}
function forEach(obj, fn) {
  Object.keys(obj).forEach(function (key) {
    return fn(obj[key], key);
  });
}
function applyUpdaterToNextProps(updaterMap, prevProps, nextProps, instance) {
  var map = {};
  var iter = function iter(fn, key) {
    var nextValue = nextProps[key];
    if (nextValue !== prevProps[key]) {
      map[key] = nextValue;
      fn(instance, nextValue);
    }
  };
  forEach(updaterMap, iter);
  return map;
}
function registerEvents(props, instance, eventMap) {
  var registeredList = reduce(eventMap, function reducer(acc, googleEventName, onEventName) {
    if (typeof props[onEventName] === 'function') {
      acc.push(google.maps.event.addListener(instance, googleEventName, props[onEventName]));
    }
    return acc;
  }, []);
  return registeredList;
}
function unregisterEvent(registered) {
  google.maps.event.removeListener(registered);
}
function unregisterEvents(events) {
  if (events === void 0) {
    events = [];
  }
  events.forEach(unregisterEvent);
}
function applyUpdatersToPropsAndRegisterEvents(_a) {
  var updaterMap = _a.updaterMap,
    eventMap = _a.eventMap,
    prevProps = _a.prevProps,
    nextProps = _a.nextProps,
    instance = _a.instance;
  var registeredEvents = registerEvents(nextProps, instance, eventMap);
  applyUpdaterToNextProps(updaterMap, prevProps, nextProps, instance);
  return registeredEvents;
}
var eventMap$i = {
  onDblClick: 'dblclick',
  onDragEnd: 'dragend',
  onDragStart: 'dragstart',
  onMapTypeIdChanged: 'maptypeid_changed',
  onMouseMove: 'mousemove',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover',
  onMouseDown: 'mousedown',
  onMouseUp: 'mouseup',
  onRightClick: 'rightclick',
  onTilesLoaded: 'tilesloaded',
  onBoundsChanged: 'bounds_changed',
  onCenterChanged: 'center_changed',
  onClick: 'click',
  onDrag: 'drag',
  onHeadingChanged: 'heading_changed',
  onIdle: 'idle',
  onProjectionChanged: 'projection_changed',
  onResize: 'resize',
  onTiltChanged: 'tilt_changed',
  onZoomChanged: 'zoom_changed'
};
var updaterMap$i = {
  extraMapTypes: function extraMapTypes(map, extra) {
    extra.forEach(function forEachExtra(it, i) {
      map.mapTypes.set(String(i), it);
    });
  },
  center: function center(map, _center) {
    map.setCenter(_center);
  },
  clickableIcons: function clickableIcons(map, clickable) {
    map.setClickableIcons(clickable);
  },
  heading: function heading(map, _heading) {
    map.setHeading(_heading);
  },
  mapTypeId: function mapTypeId(map, _mapTypeId) {
    map.setMapTypeId(_mapTypeId);
  },
  options: function options(map, _options) {
    map.setOptions(_options);
  },
  streetView: function streetView(map, _streetView) {
    map.setStreetView(_streetView);
  },
  tilt: function tilt(map, _tilt) {
    map.setTilt(_tilt);
  },
  zoom: function zoom(map, _zoom) {
    map.setZoom(_zoom);
  }
};
function GoogleMapFunctional(_a) {
  var children = _a.children,
    options = _a.options,
    id = _a.id,
    mapContainerStyle = _a.mapContainerStyle,
    mapContainerClassName = _a.mapContainerClassName,
    center = _a.center,
    onClick = _a.onClick,
    onDblClick = _a.onDblClick,
    onDrag = _a.onDrag,
    onDragEnd = _a.onDragEnd,
    onDragStart = _a.onDragStart,
    onMouseMove = _a.onMouseMove,
    onMouseOut = _a.onMouseOut,
    onMouseOver = _a.onMouseOver,
    onMouseDown = _a.onMouseDown,
    onMouseUp = _a.onMouseUp,
    onRightClick = _a.onRightClick,
    onCenterChanged = _a.onCenterChanged,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var _b = React.useState(null),
    map = _b[0],
    setMap = _b[1];
  var ref = React.useRef(null);
  var _c = React.useState(null),
    centerChangedListener = _c[0],
    setCenterChangedListener = _c[1];
  var _d = React.useState(null),
    dblclickListener = _d[0],
    setDblclickListener = _d[1];
  var _e = React.useState(null),
    dragendListener = _e[0],
    setDragendListener = _e[1];
  var _f = React.useState(null),
    dragstartListener = _f[0],
    setDragstartListener = _f[1];
  var _g = React.useState(null),
    mousedownListener = _g[0],
    setMousedownListener = _g[1];
  var _h = React.useState(null),
    mousemoveListener = _h[0],
    setMousemoveListener = _h[1];
  var _j = React.useState(null),
    mouseoutListener = _j[0],
    setMouseoutListener = _j[1];
  var _k = React.useState(null),
    mouseoverListener = _k[0],
    setMouseoverListener = _k[1];
  var _l = React.useState(null),
    mouseupListener = _l[0],
    setMouseupListener = _l[1];
  var _m = React.useState(null),
    rightclickListener = _m[0],
    setRightclickListener = _m[1];
  var _o = React.useState(null),
    clickListener = _o[0],
    setClickListener = _o[1];
  var _p = React.useState(null),
    dragListener = _p[0],
    setDragListener = _p[1];
  React.useEffect(function () {
    if (options && map !== null) {
      map.setOptions(options);
    }
  }, [map, options]);
  React.useEffect(function () {
    if (map !== null && typeof center !== 'undefined') {
      map.setCenter(center);
    }
  }, [map, center]);
  React.useEffect(function () {
    if (map && onDblClick) {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      setDblclickListener(google.maps.event.addListener(map, 'dblclick', onDblClick));
    }
  }, [onDblClick]);
  React.useEffect(function () {
    if (map && onDragEnd) {
      if (dragendListener !== null) {
        google.maps.event.removeListener(dragendListener);
      }
      setDragendListener(google.maps.event.addListener(map, 'dragend', onDragEnd));
    }
  }, [onDragEnd]);
  React.useEffect(function () {
    if (map && onDragStart) {
      if (dragstartListener !== null) {
        google.maps.event.removeListener(dragstartListener);
      }
      setDragstartListener(google.maps.event.addListener(map, 'dragstart', onDragStart));
    }
  }, [onDragStart]);
  React.useEffect(function () {
    if (map && onMouseDown) {
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      setMousedownListener(google.maps.event.addListener(map, 'mousedown', onMouseDown));
    }
  }, [onMouseDown]);
  React.useEffect(function () {
    if (map && onMouseMove) {
      if (mousemoveListener !== null) {
        google.maps.event.removeListener(mousemoveListener);
      }
      setMousemoveListener(google.maps.event.addListener(map, 'mousemove', onMouseMove));
    }
  }, [onMouseMove]);
  React.useEffect(function () {
    if (map && onMouseOut) {
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      setMouseoutListener(google.maps.event.addListener(map, 'mouseout', onMouseOut));
    }
  }, [onMouseOut]);
  React.useEffect(function () {
    if (map && onMouseOver) {
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      setMouseoverListener(google.maps.event.addListener(map, 'mouseover', onMouseOver));
    }
  }, [onMouseOver]);
  React.useEffect(function () {
    if (map && onMouseUp) {
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      setMouseupListener(google.maps.event.addListener(map, 'mouseup', onMouseUp));
    }
  }, [onMouseUp]);
  React.useEffect(function () {
    if (map && onRightClick) {
      if (rightclickListener !== null) {
        google.maps.event.removeListener(rightclickListener);
      }
      setRightclickListener(google.maps.event.addListener(map, 'rightclick', onRightClick));
    }
  }, [onRightClick]);
  React.useEffect(function () {
    if (map && onClick) {
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      setClickListener(google.maps.event.addListener(map, 'click', onClick));
    }
  }, [onClick]);
  React.useEffect(function () {
    if (map && onDrag) {
      if (dragListener !== null) {
        google.maps.event.removeListener(dragListener);
      }
      setDragListener(google.maps.event.addListener(map, 'drag', onDrag));
    }
  }, [onDrag]);
  React.useEffect(function () {
    if (map && onCenterChanged) {
      if (centerChangedListener !== null) {
        google.maps.event.removeListener(centerChangedListener);
      }
      setCenterChangedListener(google.maps.event.addListener(map, 'center_changed', onCenterChanged));
    }
  }, [onClick]);
  React.useEffect(function () {
    var map = ref.current === null ? null : new google.maps.Map(ref.current, options);
    setMap(map);
    if (map !== null && onLoad) {
      onLoad(map);
    }
    return function () {
      if (map !== null) {
        if (onUnmount) {
          onUnmount(map);
        }
      }
    };
  }, []);
  return jsxRuntime.jsx("div", {
    id: id,
    ref: ref,
    style: mapContainerStyle,
    className: mapContainerClassName,
    children: jsxRuntime.jsx(MapContext.Provider, {
      value: map,
      children: map !== null ? children : jsxRuntime.jsx(jsxRuntime.Fragment, {})
    })
  });
}
React.memo(GoogleMapFunctional);
var GoogleMap = function (_super) {
  __extends(GoogleMap, _super);
  function GoogleMap() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.state = {
      map: null
    };
    _this.registeredEvents = [];
    _this.mapRef = null;
    _this.getInstance = function () {
      if (_this.mapRef === null) {
        return null;
      }
      return new google.maps.Map(_this.mapRef, _this.props.options);
    };
    _this.panTo = function (latLng) {
      var map = _this.getInstance();
      if (map) {
        map.panTo(latLng);
      }
    };
    _this.setMapCallback = function () {
      if (_this.state.map !== null) {
        if (_this.props.onLoad) {
          _this.props.onLoad(_this.state.map);
        }
      }
    };
    _this.getRef = function (ref) {
      _this.mapRef = ref;
    };
    return _this;
  }
  GoogleMap.prototype.componentDidMount = function () {
    var map = this.getInstance();
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$i,
      eventMap: eventMap$i,
      prevProps: {},
      nextProps: this.props,
      instance: map
    });
    this.setState(function setMap() {
      return {
        map: map
      };
    }, this.setMapCallback);
  };
  GoogleMap.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.map !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$i,
        eventMap: eventMap$i,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.map
      });
    }
  };
  GoogleMap.prototype.componentWillUnmount = function () {
    if (this.state.map !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.map);
      }
      unregisterEvents(this.registeredEvents);
    }
  };
  GoogleMap.prototype.render = function () {
    return jsxRuntime.jsx("div", {
      id: this.props.id,
      ref: this.getRef,
      style: this.props.mapContainerStyle,
      className: this.props.mapContainerClassName,
      children: jsxRuntime.jsx(MapContext.Provider, {
        value: this.state.map,
        children: this.state.map !== null ? this.props.children : jsxRuntime.jsx(jsxRuntime.Fragment, {})
      })
    });
  };
  return GoogleMap;
}(React.PureComponent);
var isBrowser = typeof document !== 'undefined';
function injectScript(_a) {
  var url = _a.url,
    id = _a.id,
    nonce = _a.nonce;
  if (!isBrowser) {
    return Promise.reject(new Error('document is undefined'));
  }
  return new Promise(function injectScriptCallback(resolve, reject) {
    var existingScript = document.getElementById(id);
    var windowWithGoogleMap = window;
    if (existingScript) {
      var dataStateAttribute = existingScript.getAttribute('data-state');
      if (existingScript.src === url && dataStateAttribute !== 'error') {
        if (dataStateAttribute === 'ready') {
          return resolve(id);
        } else {
          var originalInitMap_1 = windowWithGoogleMap.initMap;
          var originalErrorCallback_1 = existingScript.onerror;
          windowWithGoogleMap.initMap = function initMap() {
            if (originalInitMap_1) {
              originalInitMap_1();
            }
            resolve(id);
          };
          existingScript.onerror = function (err) {
            if (originalErrorCallback_1) {
              originalErrorCallback_1(err);
            }
            reject(err);
          };
          return;
        }
      } else {
        existingScript.remove();
      }
    }
    var script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = url;
    script.id = id;
    script.async = true;
    script.nonce = nonce || '';
    script.onerror = function onerror(err) {
      script.setAttribute('data-state', 'error');
      reject(err);
    };
    windowWithGoogleMap.initMap = function onload() {
      script.setAttribute('data-state', 'ready');
      resolve(id);
    };
    document.head.appendChild(script);
  })["catch"](function (err) {
    console.error('injectScript error: ', err);
    throw err;
  });
}
function isGoogleFontStyle(element) {
  var href = element.href;
  if (href && (href.indexOf('https://fonts.googleapis.com/css?family=Roboto') === 0 || href.indexOf('https://fonts.googleapis.com/css?family=Google+Sans+Text') === 0)) {
    return true;
  }
  if (element.tagName.toLowerCase() === 'style' && element.styleSheet && element.styleSheet.cssText && element.styleSheet.cssText.replace('\r\n', '').indexOf('.gm-style') === 0) {
    element.styleSheet.cssText = '';
    return true;
  }
  if (element.tagName.toLowerCase() === 'style' && element.innerHTML && element.innerHTML.replace('\r\n', '').indexOf('.gm-style') === 0) {
    element.innerHTML = '';
    return true;
  }
  if (element.tagName.toLowerCase() === 'style' && !element.styleSheet && !element.innerHTML) {
    return true;
  }
  return false;
}
function preventGoogleFonts() {
  var head = document.getElementsByTagName('head')[0];
  if (head) {
    var trueInsertBefore_1 = head.insertBefore.bind(head);
    head.insertBefore = function insertBefore(newElement, referenceElement) {
      if (!isGoogleFontStyle(newElement)) {
        Reflect.apply(trueInsertBefore_1, head, [newElement, referenceElement]);
      }
      return newElement;
    };
    var trueAppend_1 = head.appendChild.bind(head);
    head.appendChild = function appendChild(textNode) {
      if (!isGoogleFontStyle(textNode)) {
        Reflect.apply(trueAppend_1, head, [textNode]);
      }
      return textNode;
    };
  }
}
function makeLoadScriptUrl(_a) {
  var googleMapsApiKey = _a.googleMapsApiKey,
    googleMapsClientId = _a.googleMapsClientId,
    _b = _a.version,
    version = _b === void 0 ? 'weekly' : _b,
    language = _a.language,
    region = _a.region,
    libraries = _a.libraries,
    channel = _a.channel,
    mapIds = _a.mapIds,
    authReferrerPolicy = _a.authReferrerPolicy;
  var params = [];
  invariant$1(googleMapsApiKey && googleMapsClientId || !(googleMapsApiKey && googleMapsClientId), 'You need to specify either googleMapsApiKey or googleMapsClientId for @react-google-maps/api load script to work. You cannot use both at the same time.');
  if (googleMapsApiKey) {
    params.push("key=".concat(googleMapsApiKey));
  } else if (googleMapsClientId) {
    params.push("client=".concat(googleMapsClientId));
  }
  if (version) {
    params.push("v=".concat(version));
  }
  if (language) {
    params.push("language=".concat(language));
  }
  if (region) {
    params.push("region=".concat(region));
  }
  if (libraries && libraries.length) {
    params.push("libraries=".concat(libraries.sort().join(',')));
  }
  if (channel) {
    params.push("channel=".concat(channel));
  }
  if (mapIds && mapIds.length) {
    params.push("map_ids=".concat(mapIds.join(',')));
  }
  if (authReferrerPolicy) {
    params.push("auth_referrer_policy=".concat(authReferrerPolicy));
  }
  params.push('callback=initMap');
  return "https://maps.googleapis.com/maps/api/js?".concat(params.join('&'));
}
var cleaningUp = false;
function DefaultLoadingElement() {
  return jsxRuntime.jsx("div", {
    children: "Loading..."
  });
}
var defaultLoadScriptProps = {
  id: 'script-loader',
  version: 'weekly'
};
var LoadScript = function (_super) {
  __extends(LoadScript, _super);
  function LoadScript() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.check = React.createRef();
    _this.state = {
      loaded: false
    };
    _this.cleanupCallback = function () {
      delete window.google.maps;
      _this.injectScript();
    };
    _this.isCleaningUp = function () {
      return __awaiter$1(_this, void 0, void 0, function () {
        function promiseCallback(resolve) {
          if (!cleaningUp) {
            resolve();
          } else {
            if (isBrowser) {
              var timer_1 = window.setInterval(function interval() {
                if (!cleaningUp) {
                  window.clearInterval(timer_1);
                  resolve();
                }
              }, 1);
            }
          }
          return;
        }
        return __generator(this, function (_a) {
          return [2, new Promise(promiseCallback)];
        });
      });
    };
    _this.cleanup = function () {
      cleaningUp = true;
      var script = document.getElementById(_this.props.id);
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
      Array.prototype.slice.call(document.getElementsByTagName('script')).filter(function filter(script) {
        return typeof script.src === 'string' && script.src.includes('maps.googleapis');
      }).forEach(function forEach(script) {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
      Array.prototype.slice.call(document.getElementsByTagName('link')).filter(function filter(link) {
        return link.href === 'https://fonts.googleapis.com/css?family=Roboto:300,400,500,700|Google+Sans';
      }).forEach(function forEach(link) {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
      Array.prototype.slice.call(document.getElementsByTagName('style')).filter(function filter(style) {
        return style.innerText !== undefined && style.innerText.length > 0 && style.innerText.includes('.gm-');
      }).forEach(function forEach(style) {
        if (style.parentNode) {
          style.parentNode.removeChild(style);
        }
      });
    };
    _this.injectScript = function () {
      if (_this.props.preventGoogleFontsLoading) {
        preventGoogleFonts();
      }
      invariant$1(!!_this.props.id, 'LoadScript requires "id" prop to be a string: %s', _this.props.id);
      var injectScriptOptions = {
        id: _this.props.id,
        nonce: _this.props.nonce,
        url: makeLoadScriptUrl(_this.props)
      };
      injectScript(injectScriptOptions).then(function () {
        if (_this.props.onLoad) {
          _this.props.onLoad();
        }
        _this.setState(function setLoaded() {
          return {
            loaded: true
          };
        });
        return;
      })["catch"](function (err) {
        if (_this.props.onError) {
          _this.props.onError(err);
        }
        console.error("\n          There has been an Error with loading Google Maps API script, please check that you provided correct google API key (".concat(_this.props.googleMapsApiKey || '-', ") or Client ID (").concat(_this.props.googleMapsClientId || '-', ") to <LoadScript />\n          Otherwise it is a Network issue.\n        "));
      });
    };
    return _this;
  }
  LoadScript.prototype.componentDidMount = function () {
    if (isBrowser) {
      if (window.google && window.google.maps && !cleaningUp) {
        console.error('google api is already presented');
        return;
      }
      this.isCleaningUp().then(this.injectScript)["catch"](function error(err) {
        console.error('Error at injecting script after cleaning up: ', err);
      });
    }
  };
  LoadScript.prototype.componentDidUpdate = function (prevProps) {
    if (this.props.libraries !== prevProps.libraries) {
      console.warn('Performance warning! LoadScript has been reloaded unintentionally! You should not pass `libraries` prop as new array. Please keep an array of libraries as static class property for Components and PureComponents, or just a const variable outside of component, or somewhere in config files or ENV variables');
    }
    if (isBrowser && prevProps.language !== this.props.language) {
      this.cleanup();
      this.setState(function setLoaded() {
        return {
          loaded: false
        };
      }, this.cleanupCallback);
    }
  };
  LoadScript.prototype.componentWillUnmount = function () {
    var _this = this;
    if (isBrowser) {
      this.cleanup();
      var timeoutCallback = function timeoutCallback() {
        if (!_this.check.current) {
          delete window.google;
          cleaningUp = false;
        }
      };
      window.setTimeout(timeoutCallback, 1);
      if (this.props.onUnmount) {
        this.props.onUnmount();
      }
    }
  };
  LoadScript.prototype.render = function () {
    return jsxRuntime.jsxs(jsxRuntime.Fragment, {
      children: [jsxRuntime.jsx("div", {
        ref: this.check
      }), this.state.loaded ? this.props.children : this.props.loadingElement || jsxRuntime.jsx(DefaultLoadingElement, {})]
    });
  };
  LoadScript.defaultProps = defaultLoadScriptProps;
  return LoadScript;
}(React.PureComponent);
var previouslyLoadedUrl;
function useLoadScript(_a) {
  var _b = _a.id,
    id = _b === void 0 ? defaultLoadScriptProps.id : _b,
    _c = _a.version,
    version = _c === void 0 ? defaultLoadScriptProps.version : _c,
    nonce = _a.nonce,
    googleMapsApiKey = _a.googleMapsApiKey,
    googleMapsClientId = _a.googleMapsClientId,
    language = _a.language,
    region = _a.region,
    libraries = _a.libraries,
    preventGoogleFontsLoading = _a.preventGoogleFontsLoading,
    channel = _a.channel,
    mapIds = _a.mapIds,
    authReferrerPolicy = _a.authReferrerPolicy;
  var isMounted = React.useRef(false);
  var _d = React.useState(false),
    isLoaded = _d[0],
    setLoaded = _d[1];
  var _e = React.useState(undefined),
    loadError = _e[0],
    setLoadError = _e[1];
  React.useEffect(function trackMountedState() {
    isMounted.current = true;
    return function () {
      isMounted.current = false;
    };
  }, []);
  React.useEffect(function applyPreventGoogleFonts() {
    if (isBrowser && preventGoogleFontsLoading) {
      preventGoogleFonts();
    }
  }, [preventGoogleFontsLoading]);
  React.useEffect(function validateLoadedState() {
    if (isLoaded) {
      invariant$1(!!window.google, 'useLoadScript was marked as loaded, but window.google is not present. Something went wrong.');
    }
  }, [isLoaded]);
  var url = makeLoadScriptUrl({
    version: version,
    googleMapsApiKey: googleMapsApiKey,
    googleMapsClientId: googleMapsClientId,
    language: language,
    region: region,
    libraries: libraries,
    channel: channel,
    mapIds: mapIds,
    authReferrerPolicy: authReferrerPolicy
  });
  React.useEffect(function loadScriptAndModifyLoadedState() {
    if (!isBrowser) {
      return;
    }
    function setLoadedIfMounted() {
      if (isMounted.current) {
        setLoaded(true);
        previouslyLoadedUrl = url;
      }
    }
    if (window.google && window.google.maps && previouslyLoadedUrl === url) {
      setLoadedIfMounted();
      return;
    }
    injectScript({
      id: id,
      url: url,
      nonce: nonce
    }).then(setLoadedIfMounted)["catch"](function handleInjectError(err) {
      if (isMounted.current) {
        setLoadError(err);
      }
      console.warn("\n        There has been an Error with loading Google Maps API script, please check that you provided correct google API key (".concat(googleMapsApiKey || '-', ") or Client ID (").concat(googleMapsClientId || '-', ")\n        Otherwise it is a Network issue.\n      "));
      console.error(err);
    });
  }, [id, url, nonce]);
  var prevLibraries = React.useRef();
  React.useEffect(function checkPerformance() {
    if (prevLibraries.current && libraries !== prevLibraries.current) {
      console.warn('Performance warning! LoadScript has been reloaded unintentionally! You should not pass `libraries` prop as new array. Please keep an array of libraries as static class property for Components and PureComponents, or just a const variable outside of component, or somewhere in config files or ENV variables');
    }
    prevLibraries.current = libraries;
  }, [libraries]);
  return {
    isLoaded: isLoaded,
    loadError: loadError,
    url: url
  };
}
var defaultLoadingElement = jsxRuntime.jsx(DefaultLoadingElement, {});
function LoadScriptNext(_a) {
  var loadingElement = _a.loadingElement,
    onLoad = _a.onLoad,
    onError = _a.onError,
    onUnmount = _a.onUnmount,
    children = _a.children,
    hookOptions = __rest$1(_a, ["loadingElement", "onLoad", "onError", "onUnmount", "children"]);
  var _b = useLoadScript(hookOptions),
    isLoaded = _b.isLoaded,
    loadError = _b.loadError;
  React.useEffect(function handleOnLoad() {
    if (isLoaded && typeof onLoad === 'function') {
      onLoad();
    }
  }, [isLoaded, onLoad]);
  React.useEffect(function handleOnError() {
    if (loadError && typeof onError === 'function') {
      onError(loadError);
    }
  }, [loadError, onError]);
  React.useEffect(function handleOnUnmount() {
    return function () {
      if (onUnmount) {
        onUnmount();
      }
    };
  }, [onUnmount]);
  return isLoaded ? children : loadingElement || defaultLoadingElement;
}
var LoadScriptNext$1 = React.memo(LoadScriptNext);
var LoaderStatus;
(function (LoaderStatus) {
  LoaderStatus[LoaderStatus["INITIALIZED"] = 0] = "INITIALIZED";
  LoaderStatus[LoaderStatus["LOADING"] = 1] = "LOADING";
  LoaderStatus[LoaderStatus["SUCCESS"] = 2] = "SUCCESS";
  LoaderStatus[LoaderStatus["FAILURE"] = 3] = "FAILURE";
})(LoaderStatus || (LoaderStatus = {}));
var eventMap$h = {};
var updaterMap$h = {
  options: function options(instance, _options2) {
    instance.setOptions(_options2);
  }
};
function TrafficLayerFunctional(_a) {
  var options = _a.options,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  React.useEffect(function () {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    if (options && instance !== null) {
      instance.setOptions(options);
    }
  }, [instance, options]);
  React.useEffect(function () {
    var trafficLayer = new google.maps.TrafficLayer(_assign(_assign({}, options || {}), {
      map: map
    }));
    setInstance(trafficLayer);
    if (onLoad) {
      onLoad(trafficLayer);
    }
    return function () {
      if (instance !== null) {
        if (onUnmount) {
          onUnmount(instance);
        }
        instance.setMap(null);
      }
    };
  }, []);
  return null;
}
var TrafficLayerF = React.memo(TrafficLayerFunctional);
var TrafficLayer = function (_super) {
  __extends(TrafficLayer, _super);
  function TrafficLayer() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.state = {
      trafficLayer: null
    };
    _this.setTrafficLayerCallback = function () {
      if (_this.state.trafficLayer !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.trafficLayer);
      }
    };
    _this.registeredEvents = [];
    return _this;
  }
  TrafficLayer.prototype.componentDidMount = function () {
    var trafficLayer = new google.maps.TrafficLayer(_assign(_assign({}, this.props.options || {}), {
      map: this.context
    }));
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$h,
      eventMap: eventMap$h,
      prevProps: {},
      nextProps: this.props,
      instance: trafficLayer
    });
    this.setState(function setTrafficLayer() {
      return {
        trafficLayer: trafficLayer
      };
    }, this.setTrafficLayerCallback);
  };
  TrafficLayer.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.trafficLayer !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$h,
        eventMap: eventMap$h,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.trafficLayer
      });
    }
  };
  TrafficLayer.prototype.componentWillUnmount = function () {
    if (this.state.trafficLayer !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.trafficLayer);
      }
      unregisterEvents(this.registeredEvents);
      this.state.trafficLayer.setMap(null);
    }
  };
  TrafficLayer.prototype.render = function () {
    return null;
  };
  TrafficLayer.contextType = MapContext;
  return TrafficLayer;
}(React.PureComponent);
function BicyclingLayerFunctional(_a) {
  var onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  React.useEffect(function () {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    var bicyclingLayer = new google.maps.BicyclingLayer();
    setInstance(bicyclingLayer);
    bicyclingLayer.setMap(map);
    if (onLoad) {
      onLoad(bicyclingLayer);
    }
    return function () {
      if (bicyclingLayer !== null) {
        if (onUnmount) {
          onUnmount(bicyclingLayer);
        }
        bicyclingLayer.setMap(null);
      }
    };
  }, []);
  return null;
}
var BicyclingLayerF = React.memo(BicyclingLayerFunctional);
var BicyclingLayer = function (_super) {
  __extends(BicyclingLayer, _super);
  function BicyclingLayer() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.state = {
      bicyclingLayer: null
    };
    _this.setBicyclingLayerCallback = function () {
      if (_this.state.bicyclingLayer !== null) {
        _this.state.bicyclingLayer.setMap(_this.context);
        if (_this.props.onLoad) {
          _this.props.onLoad(_this.state.bicyclingLayer);
        }
      }
    };
    return _this;
  }
  BicyclingLayer.prototype.componentDidMount = function () {
    var bicyclingLayer = new google.maps.BicyclingLayer();
    this.setState(function () {
      return {
        bicyclingLayer: bicyclingLayer
      };
    }, this.setBicyclingLayerCallback);
  };
  BicyclingLayer.prototype.componentWillUnmount = function () {
    if (this.state.bicyclingLayer !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.bicyclingLayer);
      }
      this.state.bicyclingLayer.setMap(null);
    }
  };
  BicyclingLayer.prototype.render = function () {
    return null;
  };
  BicyclingLayer.contextType = MapContext;
  return BicyclingLayer;
}(React.PureComponent);
function TransitLayerFunctional(_a) {
  var onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  React.useEffect(function () {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    var transitLayer = new google.maps.TransitLayer();
    setInstance(transitLayer);
    transitLayer.setMap(map);
    if (onLoad) {
      onLoad(transitLayer);
    }
    return function () {
      if (instance !== null) {
        if (onUnmount) {
          onUnmount(instance);
        }
        instance.setMap(null);
      }
    };
  }, []);
  return null;
}
var TransitLayerF = React.memo(TransitLayerFunctional);
var TransitLayer = function (_super) {
  __extends(TransitLayer, _super);
  function TransitLayer() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.state = {
      transitLayer: null
    };
    _this.setTransitLayerCallback = function () {
      if (_this.state.transitLayer !== null) {
        _this.state.transitLayer.setMap(_this.context);
        if (_this.props.onLoad) {
          _this.props.onLoad(_this.state.transitLayer);
        }
      }
    };
    return _this;
  }
  TransitLayer.prototype.componentDidMount = function () {
    var transitLayer = new google.maps.TransitLayer();
    this.setState(function setTransitLayer() {
      return {
        transitLayer: transitLayer
      };
    }, this.setTransitLayerCallback);
  };
  TransitLayer.prototype.componentWillUnmount = function () {
    if (this.state.transitLayer !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.transitLayer);
      }
      this.state.transitLayer.setMap(null);
    }
  };
  TransitLayer.prototype.render = function () {
    return null;
  };
  TransitLayer.contextType = MapContext;
  return TransitLayer;
}(React.PureComponent);
var eventMap$g = {
  onCircleComplete: 'circlecomplete',
  onMarkerComplete: 'markercomplete',
  onOverlayComplete: 'overlaycomplete',
  onPolygonComplete: 'polygoncomplete',
  onPolylineComplete: 'polylinecomplete',
  onRectangleComplete: 'rectanglecomplete'
};
var updaterMap$g = {
  drawingMode: function drawingMode(instance, _drawingMode) {
    instance.setDrawingMode(_drawingMode);
  },
  options: function options(instance, _options3) {
    instance.setOptions(_options3);
  }
};
function DrawingManagerFunctional(_a) {
  var options = _a.options,
    drawingMode = _a.drawingMode,
    onCircleComplete = _a.onCircleComplete,
    onMarkerComplete = _a.onMarkerComplete,
    onOverlayComplete = _a.onOverlayComplete,
    onPolygonComplete = _a.onPolygonComplete,
    onPolylineComplete = _a.onPolylineComplete,
    onRectangleComplete = _a.onRectangleComplete,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  var _c = React.useState(null),
    circlecompleteListener = _c[0],
    setCircleCompleteListener = _c[1];
  var _d = React.useState(null),
    markercompleteListener = _d[0],
    setMarkerCompleteListener = _d[1];
  var _e = React.useState(null),
    overlaycompleteListener = _e[0],
    setOverlayCompleteListener = _e[1];
  var _f = React.useState(null),
    polygoncompleteListener = _f[0],
    setPolygonCompleteListener = _f[1];
  var _g = React.useState(null),
    polylinecompleteListener = _g[0],
    setPolylineCompleteListener = _g[1];
  var _h = React.useState(null),
    rectanglecompleteListener = _h[0],
    setRectangleCompleteListener = _h[1];
  React.useEffect(function () {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    if (options && instance !== null) {
      instance.setOptions(options);
    }
  }, [instance, options]);
  React.useEffect(function () {
    if (drawingMode && instance !== null) {
      instance.setDrawingMode(drawingMode);
    }
  }, [instance, drawingMode]);
  React.useEffect(function () {
    if (instance && onCircleComplete) {
      if (circlecompleteListener !== null) {
        google.maps.event.removeListener(circlecompleteListener);
      }
      setCircleCompleteListener(google.maps.event.addListener(instance, 'circlecomplete', onCircleComplete));
    }
  }, [instance, onCircleComplete]);
  React.useEffect(function () {
    if (instance && onMarkerComplete) {
      if (markercompleteListener !== null) {
        google.maps.event.removeListener(markercompleteListener);
      }
      setMarkerCompleteListener(google.maps.event.addListener(instance, 'markercomplete', onMarkerComplete));
    }
  }, [instance, onMarkerComplete]);
  React.useEffect(function () {
    if (instance && onOverlayComplete) {
      if (overlaycompleteListener !== null) {
        google.maps.event.removeListener(overlaycompleteListener);
      }
      setOverlayCompleteListener(google.maps.event.addListener(instance, 'overlaycomplete', onOverlayComplete));
    }
  }, [instance, onOverlayComplete]);
  React.useEffect(function () {
    if (instance && onPolygonComplete) {
      if (polygoncompleteListener !== null) {
        google.maps.event.removeListener(polygoncompleteListener);
      }
      setPolygonCompleteListener(google.maps.event.addListener(instance, 'polygoncomplete', onPolygonComplete));
    }
  }, [instance, onPolygonComplete]);
  React.useEffect(function () {
    if (instance && onPolylineComplete) {
      if (polylinecompleteListener !== null) {
        google.maps.event.removeListener(polylinecompleteListener);
      }
      setPolylineCompleteListener(google.maps.event.addListener(instance, 'polylinecomplete', onPolylineComplete));
    }
  }, [instance, onPolylineComplete]);
  React.useEffect(function () {
    if (instance && onRectangleComplete) {
      if (rectanglecompleteListener !== null) {
        google.maps.event.removeListener(rectanglecompleteListener);
      }
      setRectangleCompleteListener(google.maps.event.addListener(instance, 'rectanglecomplete', onRectangleComplete));
    }
  }, [instance, onRectangleComplete]);
  React.useEffect(function () {
    invariant$1(!!google.maps.drawing, "Did you include prop libraries={['drawing']} in the URL? %s", google.maps.drawing);
    var drawingManager = new google.maps.drawing.DrawingManager(_assign(_assign({}, options || {}), {
      map: map
    }));
    if (drawingMode) {
      drawingManager.setDrawingMode(drawingMode);
    }
    if (onCircleComplete) {
      setCircleCompleteListener(google.maps.event.addListener(drawingManager, 'circlecomplete', onCircleComplete));
    }
    if (onMarkerComplete) {
      setMarkerCompleteListener(google.maps.event.addListener(drawingManager, 'markercomplete', onMarkerComplete));
    }
    if (onOverlayComplete) {
      setOverlayCompleteListener(google.maps.event.addListener(drawingManager, 'overlaycomplete', onOverlayComplete));
    }
    if (onPolygonComplete) {
      setPolygonCompleteListener(google.maps.event.addListener(drawingManager, 'polygoncomplete', onPolygonComplete));
    }
    if (onPolylineComplete) {
      setPolylineCompleteListener(google.maps.event.addListener(drawingManager, 'polylinecomplete', onPolylineComplete));
    }
    if (onRectangleComplete) {
      setRectangleCompleteListener(google.maps.event.addListener(drawingManager, 'rectanglecomplete', onRectangleComplete));
    }
    setInstance(drawingManager);
    if (onLoad) {
      onLoad(drawingManager);
    }
    return function () {
      if (instance !== null) {
        if (circlecompleteListener) {
          google.maps.event.removeListener(circlecompleteListener);
        }
        if (markercompleteListener) {
          google.maps.event.removeListener(markercompleteListener);
        }
        if (overlaycompleteListener) {
          google.maps.event.removeListener(overlaycompleteListener);
        }
        if (polygoncompleteListener) {
          google.maps.event.removeListener(polygoncompleteListener);
        }
        if (polylinecompleteListener) {
          google.maps.event.removeListener(polylinecompleteListener);
        }
        if (rectanglecompleteListener) {
          google.maps.event.removeListener(rectanglecompleteListener);
        }
        if (onUnmount) {
          onUnmount(instance);
        }
        instance.setMap(null);
      }
    };
  }, []);
  return null;
}
var DrawingManagerF = React.memo(DrawingManagerFunctional);
var DrawingManager = function (_super) {
  __extends(DrawingManager, _super);
  function DrawingManager(props) {
    var _this = _super.call(this, props) || this;
    _this.registeredEvents = [];
    _this.state = {
      drawingManager: null
    };
    _this.setDrawingManagerCallback = function () {
      if (_this.state.drawingManager !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.drawingManager);
      }
    };
    invariant$1(!!google.maps.drawing, "Did you include prop libraries={['drawing']} in the URL? %s", google.maps.drawing);
    return _this;
  }
  DrawingManager.prototype.componentDidMount = function () {
    var drawingManager = new google.maps.drawing.DrawingManager(_assign(_assign({}, this.props.options || {}), {
      map: this.context
    }));
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$g,
      eventMap: eventMap$g,
      prevProps: {},
      nextProps: this.props,
      instance: drawingManager
    });
    this.setState(function setDrawingManager() {
      return {
        drawingManager: drawingManager
      };
    }, this.setDrawingManagerCallback);
  };
  DrawingManager.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.drawingManager !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$g,
        eventMap: eventMap$g,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.drawingManager
      });
    }
  };
  DrawingManager.prototype.componentWillUnmount = function () {
    if (this.state.drawingManager !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.drawingManager);
      }
      unregisterEvents(this.registeredEvents);
      this.state.drawingManager.setMap(null);
    }
  };
  DrawingManager.prototype.render = function () {
    return null;
  };
  DrawingManager.contextType = MapContext;
  return DrawingManager;
}(React.PureComponent);
var eventMap$f = {
  onAnimationChanged: 'animation_changed',
  onClick: 'click',
  onClickableChanged: 'clickable_changed',
  onCursorChanged: 'cursor_changed',
  onDblClick: 'dblclick',
  onDrag: 'drag',
  onDragEnd: 'dragend',
  onDraggableChanged: 'draggable_changed',
  onDragStart: 'dragstart',
  onFlatChanged: 'flat_changed',
  onIconChanged: 'icon_changed',
  onMouseDown: 'mousedown',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover',
  onMouseUp: 'mouseup',
  onPositionChanged: 'position_changed',
  onRightClick: 'rightclick',
  onShapeChanged: 'shape_changed',
  onTitleChanged: 'title_changed',
  onVisibleChanged: 'visible_changed',
  onZindexChanged: 'zindex_changed'
};
var updaterMap$f = {
  animation: function animation(instance, _animation) {
    instance.setAnimation(_animation);
  },
  clickable: function clickable(instance, _clickable) {
    instance.setClickable(_clickable);
  },
  cursor: function cursor(instance, _cursor) {
    instance.setCursor(_cursor);
  },
  draggable: function draggable(instance, _draggable) {
    instance.setDraggable(_draggable);
  },
  icon: function icon(instance, _icon) {
    instance.setIcon(_icon);
  },
  label: function label(instance, _label) {
    instance.setLabel(_label);
  },
  map: function map(instance, _map) {
    instance.setMap(_map);
  },
  opacity: function opacity(instance, _opacity) {
    instance.setOpacity(_opacity);
  },
  options: function options(instance, _options4) {
    instance.setOptions(_options4);
  },
  position: function position(instance, _position) {
    instance.setPosition(_position);
  },
  shape: function shape(instance, _shape) {
    instance.setShape(_shape);
  },
  title: function title(instance, _title) {
    instance.setTitle(_title);
  },
  visible: function visible(instance, _visible) {
    instance.setVisible(_visible);
  },
  zIndex: function zIndex(instance, _zIndex) {
    instance.setZIndex(_zIndex);
  }
};
var defaultOptions$5 = {};
function MarkerFunctional(_a) {
  var position = _a.position,
    options = _a.options,
    clusterer = _a.clusterer,
    noClustererRedraw = _a.noClustererRedraw,
    children = _a.children,
    draggable = _a.draggable,
    visible = _a.visible,
    animation = _a.animation,
    clickable = _a.clickable,
    cursor = _a.cursor,
    icon = _a.icon,
    label = _a.label,
    opacity = _a.opacity,
    shape = _a.shape,
    title = _a.title,
    zIndex = _a.zIndex,
    onClick = _a.onClick,
    onDblClick = _a.onDblClick,
    onDrag = _a.onDrag,
    onDragEnd = _a.onDragEnd,
    onDragStart = _a.onDragStart,
    onMouseOut = _a.onMouseOut,
    onMouseOver = _a.onMouseOver,
    onMouseUp = _a.onMouseUp,
    onMouseDown = _a.onMouseDown,
    onRightClick = _a.onRightClick,
    onClickableChanged = _a.onClickableChanged,
    onCursorChanged = _a.onCursorChanged,
    onAnimationChanged = _a.onAnimationChanged,
    onDraggableChanged = _a.onDraggableChanged,
    onFlatChanged = _a.onFlatChanged,
    onIconChanged = _a.onIconChanged,
    onPositionChanged = _a.onPositionChanged,
    onShapeChanged = _a.onShapeChanged,
    onTitleChanged = _a.onTitleChanged,
    onVisibleChanged = _a.onVisibleChanged,
    onZindexChanged = _a.onZindexChanged,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  var _c = React.useState(null),
    dblclickListener = _c[0],
    setDblclickListener = _c[1];
  var _d = React.useState(null),
    dragendListener = _d[0],
    setDragendListener = _d[1];
  var _e = React.useState(null),
    dragstartListener = _e[0],
    setDragstartListener = _e[1];
  var _f = React.useState(null),
    mousedownListener = _f[0],
    setMousedownListener = _f[1];
  var _g = React.useState(null),
    mouseoutListener = _g[0],
    setMouseoutListener = _g[1];
  var _h = React.useState(null),
    mouseoverListener = _h[0],
    setMouseoverListener = _h[1];
  var _j = React.useState(null),
    mouseupListener = _j[0],
    setMouseupListener = _j[1];
  var _k = React.useState(null),
    rightclickListener = _k[0],
    setRightclickListener = _k[1];
  var _l = React.useState(null),
    clickListener = _l[0],
    setClickListener = _l[1];
  var _m = React.useState(null),
    dragListener = _m[0],
    setDragListener = _m[1];
  var _o = React.useState(null),
    clickableChangedListener = _o[0],
    setClickableChangedListener = _o[1];
  var _p = React.useState(null),
    cursorChangedListener = _p[0],
    setCursorChangedListener = _p[1];
  var _q = React.useState(null),
    animationChangedListener = _q[0],
    setAnimationChangedListener = _q[1];
  var _r = React.useState(null),
    draggableChangedListener = _r[0],
    setDraggableChangedListener = _r[1];
  var _s = React.useState(null),
    flatChangedListener = _s[0],
    setFlatChangedListener = _s[1];
  var _t = React.useState(null),
    iconChangedListener = _t[0],
    setIconChangedListener = _t[1];
  var _u = React.useState(null),
    positionChangedListener = _u[0],
    setPositionChangedListener = _u[1];
  var _v = React.useState(null),
    shapeChangedListener = _v[0],
    setShapeChangedListener = _v[1];
  var _w = React.useState(null),
    titleChangedListener = _w[0],
    setTitleChangedListener = _w[1];
  var _x = React.useState(null),
    visibleChangedListener = _x[0],
    setVisibleChangedListener = _x[1];
  var _y = React.useState(null),
    zIndexChangedListener = _y[0],
    setZindexChangedListener = _y[1];
  React.useEffect(function () {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    if (typeof options !== 'undefined' && instance !== null) {
      instance.setOptions(options);
    }
  }, [instance, options]);
  React.useEffect(function () {
    if (typeof draggable !== 'undefined' && instance !== null) {
      instance.setDraggable(draggable);
    }
  }, [instance, draggable]);
  React.useEffect(function () {
    if (position && instance !== null) {
      instance.setPosition(position);
    }
  }, [instance, position]);
  React.useEffect(function () {
    if (typeof visible !== 'undefined' && instance !== null) {
      instance.setVisible(visible);
    }
  }, [instance, visible]);
  React.useEffect(function () {
    instance === null || instance === void 0 ? void 0 : instance.setAnimation(animation);
  }, [instance, animation]);
  React.useEffect(function () {
    if (instance && onDblClick) {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      setDblclickListener(google.maps.event.addListener(instance, 'dblclick', onDblClick));
    }
  }, [onDblClick]);
  React.useEffect(function () {
    if (instance && onDragEnd) {
      if (dragendListener !== null) {
        google.maps.event.removeListener(dragendListener);
      }
      setDragendListener(google.maps.event.addListener(instance, 'dragend', onDragEnd));
    }
  }, [onDragEnd]);
  React.useEffect(function () {
    if (instance && onDragStart) {
      if (dragstartListener !== null) {
        google.maps.event.removeListener(dragstartListener);
      }
      setDragstartListener(google.maps.event.addListener(instance, 'dragstart', onDragStart));
    }
  }, [onDragStart]);
  React.useEffect(function () {
    if (instance && onMouseDown) {
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      setMousedownListener(google.maps.event.addListener(instance, 'mousedown', onMouseDown));
    }
  }, [onMouseDown]);
  React.useEffect(function () {
    if (instance && onMouseOut) {
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      setMouseoutListener(google.maps.event.addListener(instance, 'mouseout', onMouseOut));
    }
  }, [onMouseOut]);
  React.useEffect(function () {
    if (instance && onMouseOver) {
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      setMouseoverListener(google.maps.event.addListener(instance, 'mouseover', onMouseOver));
    }
  }, [onMouseOver]);
  React.useEffect(function () {
    if (instance && onMouseUp) {
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      setMouseupListener(google.maps.event.addListener(instance, 'mouseup', onMouseUp));
    }
  }, [onMouseUp]);
  React.useEffect(function () {
    if (instance && onRightClick) {
      if (rightclickListener !== null) {
        google.maps.event.removeListener(rightclickListener);
      }
      setRightclickListener(google.maps.event.addListener(instance, 'rightclick', onRightClick));
    }
  }, [onRightClick]);
  React.useEffect(function () {
    if (instance && onClick) {
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      setClickListener(google.maps.event.addListener(instance, 'click', onClick));
    }
  }, [onClick]);
  React.useEffect(function () {
    if (instance && onDrag) {
      if (dragListener !== null) {
        google.maps.event.removeListener(dragListener);
      }
      setDragListener(google.maps.event.addListener(instance, 'drag', onDrag));
    }
  }, [onDrag]);
  React.useEffect(function () {
    if (instance && onClickableChanged) {
      if (clickableChangedListener !== null) {
        google.maps.event.removeListener(clickableChangedListener);
      }
      setClickableChangedListener(google.maps.event.addListener(instance, 'clickable_changed', onClickableChanged));
    }
  }, [onClickableChanged]);
  React.useEffect(function () {
    if (instance && onCursorChanged) {
      if (cursorChangedListener !== null) {
        google.maps.event.removeListener(cursorChangedListener);
      }
      setCursorChangedListener(google.maps.event.addListener(instance, 'cursor_changed', onCursorChanged));
    }
  }, [onCursorChanged]);
  React.useEffect(function () {
    if (instance && onAnimationChanged) {
      if (animationChangedListener !== null) {
        google.maps.event.removeListener(animationChangedListener);
      }
      setAnimationChangedListener(google.maps.event.addListener(instance, 'animation_changed', onAnimationChanged));
    }
  }, [onAnimationChanged]);
  React.useEffect(function () {
    if (instance && onDraggableChanged) {
      if (draggableChangedListener !== null) {
        google.maps.event.removeListener(draggableChangedListener);
      }
      setDraggableChangedListener(google.maps.event.addListener(instance, 'draggable_changed', onDraggableChanged));
    }
  }, [onDraggableChanged]);
  React.useEffect(function () {
    if (instance && onFlatChanged) {
      if (flatChangedListener !== null) {
        google.maps.event.removeListener(flatChangedListener);
      }
      setFlatChangedListener(google.maps.event.addListener(instance, 'flat_changed', onFlatChanged));
    }
  }, [onFlatChanged]);
  React.useEffect(function () {
    if (instance && onIconChanged) {
      if (iconChangedListener !== null) {
        google.maps.event.removeListener(iconChangedListener);
      }
      setIconChangedListener(google.maps.event.addListener(instance, 'icon_changed', onIconChanged));
    }
  }, [onIconChanged]);
  React.useEffect(function () {
    if (instance && onPositionChanged) {
      if (positionChangedListener !== null) {
        google.maps.event.removeListener(positionChangedListener);
      }
      setPositionChangedListener(google.maps.event.addListener(instance, 'position_changed', onPositionChanged));
    }
  }, [onPositionChanged]);
  React.useEffect(function () {
    if (instance && onShapeChanged) {
      if (shapeChangedListener !== null) {
        google.maps.event.removeListener(shapeChangedListener);
      }
      setShapeChangedListener(google.maps.event.addListener(instance, 'shape_changed', onShapeChanged));
    }
  }, [onShapeChanged]);
  React.useEffect(function () {
    if (instance && onTitleChanged) {
      if (titleChangedListener !== null) {
        google.maps.event.removeListener(titleChangedListener);
      }
      setTitleChangedListener(google.maps.event.addListener(instance, 'title_changed', onTitleChanged));
    }
  }, [onTitleChanged]);
  React.useEffect(function () {
    if (instance && onVisibleChanged) {
      if (visibleChangedListener !== null) {
        google.maps.event.removeListener(visibleChangedListener);
      }
      setVisibleChangedListener(google.maps.event.addListener(instance, 'visible_changed', onVisibleChanged));
    }
  }, [onVisibleChanged]);
  React.useEffect(function () {
    if (instance && onZindexChanged) {
      if (zIndexChangedListener !== null) {
        google.maps.event.removeListener(zIndexChangedListener);
      }
      setZindexChangedListener(google.maps.event.addListener(instance, 'zindex_changed', onZindexChanged));
    }
  }, [onZindexChanged]);
  React.useEffect(function () {
    var markerOptions = _assign(_assign(_assign({}, options || defaultOptions$5), clusterer ? defaultOptions$5 : {
      map: map
    }), {
      position: position
    });
    var marker = new google.maps.Marker(markerOptions);
    if (clusterer) {
      clusterer.addMarker(marker, !!noClustererRedraw);
    } else {
      marker.setMap(map);
    }
    if (position) {
      marker.setPosition(position);
    }
    if (typeof visible !== 'undefined') {
      marker.setVisible(visible);
    }
    if (typeof draggable !== 'undefined') {
      marker.setDraggable(draggable);
    }
    if (typeof clickable !== 'undefined') {
      marker.setClickable(clickable);
    }
    if (typeof cursor === 'string') {
      marker.setCursor(cursor);
    }
    if (icon) {
      marker.setIcon(icon);
    }
    if (typeof label !== 'undefined') {
      marker.setLabel(label);
    }
    if (typeof opacity !== 'undefined') {
      marker.setOpacity(opacity);
    }
    if (shape) {
      marker.setShape(shape);
    }
    if (typeof title === 'string') {
      marker.setTitle(title);
    }
    if (typeof zIndex === 'number') {
      marker.setZIndex(zIndex);
    }
    if (onDblClick) {
      setDblclickListener(google.maps.event.addListener(marker, 'dblclick', onDblClick));
    }
    if (onDragEnd) {
      setDragendListener(google.maps.event.addListener(marker, 'dragend', onDragEnd));
    }
    if (onDragStart) {
      setDragstartListener(google.maps.event.addListener(marker, 'dragstart', onDragStart));
    }
    if (onMouseDown) {
      setMousedownListener(google.maps.event.addListener(marker, 'mousedown', onMouseDown));
    }
    if (onMouseOut) {
      setMouseoutListener(google.maps.event.addListener(marker, 'mouseout', onMouseOut));
    }
    if (onMouseOver) {
      setMouseoverListener(google.maps.event.addListener(marker, 'mouseover', onMouseOver));
    }
    if (onMouseUp) {
      setMouseupListener(google.maps.event.addListener(marker, 'mouseup', onMouseUp));
    }
    if (onRightClick) {
      setRightclickListener(google.maps.event.addListener(marker, 'rightclick', onRightClick));
    }
    if (onClick) {
      setClickListener(google.maps.event.addListener(marker, 'click', onClick));
    }
    if (onDrag) {
      setDragListener(google.maps.event.addListener(marker, 'drag', onDrag));
    }
    if (onClickableChanged) {
      setClickableChangedListener(google.maps.event.addListener(marker, 'clickable_changed', onClickableChanged));
    }
    if (onCursorChanged) {
      setCursorChangedListener(google.maps.event.addListener(marker, 'cursor_changed', onCursorChanged));
    }
    if (onAnimationChanged) {
      setAnimationChangedListener(google.maps.event.addListener(marker, 'animation_changed', onAnimationChanged));
    }
    if (onDraggableChanged) {
      setDraggableChangedListener(google.maps.event.addListener(marker, 'draggable_changed', onDraggableChanged));
    }
    if (onFlatChanged) {
      setFlatChangedListener(google.maps.event.addListener(marker, 'flat_changed', onFlatChanged));
    }
    if (onIconChanged) {
      setIconChangedListener(google.maps.event.addListener(marker, 'icon_changed', onIconChanged));
    }
    if (onPositionChanged) {
      setPositionChangedListener(google.maps.event.addListener(marker, 'position_changed', onPositionChanged));
    }
    if (onShapeChanged) {
      setShapeChangedListener(google.maps.event.addListener(marker, 'shape_changed', onShapeChanged));
    }
    if (onTitleChanged) {
      setTitleChangedListener(google.maps.event.addListener(marker, 'title_changed', onTitleChanged));
    }
    if (onVisibleChanged) {
      setVisibleChangedListener(google.maps.event.addListener(marker, 'visible_changed', onVisibleChanged));
    }
    if (onZindexChanged) {
      setZindexChangedListener(google.maps.event.addListener(marker, 'zindex_changed', onZindexChanged));
    }
    setInstance(marker);
    if (onLoad) {
      onLoad(marker);
    }
    return function () {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      if (dragendListener !== null) {
        google.maps.event.removeListener(dragendListener);
      }
      if (dragstartListener !== null) {
        google.maps.event.removeListener(dragstartListener);
      }
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      if (rightclickListener !== null) {
        google.maps.event.removeListener(rightclickListener);
      }
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      if (clickableChangedListener !== null) {
        google.maps.event.removeListener(clickableChangedListener);
      }
      if (cursorChangedListener !== null) {
        google.maps.event.removeListener(cursorChangedListener);
      }
      if (animationChangedListener !== null) {
        google.maps.event.removeListener(animationChangedListener);
      }
      if (draggableChangedListener !== null) {
        google.maps.event.removeListener(draggableChangedListener);
      }
      if (flatChangedListener !== null) {
        google.maps.event.removeListener(flatChangedListener);
      }
      if (iconChangedListener !== null) {
        google.maps.event.removeListener(iconChangedListener);
      }
      if (positionChangedListener !== null) {
        google.maps.event.removeListener(positionChangedListener);
      }
      if (titleChangedListener !== null) {
        google.maps.event.removeListener(titleChangedListener);
      }
      if (visibleChangedListener !== null) {
        google.maps.event.removeListener(visibleChangedListener);
      }
      if (zIndexChangedListener !== null) {
        google.maps.event.removeListener(zIndexChangedListener);
      }
      if (onUnmount) {
        onUnmount(marker);
      }
      if (clusterer) {
        clusterer.removeMarker(marker, !!noClustererRedraw);
      } else if (marker) {
        marker.setMap(null);
      }
    };
  }, []);
  var chx = React.useMemo(function () {
    return children ? React.Children.map(children, function (child) {
      if (!React.isValidElement(child)) {
        return child;
      }
      var elementChild = child;
      return React.cloneElement(elementChild, {
        anchor: instance
      });
    }) : null;
  }, [children, instance]);
  return jsxRuntime.jsx(jsxRuntime.Fragment, {
    children: chx
  }) || null;
}
var MarkerF = React.memo(MarkerFunctional);
var Marker = function (_super) {
  __extends(Marker, _super);
  function Marker() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    return _this;
  }
  Marker.prototype.componentDidMount = function () {
    var markerOptions = _assign(_assign(_assign({}, this.props.options || defaultOptions$5), this.props.clusterer ? defaultOptions$5 : {
      map: this.context
    }), {
      position: this.props.position
    });
    this.marker = new google.maps.Marker(markerOptions);
    if (this.props.clusterer) {
      this.props.clusterer.addMarker(this.marker, !!this.props.noClustererRedraw);
    } else {
      this.marker.setMap(this.context);
    }
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$f,
      eventMap: eventMap$f,
      prevProps: {},
      nextProps: this.props,
      instance: this.marker
    });
    if (this.props.onLoad) {
      this.props.onLoad(this.marker);
    }
  };
  Marker.prototype.componentDidUpdate = function (prevProps) {
    if (this.marker) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$f,
        eventMap: eventMap$f,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.marker
      });
    }
  };
  Marker.prototype.componentWillUnmount = function () {
    if (this.marker) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.marker);
      }
      unregisterEvents(this.registeredEvents);
      if (this.props.clusterer) {
        this.props.clusterer.removeMarker(this.marker, !!this.props.noClustererRedraw);
      } else {
        this.marker && this.marker.setMap(null);
      }
    }
  };
  Marker.prototype.render = function () {
    var _this = this;
    var children = null;
    if (this.props.children) {
      children = React.Children.map(this.props.children, function (child) {
        if (!React.isValidElement(child)) {
          return child;
        }
        var elementChild = child;
        return React.cloneElement(elementChild, {
          anchor: _this.marker
        });
      });
    }
    return children || null;
  };
  Marker.contextType = MapContext;
  return Marker;
}(React.PureComponent);
var ClusterIcon = function () {
  function ClusterIcon(cluster, styles) {
    cluster.getClusterer().extend(ClusterIcon, google.maps.OverlayView);
    this.cluster = cluster;
    this.clusterClassName = this.cluster.getClusterer().getClusterClass();
    this.className = this.clusterClassName;
    this.styles = styles;
    this.center = undefined;
    this.div = null;
    this.sums = null;
    this.visible = false;
    this.boundsChangedListener = null;
    this.url = '';
    this.height = 0;
    this.width = 0;
    this.anchorText = [0, 0];
    this.anchorIcon = [0, 0];
    this.textColor = 'black';
    this.textSize = 11;
    this.textDecoration = 'none';
    this.fontWeight = 'bold';
    this.fontStyle = 'normal';
    this.fontFamily = 'Arial,sans-serif';
    this.backgroundPosition = '0 0';
    this.cMouseDownInCluster = null;
    this.cDraggingMapByCluster = null;
    this.timeOut = null;
    this.setMap(cluster.getMap());
    this.onBoundsChanged = this.onBoundsChanged.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);
    this.onAdd = this.onAdd.bind(this);
    this.onRemove = this.onRemove.bind(this);
    this.draw = this.draw.bind(this);
    this.hide = this.hide.bind(this);
    this.show = this.show.bind(this);
    this.useStyle = this.useStyle.bind(this);
    this.setCenter = this.setCenter.bind(this);
    this.getPosFromLatLng = this.getPosFromLatLng.bind(this);
  }
  ClusterIcon.prototype.onBoundsChanged = function () {
    this.cDraggingMapByCluster = this.cMouseDownInCluster;
  };
  ClusterIcon.prototype.onMouseDown = function () {
    this.cMouseDownInCluster = true;
    this.cDraggingMapByCluster = false;
  };
  ClusterIcon.prototype.onClick = function (event) {
    this.cMouseDownInCluster = false;
    if (!this.cDraggingMapByCluster) {
      var markerClusterer_1 = this.cluster.getClusterer();
      google.maps.event.trigger(markerClusterer_1, 'click', this.cluster);
      google.maps.event.trigger(markerClusterer_1, 'clusterclick', this.cluster);
      if (markerClusterer_1.getZoomOnClick()) {
        var maxZoom_1 = markerClusterer_1.getMaxZoom();
        var bounds_1 = this.cluster.getBounds();
        var map = markerClusterer_1.getMap();
        if (map !== null && 'fitBounds' in map) {
          map.fitBounds(bounds_1);
        }
        this.timeOut = window.setTimeout(function () {
          var map = markerClusterer_1.getMap();
          if (map !== null) {
            if ('fitBounds' in map) {
              map.fitBounds(bounds_1);
            }
            var zoom = map.getZoom() || 0;
            if (maxZoom_1 !== null && zoom > maxZoom_1) {
              map.setZoom(maxZoom_1 + 1);
            }
          }
        }, 100);
      }
      event.cancelBubble = true;
      if (event.stopPropagation) {
        event.stopPropagation();
      }
    }
  };
  ClusterIcon.prototype.onMouseOver = function () {
    google.maps.event.trigger(this.cluster.getClusterer(), 'mouseover', this.cluster);
  };
  ClusterIcon.prototype.onMouseOut = function () {
    google.maps.event.trigger(this.cluster.getClusterer(), 'mouseout', this.cluster);
  };
  ClusterIcon.prototype.onAdd = function () {
    var _a;
    this.div = document.createElement('div');
    this.div.className = this.className;
    if (this.visible) {
      this.show();
    }
    (_a = this.getPanes()) === null || _a === void 0 ? void 0 : _a.overlayMouseTarget.appendChild(this.div);
    var map = this.getMap();
    if (map !== null) {
      this.boundsChangedListener = google.maps.event.addListener(map, 'bounds_changed', this.onBoundsChanged);
      this.div.addEventListener('mousedown', this.onMouseDown);
      this.div.addEventListener('click', this.onClick);
      this.div.addEventListener('mouseover', this.onMouseOver);
      this.div.addEventListener('mouseout', this.onMouseOut);
    }
  };
  ClusterIcon.prototype.onRemove = function () {
    if (this.div && this.div.parentNode) {
      this.hide();
      if (this.boundsChangedListener !== null) {
        google.maps.event.removeListener(this.boundsChangedListener);
      }
      this.div.removeEventListener('mousedown', this.onMouseDown);
      this.div.removeEventListener('click', this.onClick);
      this.div.removeEventListener('mouseover', this.onMouseOver);
      this.div.removeEventListener('mouseout', this.onMouseOut);
      this.div.parentNode.removeChild(this.div);
      if (this.timeOut !== null) {
        window.clearTimeout(this.timeOut);
        this.timeOut = null;
      }
      this.div = null;
    }
  };
  ClusterIcon.prototype.draw = function () {
    if (this.visible && this.div !== null && this.center) {
      var pos = this.getPosFromLatLng(this.center);
      this.div.style.top = pos !== null ? "".concat(pos.y, "px") : '0';
      this.div.style.left = pos !== null ? "".concat(pos.x, "px") : '0';
    }
  };
  ClusterIcon.prototype.hide = function () {
    if (this.div) {
      this.div.style.display = 'none';
    }
    this.visible = false;
  };
  ClusterIcon.prototype.show = function () {
    var _a, _b, _c, _d, _e, _f;
    if (this.div && this.center) {
      var divTitle = this.sums === null || typeof this.sums.title === 'undefined' || this.sums.title === '' ? this.cluster.getClusterer().getTitle() : this.sums.title;
      var bp = this.backgroundPosition.split(' ');
      var spriteH = parseInt(((_a = bp[0]) === null || _a === void 0 ? void 0 : _a.replace(/^\s+|\s+$/g, '')) || '0', 10);
      var spriteV = parseInt(((_b = bp[1]) === null || _b === void 0 ? void 0 : _b.replace(/^\s+|\s+$/g, '')) || '0', 10);
      var pos = this.getPosFromLatLng(this.center);
      this.div.className = this.className;
      this.div.setAttribute('style', "cursor: pointer; position: absolute; top: ".concat(pos !== null ? "".concat(pos.y, "px") : '0', "; left: ").concat(pos !== null ? "".concat(pos.x, "px") : '0', "; width: ").concat(this.width, "px; height: ").concat(this.height, "px; "));
      var img = document.createElement('img');
      img.alt = divTitle;
      img.src = this.url;
      img.width = this.width;
      img.height = this.height;
      img.setAttribute('style', "position: absolute; top: ".concat(spriteV, "px; left: ").concat(spriteH, "px"));
      if (!this.cluster.getClusterer().enableRetinaIcons) {
        img.style.clip = "rect(-".concat(spriteV, "px, -").concat(spriteH + this.width, "px, -").concat(spriteV + this.height, ", -").concat(spriteH, ")");
      }
      var textElm = document.createElement('div');
      textElm.setAttribute('style', "position: absolute; top: ".concat(this.anchorText[0], "px; left: ").concat(this.anchorText[1], "px; color: ").concat(this.textColor, "; font-size: ").concat(this.textSize, "px; font-family: ").concat(this.fontFamily, "; font-weight: ").concat(this.fontWeight, "; fontStyle: ").concat(this.fontStyle, "; text-decoration: ").concat(this.textDecoration, "; text-align: center; width: ").concat(this.width, "px; line-height: ").concat(this.height, "px"));
      if ((_c = this.sums) === null || _c === void 0 ? void 0 : _c.text) textElm.innerText = "".concat((_d = this.sums) === null || _d === void 0 ? void 0 : _d.text);
      if ((_e = this.sums) === null || _e === void 0 ? void 0 : _e.html) textElm.innerHTML = "".concat((_f = this.sums) === null || _f === void 0 ? void 0 : _f.html);
      this.div.innerHTML = '';
      this.div.appendChild(img);
      this.div.appendChild(textElm);
      this.div.title = divTitle;
      this.div.style.display = '';
    }
    this.visible = true;
  };
  ClusterIcon.prototype.useStyle = function (sums) {
    this.sums = sums;
    var styles = this.cluster.getClusterer().getStyles();
    var style = styles[Math.min(styles.length - 1, Math.max(0, sums.index - 1))];
    if (style) {
      this.url = style.url;
      this.height = style.height;
      this.width = style.width;
      if (style.className) {
        this.className = "".concat(this.clusterClassName, " ").concat(style.className);
      }
      this.anchorText = style.anchorText || [0, 0];
      this.anchorIcon = style.anchorIcon || [this.height / 2, this.width / 2];
      this.textColor = style.textColor || 'black';
      this.textSize = style.textSize || 11;
      this.textDecoration = style.textDecoration || 'none';
      this.fontWeight = style.fontWeight || 'bold';
      this.fontStyle = style.fontStyle || 'normal';
      this.fontFamily = style.fontFamily || 'Arial,sans-serif';
      this.backgroundPosition = style.backgroundPosition || '0 0';
    }
  };
  ClusterIcon.prototype.setCenter = function (center) {
    this.center = center;
  };
  ClusterIcon.prototype.getPosFromLatLng = function (latlng) {
    var pos = this.getProjection().fromLatLngToDivPixel(latlng);
    if (pos !== null) {
      pos.x -= this.anchorIcon[1];
      pos.y -= this.anchorIcon[0];
    }
    return pos;
  };
  return ClusterIcon;
}();
var Cluster$1 = function () {
  function Cluster(markerClusterer) {
    this.markerClusterer = markerClusterer;
    this.map = this.markerClusterer.getMap();
    this.gridSize = this.markerClusterer.getGridSize();
    this.minClusterSize = this.markerClusterer.getMinimumClusterSize();
    this.averageCenter = this.markerClusterer.getAverageCenter();
    this.markers = [];
    this.center = undefined;
    this.bounds = null;
    this.clusterIcon = new ClusterIcon(this, this.markerClusterer.getStyles());
    this.getSize = this.getSize.bind(this);
    this.getMarkers = this.getMarkers.bind(this);
    this.getCenter = this.getCenter.bind(this);
    this.getMap = this.getMap.bind(this);
    this.getClusterer = this.getClusterer.bind(this);
    this.getBounds = this.getBounds.bind(this);
    this.remove = this.remove.bind(this);
    this.addMarker = this.addMarker.bind(this);
    this.isMarkerInClusterBounds = this.isMarkerInClusterBounds.bind(this);
    this.calculateBounds = this.calculateBounds.bind(this);
    this.updateIcon = this.updateIcon.bind(this);
    this.isMarkerAlreadyAdded = this.isMarkerAlreadyAdded.bind(this);
  }
  Cluster.prototype.getSize = function () {
    return this.markers.length;
  };
  Cluster.prototype.getMarkers = function () {
    return this.markers;
  };
  Cluster.prototype.getCenter = function () {
    return this.center;
  };
  Cluster.prototype.getMap = function () {
    return this.map;
  };
  Cluster.prototype.getClusterer = function () {
    return this.markerClusterer;
  };
  Cluster.prototype.getBounds = function () {
    var bounds = new google.maps.LatLngBounds(this.center, this.center);
    var markers = this.getMarkers();
    for (var _i = 0, markers_1 = markers; _i < markers_1.length; _i++) {
      var marker = markers_1[_i];
      var position = marker.getPosition();
      if (position) {
        bounds.extend(position);
      }
    }
    return bounds;
  };
  Cluster.prototype.remove = function () {
    this.clusterIcon.setMap(null);
    this.markers = [];
    delete this.markers;
  };
  Cluster.prototype.addMarker = function (marker) {
    var _a;
    if (this.isMarkerAlreadyAdded(marker)) {
      return false;
    }
    if (!this.center) {
      var position = marker.getPosition();
      if (position) {
        this.center = position;
        this.calculateBounds();
      }
    } else {
      if (this.averageCenter) {
        var position = marker.getPosition();
        if (position) {
          var length_1 = this.markers.length + 1;
          this.center = new google.maps.LatLng((this.center.lat() * (length_1 - 1) + position.lat()) / length_1, (this.center.lng() * (length_1 - 1) + position.lng()) / length_1);
          this.calculateBounds();
        }
      }
    }
    marker.isAdded = true;
    this.markers.push(marker);
    var mCount = this.markers.length;
    var maxZoom = this.markerClusterer.getMaxZoom();
    var zoom = (_a = this.map) === null || _a === void 0 ? void 0 : _a.getZoom();
    if (maxZoom !== null && typeof zoom !== 'undefined' && zoom > maxZoom) {
      if (marker.getMap() !== this.map) {
        marker.setMap(this.map);
      }
    } else if (mCount < this.minClusterSize) {
      if (marker.getMap() !== this.map) {
        marker.setMap(this.map);
      }
    } else if (mCount === this.minClusterSize) {
      for (var _i = 0, _b = this.markers; _i < _b.length; _i++) {
        var markerElement = _b[_i];
        markerElement.setMap(null);
      }
    } else {
      marker.setMap(null);
    }
    return true;
  };
  Cluster.prototype.isMarkerInClusterBounds = function (marker) {
    if (this.bounds !== null) {
      var position = marker.getPosition();
      if (position) {
        return this.bounds.contains(position);
      }
    }
    return false;
  };
  Cluster.prototype.calculateBounds = function () {
    this.bounds = this.markerClusterer.getExtendedBounds(new google.maps.LatLngBounds(this.center, this.center));
  };
  Cluster.prototype.updateIcon = function () {
    var _a;
    var mCount = this.markers.length;
    var maxZoom = this.markerClusterer.getMaxZoom();
    var zoom = (_a = this.map) === null || _a === void 0 ? void 0 : _a.getZoom();
    if (maxZoom !== null && typeof zoom !== 'undefined' && zoom > maxZoom) {
      this.clusterIcon.hide();
      return;
    }
    if (mCount < this.minClusterSize) {
      this.clusterIcon.hide();
      return;
    }
    if (this.center) {
      this.clusterIcon.setCenter(this.center);
    }
    this.clusterIcon.useStyle(this.markerClusterer.getCalculator()(this.markers, this.markerClusterer.getStyles().length));
    this.clusterIcon.show();
  };
  Cluster.prototype.isMarkerAlreadyAdded = function (marker) {
    if (this.markers.includes) {
      return this.markers.includes(marker);
    }
    for (var i = 0; i < this.markers.length; i++) {
      if (marker === this.markers[i]) {
        return true;
      }
    }
    return false;
  };
  return Cluster;
}();
function CALCULATOR(markers, numStyles) {
  var count = markers.length;
  var numberOfDigits = count.toString().length;
  var index = Math.min(numberOfDigits, numStyles);
  return {
    text: count.toString(),
    index: index,
    title: ''
  };
}
var BATCH_SIZE = 2000;
var BATCH_SIZE_IE = 500;
var IMAGE_PATH = 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m';
var IMAGE_EXTENSION = 'png';
var IMAGE_SIZES = [53, 56, 66, 78, 90];
var CLUSTERER_CLASS = 'cluster';
var Clusterer = function () {
  function Clusterer(map, optMarkers, optOptions) {
    if (optMarkers === void 0) {
      optMarkers = [];
    }
    if (optOptions === void 0) {
      optOptions = {};
    }
    this.getMinimumClusterSize = this.getMinimumClusterSize.bind(this);
    this.setMinimumClusterSize = this.setMinimumClusterSize.bind(this);
    this.getEnableRetinaIcons = this.getEnableRetinaIcons.bind(this);
    this.setEnableRetinaIcons = this.setEnableRetinaIcons.bind(this);
    this.addToClosestCluster = this.addToClosestCluster.bind(this);
    this.getImageExtension = this.getImageExtension.bind(this);
    this.setImageExtension = this.setImageExtension.bind(this);
    this.getExtendedBounds = this.getExtendedBounds.bind(this);
    this.getAverageCenter = this.getAverageCenter.bind(this);
    this.setAverageCenter = this.setAverageCenter.bind(this);
    this.getTotalClusters = this.getTotalClusters.bind(this);
    this.fitMapToMarkers = this.fitMapToMarkers.bind(this);
    this.getIgnoreHidden = this.getIgnoreHidden.bind(this);
    this.setIgnoreHidden = this.setIgnoreHidden.bind(this);
    this.getClusterClass = this.getClusterClass.bind(this);
    this.setClusterClass = this.setClusterClass.bind(this);
    this.getTotalMarkers = this.getTotalMarkers.bind(this);
    this.getZoomOnClick = this.getZoomOnClick.bind(this);
    this.setZoomOnClick = this.setZoomOnClick.bind(this);
    this.getBatchSizeIE = this.getBatchSizeIE.bind(this);
    this.setBatchSizeIE = this.setBatchSizeIE.bind(this);
    this.createClusters = this.createClusters.bind(this);
    this.onZoomChanged = this.onZoomChanged.bind(this);
    this.getImageSizes = this.getImageSizes.bind(this);
    this.setImageSizes = this.setImageSizes.bind(this);
    this.getCalculator = this.getCalculator.bind(this);
    this.setCalculator = this.setCalculator.bind(this);
    this.removeMarkers = this.removeMarkers.bind(this);
    this.resetViewport = this.resetViewport.bind(this);
    this.getImagePath = this.getImagePath.bind(this);
    this.setImagePath = this.setImagePath.bind(this);
    this.pushMarkerTo = this.pushMarkerTo.bind(this);
    this.removeMarker = this.removeMarker.bind(this);
    this.clearMarkers = this.clearMarkers.bind(this);
    this.setupStyles = this.setupStyles.bind(this);
    this.getGridSize = this.getGridSize.bind(this);
    this.setGridSize = this.setGridSize.bind(this);
    this.getClusters = this.getClusters.bind(this);
    this.getMaxZoom = this.getMaxZoom.bind(this);
    this.setMaxZoom = this.setMaxZoom.bind(this);
    this.getMarkers = this.getMarkers.bind(this);
    this.addMarkers = this.addMarkers.bind(this);
    this.getStyles = this.getStyles.bind(this);
    this.setStyles = this.setStyles.bind(this);
    this.addMarker = this.addMarker.bind(this);
    this.onRemove = this.onRemove.bind(this);
    this.getTitle = this.getTitle.bind(this);
    this.setTitle = this.setTitle.bind(this);
    this.repaint = this.repaint.bind(this);
    this.onIdle = this.onIdle.bind(this);
    this.redraw = this.redraw.bind(this);
    this.onAdd = this.onAdd.bind(this);
    this.draw = this.draw.bind(this);
    this.extend = this.extend.bind(this);
    this.extend(Clusterer, google.maps.OverlayView);
    this.markers = [];
    this.clusters = [];
    this.listeners = [];
    this.activeMap = null;
    this.ready = false;
    this.gridSize = optOptions.gridSize || 60;
    this.minClusterSize = optOptions.minimumClusterSize || 2;
    this.maxZoom = optOptions.maxZoom || null;
    this.styles = optOptions.styles || [];
    this.title = optOptions.title || '';
    this.zoomOnClick = true;
    if (optOptions.zoomOnClick !== undefined) {
      this.zoomOnClick = optOptions.zoomOnClick;
    }
    this.averageCenter = false;
    if (optOptions.averageCenter !== undefined) {
      this.averageCenter = optOptions.averageCenter;
    }
    this.ignoreHidden = false;
    if (optOptions.ignoreHidden !== undefined) {
      this.ignoreHidden = optOptions.ignoreHidden;
    }
    this.enableRetinaIcons = false;
    if (optOptions.enableRetinaIcons !== undefined) {
      this.enableRetinaIcons = optOptions.enableRetinaIcons;
    }
    this.imagePath = optOptions.imagePath || IMAGE_PATH;
    this.imageExtension = optOptions.imageExtension || IMAGE_EXTENSION;
    this.imageSizes = optOptions.imageSizes || IMAGE_SIZES;
    this.calculator = optOptions.calculator || CALCULATOR;
    this.batchSize = optOptions.batchSize || BATCH_SIZE;
    this.batchSizeIE = optOptions.batchSizeIE || BATCH_SIZE_IE;
    this.clusterClass = optOptions.clusterClass || CLUSTERER_CLASS;
    if (navigator.userAgent.toLowerCase().indexOf('msie') !== -1) {
      this.batchSize = this.batchSizeIE;
    }
    this.timerRefStatic = null;
    this.setupStyles();
    this.addMarkers(optMarkers, true);
    this.setMap(map);
  }
  Clusterer.prototype.onZoomChanged = function () {
    var _a, _b;
    this.resetViewport(false);
    if (((_a = this.getMap()) === null || _a === void 0 ? void 0 : _a.getZoom()) === (this.get('minZoom') || 0) || ((_b = this.getMap()) === null || _b === void 0 ? void 0 : _b.getZoom()) === this.get('maxZoom')) {
      google.maps.event.trigger(this, 'idle');
    }
  };
  Clusterer.prototype.onIdle = function () {
    this.redraw();
  };
  Clusterer.prototype.onAdd = function () {
    var map = this.getMap();
    this.activeMap = map;
    this.ready = true;
    this.repaint();
    if (map !== null) {
      this.listeners = [google.maps.event.addListener(map, 'zoom_changed', this.onZoomChanged), google.maps.event.addListener(map, 'idle', this.onIdle)];
    }
  };
  Clusterer.prototype.onRemove = function () {
    for (var _i = 0, _a = this.markers; _i < _a.length; _i++) {
      var marker = _a[_i];
      if (marker.getMap() !== this.activeMap) {
        marker.setMap(this.activeMap);
      }
    }
    for (var _b = 0, _c = this.clusters; _b < _c.length; _b++) {
      var cluster = _c[_b];
      cluster.remove();
    }
    this.clusters = [];
    for (var _d = 0, _e = this.listeners; _d < _e.length; _d++) {
      var listener = _e[_d];
      google.maps.event.removeListener(listener);
    }
    this.listeners = [];
    this.activeMap = null;
    this.ready = false;
  };
  Clusterer.prototype.draw = function () {
    return;
  };
  Clusterer.prototype.getMap = function () {
    return null;
  };
  Clusterer.prototype.getPanes = function () {
    return null;
  };
  Clusterer.prototype.getProjection = function () {
    return {
      fromContainerPixelToLatLng: function fromContainerPixelToLatLng() {
        return null;
      },
      fromDivPixelToLatLng: function fromDivPixelToLatLng() {
        return null;
      },
      fromLatLngToContainerPixel: function fromLatLngToContainerPixel() {
        return null;
      },
      fromLatLngToDivPixel: function fromLatLngToDivPixel() {
        return null;
      },
      getVisibleRegion: function getVisibleRegion() {
        return null;
      },
      getWorldWidth: function getWorldWidth() {
        return 0;
      }
    };
  };
  Clusterer.prototype.setMap = function () {
    return;
  };
  Clusterer.prototype.addListener = function () {
    return {
      remove: function remove() {
        return;
      }
    };
  };
  Clusterer.prototype.bindTo = function () {
    return;
  };
  Clusterer.prototype.get = function () {
    return;
  };
  Clusterer.prototype.notify = function () {
    return;
  };
  Clusterer.prototype.set = function () {
    return;
  };
  Clusterer.prototype.setValues = function () {
    return;
  };
  Clusterer.prototype.unbind = function () {
    return;
  };
  Clusterer.prototype.unbindAll = function () {
    return;
  };
  Clusterer.prototype.setupStyles = function () {
    if (this.styles.length > 0) {
      return;
    }
    for (var i = 0; i < this.imageSizes.length; i++) {
      this.styles.push({
        url: "".concat(this.imagePath + (i + 1), ".").concat(this.imageExtension),
        height: this.imageSizes[i] || 0,
        width: this.imageSizes[i] || 0
      });
    }
  };
  Clusterer.prototype.fitMapToMarkers = function () {
    var markers = this.getMarkers();
    var bounds = new google.maps.LatLngBounds();
    for (var _i = 0, markers_1 = markers; _i < markers_1.length; _i++) {
      var marker = markers_1[_i];
      var position = marker.getPosition();
      if (position) {
        bounds.extend(position);
      }
    }
    var map = this.getMap();
    if (map !== null && 'fitBounds' in map) {
      map.fitBounds(bounds);
    }
  };
  Clusterer.prototype.getGridSize = function () {
    return this.gridSize;
  };
  Clusterer.prototype.setGridSize = function (gridSize) {
    this.gridSize = gridSize;
  };
  Clusterer.prototype.getMinimumClusterSize = function () {
    return this.minClusterSize;
  };
  Clusterer.prototype.setMinimumClusterSize = function (minimumClusterSize) {
    this.minClusterSize = minimumClusterSize;
  };
  Clusterer.prototype.getMaxZoom = function () {
    return this.maxZoom;
  };
  Clusterer.prototype.setMaxZoom = function (maxZoom) {
    this.maxZoom = maxZoom;
  };
  Clusterer.prototype.getStyles = function () {
    return this.styles;
  };
  Clusterer.prototype.setStyles = function (styles) {
    this.styles = styles;
  };
  Clusterer.prototype.getTitle = function () {
    return this.title;
  };
  Clusterer.prototype.setTitle = function (title) {
    this.title = title;
  };
  Clusterer.prototype.getZoomOnClick = function () {
    return this.zoomOnClick;
  };
  Clusterer.prototype.setZoomOnClick = function (zoomOnClick) {
    this.zoomOnClick = zoomOnClick;
  };
  Clusterer.prototype.getAverageCenter = function () {
    return this.averageCenter;
  };
  Clusterer.prototype.setAverageCenter = function (averageCenter) {
    this.averageCenter = averageCenter;
  };
  Clusterer.prototype.getIgnoreHidden = function () {
    return this.ignoreHidden;
  };
  Clusterer.prototype.setIgnoreHidden = function (ignoreHidden) {
    this.ignoreHidden = ignoreHidden;
  };
  Clusterer.prototype.getEnableRetinaIcons = function () {
    return this.enableRetinaIcons;
  };
  Clusterer.prototype.setEnableRetinaIcons = function (enableRetinaIcons) {
    this.enableRetinaIcons = enableRetinaIcons;
  };
  Clusterer.prototype.getImageExtension = function () {
    return this.imageExtension;
  };
  Clusterer.prototype.setImageExtension = function (imageExtension) {
    this.imageExtension = imageExtension;
  };
  Clusterer.prototype.getImagePath = function () {
    return this.imagePath;
  };
  Clusterer.prototype.setImagePath = function (imagePath) {
    this.imagePath = imagePath;
  };
  Clusterer.prototype.getImageSizes = function () {
    return this.imageSizes;
  };
  Clusterer.prototype.setImageSizes = function (imageSizes) {
    this.imageSizes = imageSizes;
  };
  Clusterer.prototype.getCalculator = function () {
    return this.calculator;
  };
  Clusterer.prototype.setCalculator = function (calculator) {
    this.calculator = calculator;
  };
  Clusterer.prototype.getBatchSizeIE = function () {
    return this.batchSizeIE;
  };
  Clusterer.prototype.setBatchSizeIE = function (batchSizeIE) {
    this.batchSizeIE = batchSizeIE;
  };
  Clusterer.prototype.getClusterClass = function () {
    return this.clusterClass;
  };
  Clusterer.prototype.setClusterClass = function (clusterClass) {
    this.clusterClass = clusterClass;
  };
  Clusterer.prototype.getMarkers = function () {
    return this.markers;
  };
  Clusterer.prototype.getTotalMarkers = function () {
    return this.markers.length;
  };
  Clusterer.prototype.getClusters = function () {
    return this.clusters;
  };
  Clusterer.prototype.getTotalClusters = function () {
    return this.clusters.length;
  };
  Clusterer.prototype.addMarker = function (marker, optNoDraw) {
    this.pushMarkerTo(marker);
    if (!optNoDraw) {
      this.redraw();
    }
  };
  Clusterer.prototype.addMarkers = function (markers, optNoDraw) {
    for (var key in markers) {
      if (Object.prototype.hasOwnProperty.call(markers, key)) {
        var marker = markers[key];
        if (marker) {
          this.pushMarkerTo(marker);
        }
      }
    }
    if (!optNoDraw) {
      this.redraw();
    }
  };
  Clusterer.prototype.pushMarkerTo = function (marker) {
    var _this = this;
    if (marker.getDraggable()) {
      google.maps.event.addListener(marker, 'dragend', function () {
        if (_this.ready) {
          marker.isAdded = false;
          _this.repaint();
        }
      });
    }
    marker.isAdded = false;
    this.markers.push(marker);
  };
  Clusterer.prototype.removeMarker_ = function (marker) {
    var index = -1;
    if (this.markers.indexOf) {
      index = this.markers.indexOf(marker);
    } else {
      for (var i = 0; i < this.markers.length; i++) {
        if (marker === this.markers[i]) {
          index = i;
          break;
        }
      }
    }
    if (index === -1) {
      return false;
    }
    marker.setMap(null);
    this.markers.splice(index, 1);
    return true;
  };
  Clusterer.prototype.removeMarker = function (marker, optNoDraw) {
    var removed = this.removeMarker_(marker);
    if (!optNoDraw && removed) {
      this.repaint();
    }
    return removed;
  };
  Clusterer.prototype.removeMarkers = function (markers, optNoDraw) {
    var removed = false;
    for (var _i = 0, markers_2 = markers; _i < markers_2.length; _i++) {
      var marker = markers_2[_i];
      removed = removed || this.removeMarker_(marker);
    }
    if (!optNoDraw && removed) {
      this.repaint();
    }
    return removed;
  };
  Clusterer.prototype.clearMarkers = function () {
    this.resetViewport(true);
    this.markers = [];
  };
  Clusterer.prototype.repaint = function () {
    var oldClusters = this.clusters.slice();
    this.clusters = [];
    this.resetViewport(false);
    this.redraw();
    setTimeout(function timeout() {
      for (var _i = 0, oldClusters_1 = oldClusters; _i < oldClusters_1.length; _i++) {
        var oldCluster = oldClusters_1[_i];
        oldCluster.remove();
      }
    }, 0);
  };
  Clusterer.prototype.getExtendedBounds = function (bounds) {
    var projection = this.getProjection();
    var trPix = projection.fromLatLngToDivPixel(new google.maps.LatLng(bounds.getNorthEast().lat(), bounds.getNorthEast().lng()));
    if (trPix !== null) {
      trPix.x += this.gridSize;
      trPix.y -= this.gridSize;
    }
    var blPix = projection.fromLatLngToDivPixel(new google.maps.LatLng(bounds.getSouthWest().lat(), bounds.getSouthWest().lng()));
    if (blPix !== null) {
      blPix.x -= this.gridSize;
      blPix.y += this.gridSize;
    }
    if (trPix !== null) {
      var point1 = projection.fromDivPixelToLatLng(trPix);
      if (point1 !== null) {
        bounds.extend(point1);
      }
    }
    if (blPix !== null) {
      var point2 = projection.fromDivPixelToLatLng(blPix);
      if (point2 !== null) {
        bounds.extend(point2);
      }
    }
    return bounds;
  };
  Clusterer.prototype.redraw = function () {
    this.createClusters(0);
  };
  Clusterer.prototype.resetViewport = function (optHide) {
    for (var _i = 0, _a = this.clusters; _i < _a.length; _i++) {
      var cluster = _a[_i];
      cluster.remove();
    }
    this.clusters = [];
    for (var _b = 0, _c = this.markers; _b < _c.length; _b++) {
      var marker = _c[_b];
      marker.isAdded = false;
      if (optHide) {
        marker.setMap(null);
      }
    }
  };
  Clusterer.prototype.distanceBetweenPoints = function (p1, p2) {
    var R = 6371;
    var dLat = (p2.lat() - p1.lat()) * Math.PI / 180;
    var dLon = (p2.lng() - p1.lng()) * Math.PI / 180;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(p1.lat() * Math.PI / 180) * Math.cos(p2.lat() * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };
  Clusterer.prototype.isMarkerInBounds = function (marker, bounds) {
    var position = marker.getPosition();
    if (position) {
      return bounds.contains(position);
    }
    return false;
  };
  Clusterer.prototype.addToClosestCluster = function (marker) {
    var cluster;
    var distance = 40000;
    var clusterToAddTo = null;
    for (var _i = 0, _a = this.clusters; _i < _a.length; _i++) {
      var clusterElement = _a[_i];
      cluster = clusterElement;
      var center = cluster.getCenter();
      var position = marker.getPosition();
      if (center && position) {
        var d = this.distanceBetweenPoints(center, position);
        if (d < distance) {
          distance = d;
          clusterToAddTo = cluster;
        }
      }
    }
    if (clusterToAddTo && clusterToAddTo.isMarkerInClusterBounds(marker)) {
      clusterToAddTo.addMarker(marker);
    } else {
      cluster = new Cluster$1(this);
      cluster.addMarker(marker);
      this.clusters.push(cluster);
    }
  };
  Clusterer.prototype.createClusters = function (iFirst) {
    var _this = this;
    if (!this.ready) {
      return;
    }
    if (iFirst === 0) {
      google.maps.event.trigger(this, 'clusteringbegin', this);
      if (this.timerRefStatic !== null) {
        window.clearTimeout(this.timerRefStatic);
        delete this.timerRefStatic;
      }
    }
    var map = this.getMap();
    var bounds = map !== null && 'getBounds' in map ? map.getBounds() : null;
    var zoom = (map === null || map === void 0 ? void 0 : map.getZoom()) || 0;
    var mapBounds = zoom > 3 ? new google.maps.LatLngBounds(bounds === null || bounds === void 0 ? void 0 : bounds.getSouthWest(), bounds === null || bounds === void 0 ? void 0 : bounds.getNorthEast()) : new google.maps.LatLngBounds(new google.maps.LatLng(85.02070771743472, -178.48388434375), new google.maps.LatLng(-85.08136444384544, 178.00048865625));
    var extendedMapBounds = this.getExtendedBounds(mapBounds);
    var iLast = Math.min(iFirst + this.batchSize, this.markers.length);
    for (var i = iFirst; i < iLast; i++) {
      var marker = this.markers[i];
      if (marker && !marker.isAdded && this.isMarkerInBounds(marker, extendedMapBounds) && (!this.ignoreHidden || this.ignoreHidden && marker.getVisible())) {
        this.addToClosestCluster(marker);
      }
    }
    if (iLast < this.markers.length) {
      this.timerRefStatic = window.setTimeout(function () {
        _this.createClusters(iLast);
      }, 0);
    } else {
      this.timerRefStatic = null;
      google.maps.event.trigger(this, 'clusteringend', this);
      for (var _i = 0, _a = this.clusters; _i < _a.length; _i++) {
        var cluster = _a[_i];
        cluster.updateIcon();
      }
    }
  };
  Clusterer.prototype.extend = function (obj1, obj2) {
    return function applyExtend(object) {
      for (var property in object.prototype) {
        var prop = property;
        this.prototype[prop] = object.prototype[prop];
      }
      return this;
    }.apply(obj1, [obj2]);
  };
  return Clusterer;
}();
var eventMap$e = {
  onClick: 'click',
  onClusteringBegin: 'clusteringbegin',
  onClusteringEnd: 'clusteringend',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover'
};
var updaterMap$e = {
  averageCenter: function averageCenter(instance, _averageCenter) {
    instance.setAverageCenter(_averageCenter);
  },
  batchSizeIE: function batchSizeIE(instance, _batchSizeIE) {
    instance.setBatchSizeIE(_batchSizeIE);
  },
  calculator: function calculator(instance, _calculator) {
    instance.setCalculator(_calculator);
  },
  clusterClass: function clusterClass(instance, _clusterClass) {
    instance.setClusterClass(_clusterClass);
  },
  enableRetinaIcons: function enableRetinaIcons(instance, _enableRetinaIcons) {
    instance.setEnableRetinaIcons(_enableRetinaIcons);
  },
  gridSize: function gridSize(instance, _gridSize) {
    instance.setGridSize(_gridSize);
  },
  ignoreHidden: function ignoreHidden(instance, _ignoreHidden) {
    instance.setIgnoreHidden(_ignoreHidden);
  },
  imageExtension: function imageExtension(instance, _imageExtension) {
    instance.setImageExtension(_imageExtension);
  },
  imagePath: function imagePath(instance, _imagePath) {
    instance.setImagePath(_imagePath);
  },
  imageSizes: function imageSizes(instance, _imageSizes) {
    instance.setImageSizes(_imageSizes);
  },
  maxZoom: function maxZoom(instance, _maxZoom) {
    instance.setMaxZoom(_maxZoom);
  },
  minimumClusterSize: function minimumClusterSize(instance, _minimumClusterSize) {
    instance.setMinimumClusterSize(_minimumClusterSize);
  },
  styles: function styles(instance, _styles) {
    instance.setStyles(_styles);
  },
  title: function title(instance, _title2) {
    instance.setTitle(_title2);
  },
  zoomOnClick: function zoomOnClick(instance, _zoomOnClick) {
    instance.setZoomOnClick(_zoomOnClick);
  }
};
var defaultOptions$4 = {};
function MarkerClustererFunctional(props) {
  var children = props.children,
    options = props.options,
    averageCenter = props.averageCenter,
    batchSizeIE = props.batchSizeIE,
    calculator = props.calculator,
    clusterClass = props.clusterClass,
    enableRetinaIcons = props.enableRetinaIcons,
    gridSize = props.gridSize,
    ignoreHidden = props.ignoreHidden,
    imageExtension = props.imageExtension,
    imagePath = props.imagePath,
    imageSizes = props.imageSizes,
    maxZoom = props.maxZoom,
    minimumClusterSize = props.minimumClusterSize,
    styles = props.styles,
    title = props.title,
    zoomOnClick = props.zoomOnClick,
    onClick = props.onClick,
    onClusteringBegin = props.onClusteringBegin,
    onClusteringEnd = props.onClusteringEnd,
    onMouseOver = props.onMouseOver,
    onMouseOut = props.onMouseOut,
    onLoad = props.onLoad,
    onUnmount = props.onUnmount;
  var _a = React.useState(null),
    instance = _a[0],
    setInstance = _a[1];
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    clickListener = _b[0],
    setClickListener = _b[1];
  var _c = React.useState(null),
    clusteringBeginListener = _c[0],
    setClusteringBeginListener = _c[1];
  var _d = React.useState(null),
    clusteringEndListener = _d[0],
    setClusteringEndListener = _d[1];
  var _e = React.useState(null),
    mouseoutListener = _e[0],
    setMouseoutListener = _e[1];
  var _f = React.useState(null),
    mouseoverListener = _f[0],
    setMouseoverListener = _f[1];
  React.useEffect(function () {
    if (instance && onMouseOut) {
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      setMouseoutListener(google.maps.event.addListener(instance, eventMap$e.onMouseOut, onMouseOut));
    }
  }, [onMouseOut]);
  React.useEffect(function () {
    if (instance && onMouseOver) {
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      setMouseoverListener(google.maps.event.addListener(instance, eventMap$e.onMouseOver, onMouseOver));
    }
  }, [onMouseOver]);
  React.useEffect(function () {
    if (instance && onClick) {
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      setClickListener(google.maps.event.addListener(instance, eventMap$e.onClick, onClick));
    }
  }, [onClick]);
  React.useEffect(function () {
    if (instance && onClusteringBegin) {
      if (clusteringBeginListener !== null) {
        google.maps.event.removeListener(clusteringBeginListener);
      }
      setClusteringBeginListener(google.maps.event.addListener(instance, eventMap$e.onClusteringBegin, onClusteringBegin));
    }
  }, [onClusteringBegin]);
  React.useEffect(function () {
    if (instance && onClusteringEnd) {
      if (clusteringEndListener !== null) {
        google.maps.event.removeListener(clusteringEndListener);
      }
      setClusteringBeginListener(google.maps.event.addListener(instance, eventMap$e.onClusteringEnd, onClusteringEnd));
    }
  }, [onClusteringEnd]);
  React.useEffect(function () {
    if (typeof averageCenter !== 'undefined' && instance !== null) {
      updaterMap$e.averageCenter(instance, averageCenter);
    }
  }, [instance, averageCenter]);
  React.useEffect(function () {
    if (typeof batchSizeIE !== 'undefined' && instance !== null) {
      updaterMap$e.batchSizeIE(instance, batchSizeIE);
    }
  }, [instance, batchSizeIE]);
  React.useEffect(function () {
    if (typeof calculator !== 'undefined' && instance !== null) {
      updaterMap$e.calculator(instance, calculator);
    }
  }, [instance, calculator]);
  React.useEffect(function () {
    if (typeof clusterClass !== 'undefined' && instance !== null) {
      updaterMap$e.clusterClass(instance, clusterClass);
    }
  }, [instance, clusterClass]);
  React.useEffect(function () {
    if (typeof enableRetinaIcons !== 'undefined' && instance !== null) {
      updaterMap$e.enableRetinaIcons(instance, enableRetinaIcons);
    }
  }, [instance, enableRetinaIcons]);
  React.useEffect(function () {
    if (typeof gridSize !== 'undefined' && instance !== null) {
      updaterMap$e.gridSize(instance, gridSize);
    }
  }, [instance, gridSize]);
  React.useEffect(function () {
    if (typeof ignoreHidden !== 'undefined' && instance !== null) {
      updaterMap$e.ignoreHidden(instance, ignoreHidden);
    }
  }, [instance, ignoreHidden]);
  React.useEffect(function () {
    if (typeof imageExtension !== 'undefined' && instance !== null) {
      updaterMap$e.imageExtension(instance, imageExtension);
    }
  }, [instance, imageExtension]);
  React.useEffect(function () {
    if (typeof imagePath !== 'undefined' && instance !== null) {
      updaterMap$e.imagePath(instance, imagePath);
    }
  }, [instance, imagePath]);
  React.useEffect(function () {
    if (typeof imageSizes !== 'undefined' && instance !== null) {
      updaterMap$e.imageSizes(instance, imageSizes);
    }
  }, [instance, imageSizes]);
  React.useEffect(function () {
    if (typeof maxZoom !== 'undefined' && instance !== null) {
      updaterMap$e.maxZoom(instance, maxZoom);
    }
  }, [instance, maxZoom]);
  React.useEffect(function () {
    if (typeof minimumClusterSize !== 'undefined' && instance !== null) {
      updaterMap$e.minimumClusterSize(instance, minimumClusterSize);
    }
  }, [instance, minimumClusterSize]);
  React.useEffect(function () {
    if (typeof styles !== 'undefined' && instance !== null) {
      updaterMap$e.styles(instance, styles);
    }
  }, [instance, styles]);
  React.useEffect(function () {
    if (typeof title !== 'undefined' && instance !== null) {
      updaterMap$e.title(instance, title);
    }
  }, [instance, title]);
  React.useEffect(function () {
    if (typeof zoomOnClick !== 'undefined' && instance !== null) {
      updaterMap$e.zoomOnClick(instance, zoomOnClick);
    }
  }, [instance, zoomOnClick]);
  React.useEffect(function () {
    if (!map) return;
    var clustererOptions = _assign({}, options || defaultOptions$4);
    var clusterer = new Clusterer(map, [], clustererOptions);
    if (averageCenter) {
      updaterMap$e.averageCenter(clusterer, averageCenter);
    }
    if (batchSizeIE) {
      updaterMap$e.batchSizeIE(clusterer, batchSizeIE);
    }
    if (calculator) {
      updaterMap$e.calculator(clusterer, calculator);
    }
    if (clusterClass) {
      updaterMap$e.clusterClass(clusterer, clusterClass);
    }
    if (enableRetinaIcons) {
      updaterMap$e.enableRetinaIcons(clusterer, enableRetinaIcons);
    }
    if (gridSize) {
      updaterMap$e.gridSize(clusterer, gridSize);
    }
    if (ignoreHidden) {
      updaterMap$e.ignoreHidden(clusterer, ignoreHidden);
    }
    if (imageExtension) {
      updaterMap$e.imageExtension(clusterer, imageExtension);
    }
    if (imagePath) {
      updaterMap$e.imagePath(clusterer, imagePath);
    }
    if (imageSizes) {
      updaterMap$e.imageSizes(clusterer, imageSizes);
    }
    if (maxZoom) {
      updaterMap$e.maxZoom(clusterer, maxZoom);
    }
    if (minimumClusterSize) {
      updaterMap$e.minimumClusterSize(clusterer, minimumClusterSize);
    }
    if (styles) {
      updaterMap$e.styles(clusterer, styles);
    }
    if (title) {
      updaterMap$e.title(clusterer, title);
    }
    if (zoomOnClick) {
      updaterMap$e.zoomOnClick(clusterer, zoomOnClick);
    }
    if (onMouseOut) {
      setMouseoutListener(google.maps.event.addListener(clusterer, eventMap$e.onMouseOut, onMouseOut));
    }
    if (onMouseOver) {
      setMouseoverListener(google.maps.event.addListener(clusterer, eventMap$e.onMouseOver, onMouseOver));
    }
    if (onClick) {
      setClickListener(google.maps.event.addListener(clusterer, eventMap$e.onClick, onClick));
    }
    if (onClusteringBegin) {
      setClusteringBeginListener(google.maps.event.addListener(clusterer, eventMap$e.onClusteringBegin, onClusteringBegin));
    }
    if (onClusteringEnd) {
      setClusteringEndListener(google.maps.event.addListener(clusterer, eventMap$e.onClusteringEnd, onClusteringEnd));
    }
    setInstance(clusterer);
    if (onLoad) {
      onLoad(clusterer);
    }
    return function () {
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      if (clusteringBeginListener !== null) {
        google.maps.event.removeListener(clusteringBeginListener);
      }
      if (clusteringEndListener !== null) {
        google.maps.event.removeListener(clusteringEndListener);
      }
      if (onUnmount) {
        onUnmount(clusterer);
      }
    };
  }, []);
  return instance !== null ? children(instance) || null : null;
}
var MarkerClustererF = React.memo(MarkerClustererFunctional);
var ClustererComponent = function (_super) {
  __extends(ClustererComponent, _super);
  function ClustererComponent() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.state = {
      markerClusterer: null
    };
    _this.setClustererCallback = function () {
      if (_this.state.markerClusterer !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.markerClusterer);
      }
    };
    return _this;
  }
  ClustererComponent.prototype.componentDidMount = function () {
    if (this.context) {
      var markerClusterer_1 = new Clusterer(this.context, [], this.props.options);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$e,
        eventMap: eventMap$e,
        prevProps: {},
        nextProps: this.props,
        instance: markerClusterer_1
      });
      this.setState(function () {
        return {
          markerClusterer: markerClusterer_1
        };
      }, this.setClustererCallback);
    }
  };
  ClustererComponent.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.markerClusterer) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$e,
        eventMap: eventMap$e,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.markerClusterer
      });
    }
  };
  ClustererComponent.prototype.componentWillUnmount = function () {
    if (this.state.markerClusterer !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.markerClusterer);
      }
      unregisterEvents(this.registeredEvents);
      this.state.markerClusterer.setMap(null);
    }
  };
  ClustererComponent.prototype.render = function () {
    return this.state.markerClusterer !== null ? this.props.children(this.state.markerClusterer) : null;
  };
  ClustererComponent.contextType = MapContext;
  return ClustererComponent;
}(React.PureComponent);
function cancelHandler(event) {
  event.cancelBubble = true;
  if (event.stopPropagation) {
    event.stopPropagation();
  }
}
var InfoBox = function () {
  function InfoBox(options) {
    if (options === void 0) {
      options = {};
    }
    this.getCloseClickHandler = this.getCloseClickHandler.bind(this);
    this.closeClickHandler = this.closeClickHandler.bind(this);
    this.createInfoBoxDiv = this.createInfoBoxDiv.bind(this);
    this.addClickHandler = this.addClickHandler.bind(this);
    this.getCloseBoxImg = this.getCloseBoxImg.bind(this);
    this.getBoxWidths = this.getBoxWidths.bind(this);
    this.setBoxStyle = this.setBoxStyle.bind(this);
    this.setPosition = this.setPosition.bind(this);
    this.getPosition = this.getPosition.bind(this);
    this.setOptions = this.setOptions.bind(this);
    this.setContent = this.setContent.bind(this);
    this.setVisible = this.setVisible.bind(this);
    this.getContent = this.getContent.bind(this);
    this.getVisible = this.getVisible.bind(this);
    this.setZIndex = this.setZIndex.bind(this);
    this.getZIndex = this.getZIndex.bind(this);
    this.onRemove = this.onRemove.bind(this);
    this.panBox = this.panBox.bind(this);
    this.extend = this.extend.bind(this);
    this.close = this.close.bind(this);
    this.draw = this.draw.bind(this);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.open = this.open.bind(this);
    this.extend(InfoBox, google.maps.OverlayView);
    this.content = options.content || '';
    this.disableAutoPan = options.disableAutoPan || false;
    this.maxWidth = options.maxWidth || 0;
    this.pixelOffset = options.pixelOffset || new google.maps.Size(0, 0);
    this.position = options.position || new google.maps.LatLng(0, 0);
    this.zIndex = options.zIndex || null;
    this.boxClass = options.boxClass || 'infoBox';
    this.boxStyle = options.boxStyle || {};
    this.closeBoxMargin = options.closeBoxMargin || '2px';
    this.closeBoxURL = options.closeBoxURL || 'http://www.google.com/intl/en_us/mapfiles/close.gif';
    if (options.closeBoxURL === '') {
      this.closeBoxURL = '';
    }
    this.infoBoxClearance = options.infoBoxClearance || new google.maps.Size(1, 1);
    if (typeof options.visible === 'undefined') {
      if (typeof options.isHidden === 'undefined') {
        options.visible = true;
      } else {
        options.visible = !options.isHidden;
      }
    }
    this.isHidden = !options.visible;
    this.alignBottom = options.alignBottom || false;
    this.pane = options.pane || 'floatPane';
    this.enableEventPropagation = options.enableEventPropagation || false;
    this.div = null;
    this.closeListener = null;
    this.moveListener = null;
    this.mapListener = null;
    this.contextListener = null;
    this.eventListeners = null;
    this.fixedWidthSet = null;
  }
  InfoBox.prototype.createInfoBoxDiv = function () {
    var _this = this;
    var ignoreHandler = function ignoreHandler(event) {
      event.returnValue = false;
      if (event.preventDefault) {
        event.preventDefault();
      }
      if (!_this.enableEventPropagation) {
        cancelHandler(event);
      }
    };
    if (!this.div) {
      this.div = document.createElement('div');
      this.setBoxStyle();
      if (typeof this.content === 'string') {
        this.div.innerHTML = this.getCloseBoxImg() + this.content;
      } else {
        this.div.innerHTML = this.getCloseBoxImg();
        this.div.appendChild(this.content);
      }
      var panes = this.getPanes();
      if (panes !== null) {
        panes[this.pane].appendChild(this.div);
      }
      this.addClickHandler();
      if (this.div.style.width) {
        this.fixedWidthSet = true;
      } else {
        if (this.maxWidth !== 0 && this.div.offsetWidth > this.maxWidth) {
          this.div.style.width = this.maxWidth + 'px';
          this.fixedWidthSet = true;
        } else {
          var bw = this.getBoxWidths();
          this.div.style.width = this.div.offsetWidth - bw.left - bw.right + 'px';
          this.fixedWidthSet = false;
        }
      }
      this.panBox(this.disableAutoPan);
      if (!this.enableEventPropagation) {
        this.eventListeners = [];
        var events = ['mousedown', 'mouseover', 'mouseout', 'mouseup', 'click', 'dblclick', 'touchstart', 'touchend', 'touchmove'];
        for (var _i = 0, events_1 = events; _i < events_1.length; _i++) {
          var event_1 = events_1[_i];
          this.eventListeners.push(google.maps.event.addListener(this.div, event_1, cancelHandler));
        }
        this.eventListeners.push(google.maps.event.addListener(this.div, 'mouseover', function () {
          if (_this.div) {
            _this.div.style.cursor = 'default';
          }
        }));
      }
      this.contextListener = google.maps.event.addListener(this.div, 'contextmenu', ignoreHandler);
      google.maps.event.trigger(this, 'domready');
    }
  };
  InfoBox.prototype.getCloseBoxImg = function () {
    var img = '';
    if (this.closeBoxURL !== '') {
      img = '<img alt=""';
      img += ' aria-hidden="true"';
      img += " src='" + this.closeBoxURL + "'";
      img += ' align=right';
      img += " style='";
      img += ' position: relative;';
      img += ' cursor: pointer;';
      img += ' margin: ' + this.closeBoxMargin + ';';
      img += "'>";
    }
    return img;
  };
  InfoBox.prototype.addClickHandler = function () {
    this.closeListener = this.div && this.div.firstChild && this.closeBoxURL !== '' ? google.maps.event.addListener(this.div.firstChild, 'click', this.getCloseClickHandler()) : null;
  };
  InfoBox.prototype.closeClickHandler = function (event) {
    event.cancelBubble = true;
    if (event.stopPropagation) {
      event.stopPropagation();
    }
    google.maps.event.trigger(this, 'closeclick');
    this.close();
  };
  InfoBox.prototype.getCloseClickHandler = function () {
    return this.closeClickHandler;
  };
  InfoBox.prototype.panBox = function (disablePan) {
    if (this.div && !disablePan) {
      var map = this.getMap();
      if (map instanceof google.maps.Map) {
        var xOffset = 0;
        var yOffset = 0;
        var bounds = map.getBounds();
        if (bounds && !bounds.contains(this.position)) {
          map.setCenter(this.position);
        }
        var mapDiv = map.getDiv();
        var mapWidth = mapDiv.offsetWidth;
        var mapHeight = mapDiv.offsetHeight;
        var iwOffsetX = this.pixelOffset.width;
        var iwOffsetY = this.pixelOffset.height;
        var iwWidth = this.div.offsetWidth;
        var iwHeight = this.div.offsetHeight;
        var padX = this.infoBoxClearance.width;
        var padY = this.infoBoxClearance.height;
        var projection = this.getProjection();
        var pixPosition = projection.fromLatLngToContainerPixel(this.position);
        if (pixPosition !== null) {
          if (pixPosition.x < -iwOffsetX + padX) {
            xOffset = pixPosition.x + iwOffsetX - padX;
          } else if (pixPosition.x + iwWidth + iwOffsetX + padX > mapWidth) {
            xOffset = pixPosition.x + iwWidth + iwOffsetX + padX - mapWidth;
          }
          if (this.alignBottom) {
            if (pixPosition.y < -iwOffsetY + padY + iwHeight) {
              yOffset = pixPosition.y + iwOffsetY - padY - iwHeight;
            } else if (pixPosition.y + iwOffsetY + padY > mapHeight) {
              yOffset = pixPosition.y + iwOffsetY + padY - mapHeight;
            }
          } else {
            if (pixPosition.y < -iwOffsetY + padY) {
              yOffset = pixPosition.y + iwOffsetY - padY;
            } else if (pixPosition.y + iwHeight + iwOffsetY + padY > mapHeight) {
              yOffset = pixPosition.y + iwHeight + iwOffsetY + padY - mapHeight;
            }
          }
        }
        if (!(xOffset === 0 && yOffset === 0)) {
          map.panBy(xOffset, yOffset);
        }
      }
    }
  };
  InfoBox.prototype.setBoxStyle = function () {
    if (this.div) {
      this.div.className = this.boxClass;
      this.div.style.cssText = '';
      var boxStyle = this.boxStyle;
      for (var i in boxStyle) {
        if (Object.prototype.hasOwnProperty.call(boxStyle, i)) {
          this.div.style[i] = boxStyle[i];
        }
      }
      this.div.style.webkitTransform = 'translateZ(0)';
      if (typeof this.div.style.opacity !== 'undefined' && this.div.style.opacity !== '') {
        var opacity = parseFloat(this.div.style.opacity || '');
        this.div.style.msFilter = '"progid:DXImageTransform.Microsoft.Alpha(Opacity=' + opacity * 100 + ')"';
        this.div.style.filter = 'alpha(opacity=' + opacity * 100 + ')';
      }
      this.div.style.position = 'absolute';
      this.div.style.visibility = 'hidden';
      if (this.zIndex !== null) {
        this.div.style.zIndex = this.zIndex + '';
      }
      if (!this.div.style.overflow) {
        this.div.style.overflow = 'auto';
      }
    }
  };
  InfoBox.prototype.getBoxWidths = function () {
    var bw = {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    };
    if (!this.div) {
      return bw;
    }
    if (document.defaultView) {
      var ownerDocument = this.div.ownerDocument;
      var computedStyle = ownerDocument && ownerDocument.defaultView ? ownerDocument.defaultView.getComputedStyle(this.div, '') : null;
      if (computedStyle) {
        bw.top = parseInt(computedStyle.borderTopWidth || '', 10) || 0;
        bw.bottom = parseInt(computedStyle.borderBottomWidth || '', 10) || 0;
        bw.left = parseInt(computedStyle.borderLeftWidth || '', 10) || 0;
        bw.right = parseInt(computedStyle.borderRightWidth || '', 10) || 0;
      }
    } else if (document.documentElement.currentStyle) {
      var currentStyle = this.div.currentStyle;
      if (currentStyle) {
        bw.top = parseInt(currentStyle.borderTopWidth || '', 10) || 0;
        bw.bottom = parseInt(currentStyle.borderBottomWidth || '', 10) || 0;
        bw.left = parseInt(currentStyle.borderLeftWidth || '', 10) || 0;
        bw.right = parseInt(currentStyle.borderRightWidth || '', 10) || 0;
      }
    }
    return bw;
  };
  InfoBox.prototype.onRemove = function () {
    if (this.div && this.div.parentNode) {
      this.div.parentNode.removeChild(this.div);
      this.div = null;
    }
  };
  InfoBox.prototype.draw = function () {
    this.createInfoBoxDiv();
    if (this.div) {
      var projection = this.getProjection();
      var pixPosition = projection.fromLatLngToDivPixel(this.position);
      if (pixPosition !== null) {
        this.div.style.left = pixPosition.x + this.pixelOffset.width + 'px';
        if (this.alignBottom) {
          this.div.style.bottom = -(pixPosition.y + this.pixelOffset.height) + 'px';
        } else {
          this.div.style.top = pixPosition.y + this.pixelOffset.height + 'px';
        }
      }
      if (this.isHidden) {
        this.div.style.visibility = 'hidden';
      } else {
        this.div.style.visibility = 'visible';
      }
    }
  };
  InfoBox.prototype.setOptions = function (options) {
    if (options === void 0) {
      options = {};
    }
    if (typeof options.boxClass !== 'undefined') {
      this.boxClass = options.boxClass;
      this.setBoxStyle();
    }
    if (typeof options.boxStyle !== 'undefined') {
      this.boxStyle = options.boxStyle;
      this.setBoxStyle();
    }
    if (typeof options.content !== 'undefined') {
      this.setContent(options.content);
    }
    if (typeof options.disableAutoPan !== 'undefined') {
      this.disableAutoPan = options.disableAutoPan;
    }
    if (typeof options.maxWidth !== 'undefined') {
      this.maxWidth = options.maxWidth;
    }
    if (typeof options.pixelOffset !== 'undefined') {
      this.pixelOffset = options.pixelOffset;
    }
    if (typeof options.alignBottom !== 'undefined') {
      this.alignBottom = options.alignBottom;
    }
    if (typeof options.position !== 'undefined') {
      this.setPosition(options.position);
    }
    if (typeof options.zIndex !== 'undefined') {
      this.setZIndex(options.zIndex);
    }
    if (typeof options.closeBoxMargin !== 'undefined') {
      this.closeBoxMargin = options.closeBoxMargin;
    }
    if (typeof options.closeBoxURL !== 'undefined') {
      this.closeBoxURL = options.closeBoxURL;
    }
    if (typeof options.infoBoxClearance !== 'undefined') {
      this.infoBoxClearance = options.infoBoxClearance;
    }
    if (typeof options.isHidden !== 'undefined') {
      this.isHidden = options.isHidden;
    }
    if (typeof options.visible !== 'undefined') {
      this.isHidden = !options.visible;
    }
    if (typeof options.enableEventPropagation !== 'undefined') {
      this.enableEventPropagation = options.enableEventPropagation;
    }
    if (this.div) {
      this.draw();
    }
  };
  InfoBox.prototype.setContent = function (content) {
    this.content = content;
    if (this.div) {
      if (this.closeListener) {
        google.maps.event.removeListener(this.closeListener);
        this.closeListener = null;
      }
      if (!this.fixedWidthSet) {
        this.div.style.width = '';
      }
      if (typeof content === 'string') {
        this.div.innerHTML = this.getCloseBoxImg() + content;
      } else {
        this.div.innerHTML = this.getCloseBoxImg();
        this.div.appendChild(content);
      }
      if (!this.fixedWidthSet) {
        this.div.style.width = this.div.offsetWidth + 'px';
        if (typeof content === 'string') {
          this.div.innerHTML = this.getCloseBoxImg() + content;
        } else {
          this.div.innerHTML = this.getCloseBoxImg();
          this.div.appendChild(content);
        }
      }
      this.addClickHandler();
    }
    google.maps.event.trigger(this, 'content_changed');
  };
  InfoBox.prototype.setPosition = function (latLng) {
    this.position = latLng;
    if (this.div) {
      this.draw();
    }
    google.maps.event.trigger(this, 'position_changed');
  };
  InfoBox.prototype.setVisible = function (isVisible) {
    this.isHidden = !isVisible;
    if (this.div) {
      this.div.style.visibility = this.isHidden ? 'hidden' : 'visible';
    }
  };
  InfoBox.prototype.setZIndex = function (index) {
    this.zIndex = index;
    if (this.div) {
      this.div.style.zIndex = index + '';
    }
    google.maps.event.trigger(this, 'zindex_changed');
  };
  InfoBox.prototype.getContent = function () {
    return this.content;
  };
  InfoBox.prototype.getPosition = function () {
    return this.position;
  };
  InfoBox.prototype.getZIndex = function () {
    return this.zIndex;
  };
  InfoBox.prototype.getVisible = function () {
    var map = this.getMap();
    return typeof map === 'undefined' || map === null ? false : !this.isHidden;
  };
  InfoBox.prototype.show = function () {
    this.isHidden = false;
    if (this.div) {
      this.div.style.visibility = 'visible';
    }
  };
  InfoBox.prototype.hide = function () {
    this.isHidden = true;
    if (this.div) {
      this.div.style.visibility = 'hidden';
    }
  };
  InfoBox.prototype.open = function (map, anchor) {
    var _this = this;
    if (anchor) {
      this.position = anchor.getPosition();
      this.moveListener = google.maps.event.addListener(anchor, 'position_changed', function () {
        var position = anchor.getPosition();
        _this.setPosition(position);
      });
      this.mapListener = google.maps.event.addListener(anchor, 'map_changed', function () {
        _this.setMap(anchor.map);
      });
    }
    this.setMap(map);
    if (this.div) {
      this.panBox();
    }
  };
  InfoBox.prototype.close = function () {
    if (this.closeListener) {
      google.maps.event.removeListener(this.closeListener);
      this.closeListener = null;
    }
    if (this.eventListeners) {
      for (var _i = 0, _a = this.eventListeners; _i < _a.length; _i++) {
        var eventListener = _a[_i];
        google.maps.event.removeListener(eventListener);
      }
      this.eventListeners = null;
    }
    if (this.moveListener) {
      google.maps.event.removeListener(this.moveListener);
      this.moveListener = null;
    }
    if (this.mapListener) {
      google.maps.event.removeListener(this.mapListener);
      this.mapListener = null;
    }
    if (this.contextListener) {
      google.maps.event.removeListener(this.contextListener);
      this.contextListener = null;
    }
    this.setMap(null);
  };
  InfoBox.prototype.extend = function (obj1, obj2) {
    return function applyExtend(object) {
      for (var property in object.prototype) {
        if (!Object.prototype.hasOwnProperty.call(this, property)) {
          this.prototype[property] = object.prototype[property];
        }
      }
      return this;
    }.apply(obj1, [obj2]);
  };
  return InfoBox;
}();
var eventMap$d = {
  onCloseClick: 'closeclick',
  onContentChanged: 'content_changed',
  onDomReady: 'domready',
  onPositionChanged: 'position_changed',
  onZindexChanged: 'zindex_changed'
};
var updaterMap$d = {
  options: function options(instance, _options5) {
    instance.setOptions(_options5);
  },
  position: function position(instance, _position2) {
    if (_position2 instanceof google.maps.LatLng) {
      instance.setPosition(_position2);
    } else {
      instance.setPosition(new google.maps.LatLng(_position2.lat, _position2.lng));
    }
  },
  visible: function visible(instance, _visible2) {
    instance.setVisible(_visible2);
  },
  zIndex: function zIndex(instance, _zIndex2) {
    instance.setZIndex(_zIndex2);
  }
};
var defaultOptions$3 = {};
function InfoBoxFunctional(_a) {
  var children = _a.children,
    anchor = _a.anchor,
    options = _a.options,
    position = _a.position,
    zIndex = _a.zIndex,
    onCloseClick = _a.onCloseClick,
    onDomReady = _a.onDomReady,
    onContentChanged = _a.onContentChanged,
    onPositionChanged = _a.onPositionChanged,
    onZindexChanged = _a.onZindexChanged,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  var _c = React.useState(null),
    closeClickListener = _c[0],
    setCloseClickListener = _c[1];
  var _d = React.useState(null),
    domReadyClickListener = _d[0],
    setDomReadyClickListener = _d[1];
  var _e = React.useState(null),
    contentChangedClickListener = _e[0],
    setContentChangedClickListener = _e[1];
  var _f = React.useState(null),
    positionChangedClickListener = _f[0],
    setPositionChangedClickListener = _f[1];
  var _g = React.useState(null),
    zIndexChangedClickListener = _g[0],
    setZindexChangedClickListener = _g[1];
  var containerElementRef = React.useRef(null);
  React.useEffect(function () {
    if (map && instance !== null) {
      instance.close();
      if (anchor) {
        instance.open(map, anchor);
      } else if (instance.getPosition()) {
        instance.open(map);
      }
    }
  }, [map, instance, anchor]);
  React.useEffect(function () {
    if (options && instance !== null) {
      instance.setOptions(options);
    }
  }, [instance, options]);
  React.useEffect(function () {
    if (position && instance !== null) {
      var positionLatLng = position instanceof google.maps.LatLng ? position : new google.maps.LatLng(position.lat, position.lng);
      instance.setPosition(positionLatLng);
    }
  }, [position]);
  React.useEffect(function () {
    if (typeof zIndex === 'number' && instance !== null) {
      instance.setZIndex(zIndex);
    }
  }, [zIndex]);
  React.useEffect(function () {
    if (instance && onCloseClick) {
      if (closeClickListener !== null) {
        google.maps.event.removeListener(closeClickListener);
      }
      setCloseClickListener(google.maps.event.addListener(instance, 'closeclick', onCloseClick));
    }
  }, [onCloseClick]);
  React.useEffect(function () {
    if (instance && onDomReady) {
      if (domReadyClickListener !== null) {
        google.maps.event.removeListener(domReadyClickListener);
      }
      setDomReadyClickListener(google.maps.event.addListener(instance, 'domready', onDomReady));
    }
  }, [onDomReady]);
  React.useEffect(function () {
    if (instance && onContentChanged) {
      if (contentChangedClickListener !== null) {
        google.maps.event.removeListener(contentChangedClickListener);
      }
      setContentChangedClickListener(google.maps.event.addListener(instance, 'content_changed', onContentChanged));
    }
  }, [onContentChanged]);
  React.useEffect(function () {
    if (instance && onPositionChanged) {
      if (positionChangedClickListener !== null) {
        google.maps.event.removeListener(positionChangedClickListener);
      }
      setPositionChangedClickListener(google.maps.event.addListener(instance, 'position_changed', onPositionChanged));
    }
  }, [onPositionChanged]);
  React.useEffect(function () {
    if (instance && onZindexChanged) {
      if (zIndexChangedClickListener !== null) {
        google.maps.event.removeListener(zIndexChangedClickListener);
      }
      setZindexChangedClickListener(google.maps.event.addListener(instance, 'zindex_changed', onZindexChanged));
    }
  }, [onZindexChanged]);
  React.useEffect(function () {
    if (map) {
      var _a = options || defaultOptions$3,
        position_1 = _a.position,
        infoBoxOptions = __rest$1(_a, ["position"]);
      var positionLatLng = void 0;
      if (position_1 && !(position_1 instanceof google.maps.LatLng)) {
        positionLatLng = new google.maps.LatLng(position_1.lat, position_1.lng);
      }
      var infoBox = new InfoBox(_assign(_assign({}, infoBoxOptions), positionLatLng ? {
        position: positionLatLng
      } : {}));
      containerElementRef.current = document.createElement('div');
      setInstance(infoBox);
      if (onCloseClick) {
        setCloseClickListener(google.maps.event.addListener(infoBox, 'closeclick', onCloseClick));
      }
      if (onDomReady) {
        setDomReadyClickListener(google.maps.event.addListener(infoBox, 'domready', onDomReady));
      }
      if (onContentChanged) {
        setContentChangedClickListener(google.maps.event.addListener(infoBox, 'content_changed', onContentChanged));
      }
      if (onPositionChanged) {
        setPositionChangedClickListener(google.maps.event.addListener(infoBox, 'position_changed', onPositionChanged));
      }
      if (onZindexChanged) {
        setZindexChangedClickListener(google.maps.event.addListener(infoBox, 'zindex_changed', onZindexChanged));
      }
      infoBox.setContent(containerElementRef.current);
      if (anchor) {
        infoBox.open(map, anchor);
      } else if (infoBox.getPosition()) {
        infoBox.open(map);
      } else {
        invariant$1(false, 'You must provide either an anchor or a position prop for <InfoBox>.');
      }
      if (onLoad) {
        onLoad(infoBox);
      }
    }
    return function () {
      if (instance !== null) {
        if (closeClickListener) {
          google.maps.event.removeListener(closeClickListener);
        }
        if (contentChangedClickListener) {
          google.maps.event.removeListener(contentChangedClickListener);
        }
        if (domReadyClickListener) {
          google.maps.event.removeListener(domReadyClickListener);
        }
        if (positionChangedClickListener) {
          google.maps.event.removeListener(positionChangedClickListener);
        }
        if (zIndexChangedClickListener) {
          google.maps.event.removeListener(zIndexChangedClickListener);
        }
        if (onUnmount) {
          onUnmount(instance);
        }
        instance.close();
      }
    };
  }, []);
  return containerElementRef.current ? ReactDOM.createPortal(React.Children.only(children), containerElementRef.current) : null;
}
var InfoBoxF = React.memo(InfoBoxFunctional);
var InfoBoxComponent = function (_super) {
  __extends(InfoBoxComponent, _super);
  function InfoBoxComponent() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.containerElement = null;
    _this.state = {
      infoBox: null
    };
    _this.open = function (infoBox, anchor) {
      if (anchor) {
        if (_this.context !== null) {
          infoBox.open(_this.context, anchor);
        }
      } else if (infoBox.getPosition()) {
        if (_this.context !== null) {
          infoBox.open(_this.context);
        }
      } else {
        invariant$1(false, 'You must provide either an anchor or a position prop for <InfoBox>.');
      }
    };
    _this.setInfoBoxCallback = function () {
      if (_this.state.infoBox !== null && _this.containerElement !== null) {
        _this.state.infoBox.setContent(_this.containerElement);
        _this.open(_this.state.infoBox, _this.props.anchor);
        if (_this.props.onLoad) {
          _this.props.onLoad(_this.state.infoBox);
        }
      }
    };
    return _this;
  }
  InfoBoxComponent.prototype.componentDidMount = function () {
    var _a = this.props.options || {},
      position = _a.position,
      infoBoxOptions = __rest$1(_a, ["position"]);
    var positionLatLng;
    if (position && !(position instanceof google.maps.LatLng)) {
      positionLatLng = new google.maps.LatLng(position.lat, position.lng);
    }
    var infoBox = new InfoBox(_assign(_assign({}, infoBoxOptions), positionLatLng ? {
      position: positionLatLng
    } : {}));
    this.containerElement = document.createElement('div');
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$d,
      eventMap: eventMap$d,
      prevProps: {},
      nextProps: this.props,
      instance: infoBox
    });
    this.setState({
      infoBox: infoBox
    }, this.setInfoBoxCallback);
  };
  InfoBoxComponent.prototype.componentDidUpdate = function (prevProps) {
    var infoBox = this.state.infoBox;
    if (infoBox !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$d,
        eventMap: eventMap$d,
        prevProps: prevProps,
        nextProps: this.props,
        instance: infoBox
      });
    }
  };
  InfoBoxComponent.prototype.componentWillUnmount = function () {
    var onUnmount = this.props.onUnmount;
    var infoBox = this.state.infoBox;
    if (infoBox !== null) {
      if (onUnmount) {
        onUnmount(infoBox);
      }
      unregisterEvents(this.registeredEvents);
      infoBox.close();
    }
  };
  InfoBoxComponent.prototype.render = function () {
    return this.containerElement ? ReactDOM.createPortal(React.Children.only(this.props.children), this.containerElement) : null;
  };
  InfoBoxComponent.contextType = MapContext;
  return InfoBoxComponent;
}(React.PureComponent);
var fastDeepEqual = function equal(a, b) {
  if (a === b) return true;
  if (a && b && typeof a == 'object' && typeof b == 'object') {
    if (a.constructor !== b.constructor) return false;
    var length, i, keys;
    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;) if (!equal(a[i], b[i])) return false;
      return true;
    }
    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
    if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();
    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;
    for (i = length; i-- !== 0;) if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;
    for (i = length; i-- !== 0;) {
      var key = keys[i];
      if (!equal(a[key], b[key])) return false;
    }
    return true;
  }
  return a !== a && b !== b;
};
var equal = /*@__PURE__*/getDefaultExportFromCjs(fastDeepEqual);
var ARRAY_TYPES = [Int8Array, Uint8Array, Uint8ClampedArray, Int16Array, Uint16Array, Int32Array, Uint32Array, Float32Array, Float64Array];
var VERSION = 1;
var HEADER_SIZE = 8;
var KDBush = /*#__PURE__*/function () {
  KDBush.from = function from(data) {
    if (!(data instanceof ArrayBuffer)) {
      throw new Error('Data must be an instance of ArrayBuffer.');
    }
    var _Uint8Array = new Uint8Array(data, 0, 2),
      magic = _Uint8Array[0],
      versionAndType = _Uint8Array[1];
    if (magic !== 0xdb) {
      throw new Error('Data does not appear to be in a KDBush format.');
    }
    var version = versionAndType >> 4;
    if (version !== VERSION) {
      throw new Error("Got v" + version + " data when expected v" + VERSION + ".");
    }
    var ArrayType = ARRAY_TYPES[versionAndType & 0x0f];
    if (!ArrayType) {
      throw new Error('Unrecognized array type.');
    }
    var _Uint16Array = new Uint16Array(data, 2, 1),
      nodeSize = _Uint16Array[0];
    var _Uint32Array = new Uint32Array(data, 4, 1),
      numItems = _Uint32Array[0];
    return new KDBush(numItems, nodeSize, ArrayType, data);
  };
  function KDBush(numItems, nodeSize, ArrayType, data) {
    if (nodeSize === void 0) {
      nodeSize = 64;
    }
    if (ArrayType === void 0) {
      ArrayType = Float64Array;
    }
    if (isNaN(numItems) || numItems < 0) throw new Error("Unpexpected numItems value: " + numItems + ".");
    this.numItems = +numItems;
    this.nodeSize = Math.min(Math.max(+nodeSize, 2), 65535);
    this.ArrayType = ArrayType;
    this.IndexArrayType = numItems < 65536 ? Uint16Array : Uint32Array;
    var arrayTypeIndex = ARRAY_TYPES.indexOf(this.ArrayType);
    var coordsByteSize = numItems * 2 * this.ArrayType.BYTES_PER_ELEMENT;
    var idsByteSize = numItems * this.IndexArrayType.BYTES_PER_ELEMENT;
    var padCoords = (8 - idsByteSize % 8) % 8;
    if (arrayTypeIndex < 0) {
      throw new Error("Unexpected typed array class: " + ArrayType + ".");
    }
    if (data && data instanceof ArrayBuffer) {
      this.data = data;
      this.ids = new this.IndexArrayType(this.data, HEADER_SIZE, numItems);
      this.coords = new this.ArrayType(this.data, HEADER_SIZE + idsByteSize + padCoords, numItems * 2);
      this._pos = numItems * 2;
      this._finished = true;
    } else {
      this.data = new ArrayBuffer(HEADER_SIZE + coordsByteSize + idsByteSize + padCoords);
      this.ids = new this.IndexArrayType(this.data, HEADER_SIZE, numItems);
      this.coords = new this.ArrayType(this.data, HEADER_SIZE + idsByteSize + padCoords, numItems * 2);
      this._pos = 0;
      this._finished = false;
      new Uint8Array(this.data, 0, 2).set([0xdb, (VERSION << 4) + arrayTypeIndex]);
      new Uint16Array(this.data, 2, 1)[0] = nodeSize;
      new Uint32Array(this.data, 4, 1)[0] = numItems;
    }
  }
  var _proto2 = KDBush.prototype;
  _proto2.add = function add(x, y) {
    var index = this._pos >> 1;
    this.ids[index] = index;
    this.coords[this._pos++] = x;
    this.coords[this._pos++] = y;
    return index;
  };
  _proto2.finish = function finish() {
    var numAdded = this._pos >> 1;
    if (numAdded !== this.numItems) {
      throw new Error("Added " + numAdded + " items when expected " + this.numItems + ".");
    }
    sort(this.ids, this.coords, this.nodeSize, 0, this.numItems - 1, 0);
    this._finished = true;
    return this;
  };
  _proto2.range = function range(minX, minY, maxX, maxY) {
    if (!this._finished) throw new Error('Data not yet indexed - call index.finish().');
    var ids = this.ids,
      coords = this.coords,
      nodeSize = this.nodeSize;
    var stack = [0, ids.length - 1, 0];
    var result = [];
    while (stack.length) {
      var axis = stack.pop() || 0;
      var right = stack.pop() || 0;
      var left = stack.pop() || 0;
      if (right - left <= nodeSize) {
        for (var i = left; i <= right; i++) {
          var _x2 = coords[2 * i];
          var _y2 = coords[2 * i + 1];
          if (_x2 >= minX && _x2 <= maxX && _y2 >= minY && _y2 <= maxY) result.push(ids[i]);
        }
        continue;
      }
      var m = left + right >> 1;
      var x = coords[2 * m];
      var y = coords[2 * m + 1];
      if (x >= minX && x <= maxX && y >= minY && y <= maxY) result.push(ids[m]);
      if (axis === 0 ? minX <= x : minY <= y) {
        stack.push(left);
        stack.push(m - 1);
        stack.push(1 - axis);
      }
      if (axis === 0 ? maxX >= x : maxY >= y) {
        stack.push(m + 1);
        stack.push(right);
        stack.push(1 - axis);
      }
    }
    return result;
  };
  _proto2.within = function within(qx, qy, r) {
    if (!this._finished) throw new Error('Data not yet indexed - call index.finish().');
    var ids = this.ids,
      coords = this.coords,
      nodeSize = this.nodeSize;
    var stack = [0, ids.length - 1, 0];
    var result = [];
    var r2 = r * r;
    while (stack.length) {
      var axis = stack.pop() || 0;
      var right = stack.pop() || 0;
      var left = stack.pop() || 0;
      if (right - left <= nodeSize) {
        for (var i = left; i <= right; i++) {
          if (sqDist(coords[2 * i], coords[2 * i + 1], qx, qy) <= r2) result.push(ids[i]);
        }
        continue;
      }
      var m = left + right >> 1;
      var x = coords[2 * m];
      var y = coords[2 * m + 1];
      if (sqDist(x, y, qx, qy) <= r2) result.push(ids[m]);
      if (axis === 0 ? qx - r <= x : qy - r <= y) {
        stack.push(left);
        stack.push(m - 1);
        stack.push(1 - axis);
      }
      if (axis === 0 ? qx + r >= x : qy + r >= y) {
        stack.push(m + 1);
        stack.push(right);
        stack.push(1 - axis);
      }
    }
    return result;
  };
  return KDBush;
}();
function sort(ids, coords, nodeSize, left, right, axis) {
  if (right - left <= nodeSize) return;
  var m = left + right >> 1;
  select(ids, coords, m, left, right, axis);
  sort(ids, coords, nodeSize, left, m - 1, 1 - axis);
  sort(ids, coords, nodeSize, m + 1, right, 1 - axis);
}
function select(ids, coords, k, left, right, axis) {
  while (right > left) {
    if (right - left > 600) {
      var n = right - left + 1;
      var m = k - left + 1;
      var z = Math.log(n);
      var s = 0.5 * Math.exp(2 * z / 3);
      var sd = 0.5 * Math.sqrt(z * s * (n - s) / n) * (m - n / 2 < 0 ? -1 : 1);
      var newLeft = Math.max(left, Math.floor(k - m * s / n + sd));
      var newRight = Math.min(right, Math.floor(k + (n - m) * s / n + sd));
      select(ids, coords, k, newLeft, newRight, axis);
    }
    var t = coords[2 * k + axis];
    var i = left;
    var j = right;
    swapItem(ids, coords, left, k);
    if (coords[2 * right + axis] > t) swapItem(ids, coords, left, right);
    while (i < j) {
      swapItem(ids, coords, i, j);
      i++;
      j--;
      while (coords[2 * i + axis] < t) i++;
      while (coords[2 * j + axis] > t) j--;
    }
    if (coords[2 * left + axis] === t) swapItem(ids, coords, left, j);else {
      j++;
      swapItem(ids, coords, j, right);
    }
    if (j <= k) left = j + 1;
    if (k <= j) right = j - 1;
  }
}
function swapItem(ids, coords, i, j) {
  swap(ids, i, j);
  swap(coords, 2 * i, 2 * j);
  swap(coords, 2 * i + 1, 2 * j + 1);
}
function swap(arr, i, j) {
  var tmp = arr[i];
  arr[i] = arr[j];
  arr[j] = tmp;
}
function sqDist(ax, ay, bx, by) {
  var dx = ax - bx;
  var dy = ay - by;
  return dx * dx + dy * dy;
}
var defaultOptions$2 = {
  minZoom: 0,
  maxZoom: 16,
  minPoints: 2,
  radius: 40,
  extent: 512,
  nodeSize: 64,
  log: false,
  generateId: false,
  reduce: null,
  map: function map(props) {
    return props;
  }
};
var fround = Math.fround || function (tmp) {
  return function (x) {
    tmp[0] = +x;
    return tmp[0];
  };
}(new Float32Array(1));
var OFFSET_ZOOM = 2;
var OFFSET_ID = 3;
var OFFSET_PARENT = 4;
var OFFSET_NUM = 5;
var OFFSET_PROP = 6;
var Supercluster = /*#__PURE__*/function () {
  function Supercluster(options) {
    this.options = Object.assign(Object.create(defaultOptions$2), options);
    this.trees = new Array(this.options.maxZoom + 1);
    this.stride = this.options.reduce ? 7 : 6;
    this.clusterProps = [];
  }
  var _proto3 = Supercluster.prototype;
  _proto3.load = function load(points) {
    var _this$options = this.options,
      log = _this$options.log,
      minZoom = _this$options.minZoom,
      maxZoom = _this$options.maxZoom;
    if (log) console.time('total time');
    var timerId = "prepare " + points.length + " points";
    if (log) console.time(timerId);
    this.points = points;
    var data = [];
    for (var i = 0; i < points.length; i++) {
      var p = points[i];
      if (!p.geometry) continue;
      var _p$geometry$coordinat = p.geometry.coordinates,
        lng = _p$geometry$coordinat[0],
        lat = _p$geometry$coordinat[1];
      var x = fround(lngX(lng));
      var y = fround(latY(lat));
      data.push(x, y, Infinity, i, -1, 1);
      if (this.options.reduce) data.push(0);
    }
    var tree = this.trees[maxZoom + 1] = this._createTree(data);
    if (log) console.timeEnd(timerId);
    for (var z = maxZoom; z >= minZoom; z--) {
      var now = +Date.now();
      tree = this.trees[z] = this._createTree(this._cluster(tree, z));
      if (log) console.log('z%d: %d clusters in %dms', z, tree.numItems, +Date.now() - now);
    }
    if (log) console.timeEnd('total time');
    return this;
  };
  _proto3.getClusters = function getClusters(bbox, zoom) {
    var minLng = ((bbox[0] + 180) % 360 + 360) % 360 - 180;
    var minLat = Math.max(-90, Math.min(90, bbox[1]));
    var maxLng = bbox[2] === 180 ? 180 : ((bbox[2] + 180) % 360 + 360) % 360 - 180;
    var maxLat = Math.max(-90, Math.min(90, bbox[3]));
    if (bbox[2] - bbox[0] >= 360) {
      minLng = -180;
      maxLng = 180;
    } else if (minLng > maxLng) {
      var easternHem = this.getClusters([minLng, minLat, 180, maxLat], zoom);
      var westernHem = this.getClusters([-180, minLat, maxLng, maxLat], zoom);
      return easternHem.concat(westernHem);
    }
    var tree = this.trees[this._limitZoom(zoom)];
    var ids = tree.range(lngX(minLng), latY(maxLat), lngX(maxLng), latY(minLat));
    var data = tree.data;
    var clusters = [];
    for (var _iterator = _createForOfIteratorHelperLoose(ids), _step; !(_step = _iterator()).done;) {
      var id = _step.value;
      var k = this.stride * id;
      clusters.push(data[k + OFFSET_NUM] > 1 ? getClusterJSON(data, k, this.clusterProps) : this.points[data[k + OFFSET_ID]]);
    }
    return clusters;
  };
  _proto3.getChildren = function getChildren(clusterId) {
    var originId = this._getOriginId(clusterId);
    var originZoom = this._getOriginZoom(clusterId);
    var errorMsg = 'No cluster with the specified id.';
    var tree = this.trees[originZoom];
    if (!tree) throw new Error(errorMsg);
    var data = tree.data;
    if (originId * this.stride >= data.length) throw new Error(errorMsg);
    var r = this.options.radius / (this.options.extent * Math.pow(2, originZoom - 1));
    var x = data[originId * this.stride];
    var y = data[originId * this.stride + 1];
    var ids = tree.within(x, y, r);
    var children = [];
    for (var _iterator2 = _createForOfIteratorHelperLoose(ids), _step2; !(_step2 = _iterator2()).done;) {
      var id = _step2.value;
      var k = id * this.stride;
      if (data[k + OFFSET_PARENT] === clusterId) {
        children.push(data[k + OFFSET_NUM] > 1 ? getClusterJSON(data, k, this.clusterProps) : this.points[data[k + OFFSET_ID]]);
      }
    }
    if (children.length === 0) throw new Error(errorMsg);
    return children;
  };
  _proto3.getLeaves = function getLeaves(clusterId, limit, offset) {
    limit = limit || 10;
    offset = offset || 0;
    var leaves = [];
    this._appendLeaves(leaves, clusterId, limit, offset, 0);
    return leaves;
  };
  _proto3.getTile = function getTile(z, x, y) {
    var tree = this.trees[this._limitZoom(z)];
    var z2 = Math.pow(2, z);
    var _this$options2 = this.options,
      extent = _this$options2.extent,
      radius = _this$options2.radius;
    var p = radius / extent;
    var top = (y - p) / z2;
    var bottom = (y + 1 + p) / z2;
    var tile = {
      features: []
    };
    this._addTileFeatures(tree.range((x - p) / z2, top, (x + 1 + p) / z2, bottom), tree.data, x, y, z2, tile);
    if (x === 0) {
      this._addTileFeatures(tree.range(1 - p / z2, top, 1, bottom), tree.data, z2, y, z2, tile);
    }
    if (x === z2 - 1) {
      this._addTileFeatures(tree.range(0, top, p / z2, bottom), tree.data, -1, y, z2, tile);
    }
    return tile.features.length ? tile : null;
  };
  _proto3.getClusterExpansionZoom = function getClusterExpansionZoom(clusterId) {
    var expansionZoom = this._getOriginZoom(clusterId) - 1;
    while (expansionZoom <= this.options.maxZoom) {
      var children = this.getChildren(clusterId);
      expansionZoom++;
      if (children.length !== 1) break;
      clusterId = children[0].properties.cluster_id;
    }
    return expansionZoom;
  };
  _proto3._appendLeaves = function _appendLeaves(result, clusterId, limit, offset, skipped) {
    var children = this.getChildren(clusterId);
    for (var _iterator3 = _createForOfIteratorHelperLoose(children), _step3; !(_step3 = _iterator3()).done;) {
      var child = _step3.value;
      var props = child.properties;
      if (props && props.cluster) {
        if (skipped + props.point_count <= offset) {
          skipped += props.point_count;
        } else {
          skipped = this._appendLeaves(result, props.cluster_id, limit, offset, skipped);
        }
      } else if (skipped < offset) {
        skipped++;
      } else {
        result.push(child);
      }
      if (result.length === limit) break;
    }
    return skipped;
  };
  _proto3._createTree = function _createTree(data) {
    var tree = new KDBush(data.length / this.stride | 0, this.options.nodeSize, Float32Array);
    for (var i = 0; i < data.length; i += this.stride) tree.add(data[i], data[i + 1]);
    tree.finish();
    tree.data = data;
    return tree;
  };
  _proto3._addTileFeatures = function _addTileFeatures(ids, data, x, y, z2, tile) {
    for (var _iterator4 = _createForOfIteratorHelperLoose(ids), _step4; !(_step4 = _iterator4()).done;) {
      var i = _step4.value;
      var k = i * this.stride;
      var isCluster = data[k + OFFSET_NUM] > 1;
      var tags = void 0,
        px = void 0,
        py = void 0;
      if (isCluster) {
        tags = getClusterProperties(data, k, this.clusterProps);
        px = data[k];
        py = data[k + 1];
      } else {
        var p = this.points[data[k + OFFSET_ID]];
        tags = p.properties;
        var _p$geometry$coordinat2 = p.geometry.coordinates,
          lng = _p$geometry$coordinat2[0],
          lat = _p$geometry$coordinat2[1];
        px = lngX(lng);
        py = latY(lat);
      }
      var f = {
        type: 1,
        geometry: [[Math.round(this.options.extent * (px * z2 - x)), Math.round(this.options.extent * (py * z2 - y))]],
        tags: tags
      };
      var id = void 0;
      if (isCluster || this.options.generateId) {
        id = data[k + OFFSET_ID];
      } else {
        id = this.points[data[k + OFFSET_ID]].id;
      }
      if (id !== undefined) f.id = id;
      tile.features.push(f);
    }
  };
  _proto3._limitZoom = function _limitZoom(z) {
    return Math.max(this.options.minZoom, Math.min(Math.floor(+z), this.options.maxZoom + 1));
  };
  _proto3._cluster = function _cluster(tree, zoom) {
    var _this$options3 = this.options,
      radius = _this$options3.radius,
      extent = _this$options3.extent,
      reduce = _this$options3.reduce,
      minPoints = _this$options3.minPoints;
    var r = radius / (extent * Math.pow(2, zoom));
    var data = tree.data;
    var nextData = [];
    var stride = this.stride;
    for (var i = 0; i < data.length; i += stride) {
      if (data[i + OFFSET_ZOOM] <= zoom) continue;
      data[i + OFFSET_ZOOM] = zoom;
      var x = data[i];
      var y = data[i + 1];
      var neighborIds = tree.within(data[i], data[i + 1], r);
      var numPointsOrigin = data[i + OFFSET_NUM];
      var numPoints = numPointsOrigin;
      for (var _iterator5 = _createForOfIteratorHelperLoose(neighborIds), _step5; !(_step5 = _iterator5()).done;) {
        var _neighborId2 = _step5.value;
        var _k3 = _neighborId2 * stride;
        if (data[_k3 + OFFSET_ZOOM] > zoom) numPoints += data[_k3 + OFFSET_NUM];
      }
      if (numPoints > numPointsOrigin && numPoints >= minPoints) {
        var wx = x * numPointsOrigin;
        var wy = y * numPointsOrigin;
        var clusterProperties = void 0;
        var clusterPropIndex = -1;
        var id = ((i / stride | 0) << 5) + (zoom + 1) + this.points.length;
        for (var _iterator6 = _createForOfIteratorHelperLoose(neighborIds), _step6; !(_step6 = _iterator6()).done;) {
          var neighborId = _step6.value;
          var k = neighborId * stride;
          if (data[k + OFFSET_ZOOM] <= zoom) continue;
          data[k + OFFSET_ZOOM] = zoom;
          var numPoints2 = data[k + OFFSET_NUM];
          wx += data[k] * numPoints2;
          wy += data[k + 1] * numPoints2;
          data[k + OFFSET_PARENT] = id;
          if (reduce) {
            if (!clusterProperties) {
              clusterProperties = this._map(data, i, true);
              clusterPropIndex = this.clusterProps.length;
              this.clusterProps.push(clusterProperties);
            }
            reduce(clusterProperties, this._map(data, k));
          }
        }
        data[i + OFFSET_PARENT] = id;
        nextData.push(wx / numPoints, wy / numPoints, Infinity, id, -1, numPoints);
        if (reduce) nextData.push(clusterPropIndex);
      } else {
        for (var j = 0; j < stride; j++) nextData.push(data[i + j]);
        if (numPoints > 1) {
          for (var _iterator7 = _createForOfIteratorHelperLoose(neighborIds), _step7; !(_step7 = _iterator7()).done;) {
            var _neighborId = _step7.value;
            var _k2 = _neighborId * stride;
            if (data[_k2 + OFFSET_ZOOM] <= zoom) continue;
            data[_k2 + OFFSET_ZOOM] = zoom;
            for (var _j2 = 0; _j2 < stride; _j2++) nextData.push(data[_k2 + _j2]);
          }
        }
      }
    }
    return nextData;
  };
  _proto3._getOriginId = function _getOriginId(clusterId) {
    return clusterId - this.points.length >> 5;
  };
  _proto3._getOriginZoom = function _getOriginZoom(clusterId) {
    return (clusterId - this.points.length) % 32;
  };
  _proto3._map = function _map(data, i, clone) {
    if (data[i + OFFSET_NUM] > 1) {
      var props = this.clusterProps[data[i + OFFSET_PROP]];
      return clone ? Object.assign({}, props) : props;
    }
    var original = this.points[data[i + OFFSET_ID]].properties;
    var result = this.options.map(original);
    return clone && result === original ? Object.assign({}, result) : result;
  };
  return Supercluster;
}();
function getClusterJSON(data, i, clusterProps) {
  return {
    type: 'Feature',
    id: data[i + OFFSET_ID],
    properties: getClusterProperties(data, i, clusterProps),
    geometry: {
      type: 'Point',
      coordinates: [xLng(data[i]), yLat(data[i + 1])]
    }
  };
}
function getClusterProperties(data, i, clusterProps) {
  var count = data[i + OFFSET_NUM];
  var abbrev = count >= 10000 ? Math.round(count / 1000) + "k" : count >= 1000 ? Math.round(count / 100) / 10 + "k" : count;
  var propIndex = data[i + OFFSET_PROP];
  var properties = propIndex === -1 ? {} : Object.assign({}, clusterProps[propIndex]);
  return Object.assign(properties, {
    cluster: true,
    cluster_id: data[i + OFFSET_ID],
    point_count: count,
    point_count_abbreviated: abbrev
  });
}
function lngX(lng) {
  return lng / 360 + 0.5;
}
function latY(lat) {
  var sin = Math.sin(lat * Math.PI / 180);
  var y = 0.5 - 0.25 * Math.log((1 + sin) / (1 - sin)) / Math.PI;
  return y < 0 ? 0 : y > 1 ? 1 : y;
}
function xLng(x) {
  return (x - 0.5) * 360;
}
function yLat(y) {
  var y2 = (180 - y * 360) * Math.PI / 180;
  return 360 * Math.atan(Math.exp(y2)) / Math.PI - 90;
}
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var MarkerUtils = /*#__PURE__*/function () {
  function MarkerUtils() {}
  MarkerUtils.isAdvancedMarkerAvailable = function isAdvancedMarkerAvailable(map) {
    return google.maps.marker && map.getMapCapabilities().isAdvancedMarkersAvailable === true;
  };
  MarkerUtils.isAdvancedMarker = function isAdvancedMarker(marker) {
    return google.maps.marker && marker instanceof google.maps.marker.AdvancedMarkerElement;
  };
  MarkerUtils.setMap = function setMap(marker, map) {
    if (this.isAdvancedMarker(marker)) {
      marker.map = map;
    } else {
      marker.setMap(map);
    }
  };
  MarkerUtils.getPosition = function getPosition(marker) {
    if (this.isAdvancedMarker(marker)) {
      if (marker.position) {
        if (marker.position instanceof google.maps.LatLng) {
          return marker.position;
        }
        if (marker.position.lat && marker.position.lng) {
          return new google.maps.LatLng(marker.position.lat, marker.position.lng);
        }
      }
      return new google.maps.LatLng(null);
    }
    return marker.getPosition();
  };
  MarkerUtils.getVisible = function getVisible(marker) {
    if (this.isAdvancedMarker(marker)) {
      return true;
    }
    return marker.getVisible();
  };
  return MarkerUtils;
}();
var Cluster = /*#__PURE__*/function () {
  function Cluster(_ref2) {
    var markers = _ref2.markers,
      position = _ref2.position;
    this.markers = markers;
    if (position) {
      if (position instanceof google.maps.LatLng) {
        this._position = position;
      } else {
        this._position = new google.maps.LatLng(position);
      }
    }
  }
  var _proto4 = Cluster.prototype;
  _proto4.push = function push(marker) {
    this.markers.push(marker);
  };
  _proto4["delete"] = function _delete() {
    if (this.marker) {
      MarkerUtils.setMap(this.marker, null);
      this.marker = undefined;
    }
    this.markers.length = 0;
  };
  _createClass(Cluster, [{
    key: "bounds",
    get: function get() {
      if (this.markers.length === 0 && !this._position) {
        return;
      }
      var bounds = new google.maps.LatLngBounds(this._position, this._position);
      for (var _iterator8 = _createForOfIteratorHelperLoose(this.markers), _step8; !(_step8 = _iterator8()).done;) {
        var marker = _step8.value;
        bounds.extend(MarkerUtils.getPosition(marker));
      }
      return bounds;
    }
  }, {
    key: "position",
    get: function get() {
      return this._position || this.bounds.getCenter();
    }
  }, {
    key: "count",
    get: function get() {
      return this.markers.filter(function (m) {
        return MarkerUtils.getVisible(m);
      }).length;
    }
  }]);
  return Cluster;
}();
var AbstractAlgorithm = /*#__PURE__*/function () {
  function AbstractAlgorithm(_ref5) {
    var _ref5$maxZoom = _ref5.maxZoom,
      maxZoom = _ref5$maxZoom === void 0 ? 16 : _ref5$maxZoom;
    this.maxZoom = maxZoom;
  }
  var _proto5 = AbstractAlgorithm.prototype;
  _proto5.noop = function noop(_ref6) {
    var markers = _ref6.markers;
    return noop$1(markers);
  };
  return AbstractAlgorithm;
}();
var noop$1 = function noop$1(markers) {
  var clusters = markers.map(function (marker) {
    return new Cluster({
      position: MarkerUtils.getPosition(marker),
      markers: [marker]
    });
  });
  return clusters;
};
var SuperClusterAlgorithm = /*#__PURE__*/function (_AbstractAlgorithm3) {
  _inheritsLoose(SuperClusterAlgorithm, _AbstractAlgorithm3);
  function SuperClusterAlgorithm(_a) {
    var _this9;
    var maxZoom = _a.maxZoom,
      _a$radius = _a.radius,
      radius = _a$radius === void 0 ? 60 : _a$radius,
      options = __rest(_a, ["maxZoom", "radius"]);
    _this9 = _AbstractAlgorithm3.call(this, {
      maxZoom: maxZoom
    }) || this;
    _this9.state = {
      zoom: -1
    };
    _this9.superCluster = new Supercluster(Object.assign({
      maxZoom: _this9.maxZoom,
      radius: radius
    }, options));
    return _this9;
  }
  var _proto9 = SuperClusterAlgorithm.prototype;
  _proto9.calculate = function calculate(input) {
    var changed = false;
    var state = {
      zoom: input.map.getZoom()
    };
    if (!equal(input.markers, this.markers)) {
      changed = true;
      this.markers = [].concat(input.markers);
      var points = this.markers.map(function (marker) {
        var position = MarkerUtils.getPosition(marker);
        var coordinates = [position.lng(), position.lat()];
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: coordinates
          },
          properties: {
            marker: marker
          }
        };
      });
      this.superCluster.load(points);
    }
    if (!changed) {
      if (this.state.zoom <= this.maxZoom || state.zoom <= this.maxZoom) {
        changed = !equal(this.state, state);
      }
    }
    this.state = state;
    if (changed) {
      this.clusters = this.cluster(input);
    }
    return {
      clusters: this.clusters,
      changed: changed
    };
  };
  _proto9.cluster = function cluster(_ref11) {
    var _this10 = this;
    var map = _ref11.map;
    return this.superCluster.getClusters([-180, -90, 180, 90], Math.round(map.getZoom())).map(function (feature) {
      return _this10.transformCluster(feature);
    });
  };
  _proto9.transformCluster = function transformCluster(_ref12) {
    var _ref12$geometry$coord = _ref12.geometry.coordinates,
      lng = _ref12$geometry$coord[0],
      lat = _ref12$geometry$coord[1],
      properties = _ref12.properties;
    if (properties.cluster) {
      return new Cluster({
        markers: this.superCluster.getLeaves(properties.cluster_id, Infinity).map(function (leaf) {
          return leaf.properties.marker;
        }),
        position: {
          lat: lat,
          lng: lng
        }
      });
    }
    var marker = properties.marker;
    return new Cluster({
      markers: [marker],
      position: MarkerUtils.getPosition(marker)
    });
  };
  return SuperClusterAlgorithm;
}(AbstractAlgorithm);
var ClusterStats = function ClusterStats(markers, clusters) {
  this.markers = {
    sum: markers.length
  };
  var clusterMarkerCounts = clusters.map(function (a) {
    return a.count;
  });
  var clusterMarkerSum = clusterMarkerCounts.reduce(function (a, b) {
    return a + b;
  }, 0);
  this.clusters = {
    count: clusters.length,
    markers: {
      mean: clusterMarkerSum / clusters.length,
      sum: clusterMarkerSum,
      min: Math.min.apply(Math, clusterMarkerCounts),
      max: Math.max.apply(Math, clusterMarkerCounts)
    }
  };
};
var DefaultRenderer = /*#__PURE__*/function () {
  function DefaultRenderer() {}
  var _proto10 = DefaultRenderer.prototype;
  _proto10.render = function render(_ref13, stats, map) {
    var count = _ref13.count,
      position = _ref13.position;
    var color = count > Math.max(10, stats.clusters.markers.mean) ? "#ff0000" : "#0000ff";
    var svg = "<svg fill=\"" + color + "\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 240 240\" width=\"50\" height=\"50\">\n<circle cx=\"120\" cy=\"120\" opacity=\".6\" r=\"70\" />\n<circle cx=\"120\" cy=\"120\" opacity=\".3\" r=\"90\" />\n<circle cx=\"120\" cy=\"120\" opacity=\".2\" r=\"110\" />\n<text x=\"50%\" y=\"50%\" style=\"fill:#fff\" text-anchor=\"middle\" font-size=\"50\" dominant-baseline=\"middle\" font-family=\"roboto,arial,sans-serif\">" + count + "</text>\n</svg>";
    var title = "Cluster of " + count + " markers",
      zIndex = Number(google.maps.Marker.MAX_ZINDEX) + count;
    if (MarkerUtils.isAdvancedMarkerAvailable(map)) {
      var div = document.createElement("div");
      div.innerHTML = svg;
      var svgEl = div.firstElementChild;
      svgEl.setAttribute("transform", "translate(0 25)");
      var _clusterOptions = {
        map: map,
        position: position,
        zIndex: zIndex,
        title: title,
        content: svgEl
      };
      return new google.maps.marker.AdvancedMarkerElement(_clusterOptions);
    }
    var clusterOptions = {
      position: position,
      zIndex: zIndex,
      title: title,
      icon: {
        url: "data:image/svg+xml;base64," + btoa(svg),
        anchor: new google.maps.Point(25, 25)
      }
    };
    return new google.maps.Marker(clusterOptions);
  };
  return DefaultRenderer;
}();
function extend(type1, type2) {
  for (var property in type2.prototype) {
    type1.prototype[property] = type2.prototype[property];
  }
}
var OverlayViewSafe = function OverlayViewSafe() {
  extend(OverlayViewSafe, google.maps.OverlayView);
};
var MarkerClustererEvents;
(function (MarkerClustererEvents) {
  MarkerClustererEvents["CLUSTERING_BEGIN"] = "clusteringbegin";
  MarkerClustererEvents["CLUSTERING_END"] = "clusteringend";
  MarkerClustererEvents["CLUSTER_CLICK"] = "click";
})(MarkerClustererEvents || (MarkerClustererEvents = {}));
var defaultOnClusterClickHandler = function defaultOnClusterClickHandler(_, cluster, map) {
  map.fitBounds(cluster.bounds);
};
var MarkerClusterer = /*#__PURE__*/function (_OverlayViewSafe) {
  _inheritsLoose(MarkerClusterer, _OverlayViewSafe);
  function MarkerClusterer(_ref14) {
    var _this11;
    var map = _ref14.map,
      _ref14$markers = _ref14.markers,
      markers = _ref14$markers === void 0 ? [] : _ref14$markers,
      _ref14$algorithmOptio = _ref14.algorithmOptions,
      algorithmOptions = _ref14$algorithmOptio === void 0 ? {} : _ref14$algorithmOptio,
      _ref14$algorithm = _ref14.algorithm,
      algorithm = _ref14$algorithm === void 0 ? new SuperClusterAlgorithm(algorithmOptions) : _ref14$algorithm,
      _ref14$renderer = _ref14.renderer,
      renderer = _ref14$renderer === void 0 ? new DefaultRenderer() : _ref14$renderer,
      _ref14$onClusterClick = _ref14.onClusterClick,
      onClusterClick = _ref14$onClusterClick === void 0 ? defaultOnClusterClickHandler : _ref14$onClusterClick;
    _this11 = _OverlayViewSafe.call(this) || this;
    _this11.markers = [].concat(markers);
    _this11.clusters = [];
    _this11.algorithm = algorithm;
    _this11.renderer = renderer;
    _this11.onClusterClick = onClusterClick;
    if (map) {
      _this11.setMap(map);
    }
    return _this11;
  }
  var _proto11 = MarkerClusterer.prototype;
  _proto11.addMarker = function addMarker(marker, noDraw) {
    if (this.markers.includes(marker)) {
      return;
    }
    this.markers.push(marker);
    if (!noDraw) {
      this.render();
    }
  };
  _proto11.addMarkers = function addMarkers(markers, noDraw) {
    var _this12 = this;
    markers.forEach(function (marker) {
      _this12.addMarker(marker, true);
    });
    if (!noDraw) {
      this.render();
    }
  };
  _proto11.removeMarker = function removeMarker(marker, noDraw) {
    var index = this.markers.indexOf(marker);
    if (index === -1) {
      return false;
    }
    MarkerUtils.setMap(marker, null);
    this.markers.splice(index, 1);
    if (!noDraw) {
      this.render();
    }
    return true;
  };
  _proto11.removeMarkers = function removeMarkers(markers, noDraw) {
    var _this13 = this;
    var removed = false;
    markers.forEach(function (marker) {
      removed = _this13.removeMarker(marker, true) || removed;
    });
    if (removed && !noDraw) {
      this.render();
    }
    return removed;
  };
  _proto11.clearMarkers = function clearMarkers(noDraw) {
    this.markers.length = 0;
    if (!noDraw) {
      this.render();
    }
  };
  _proto11.render = function render() {
    var map = this.getMap();
    if (map instanceof google.maps.Map && map.getProjection()) {
      google.maps.event.trigger(this, MarkerClustererEvents.CLUSTERING_BEGIN, this);
      var _this$algorithm$calcu = this.algorithm.calculate({
          markers: this.markers,
          map: map,
          mapCanvasProjection: this.getProjection()
        }),
        clusters = _this$algorithm$calcu.clusters,
        changed = _this$algorithm$calcu.changed;
      if (changed || changed == undefined) {
        var singleMarker = new Set();
        for (var _iterator9 = _createForOfIteratorHelperLoose(clusters), _step9; !(_step9 = _iterator9()).done;) {
          var cluster = _step9.value;
          if (cluster.markers.length == 1) {
            singleMarker.add(cluster.markers[0]);
          }
        }
        var groupMarkers = [];
        for (var _iterator10 = _createForOfIteratorHelperLoose(this.clusters), _step10; !(_step10 = _iterator10()).done;) {
          var _cluster3 = _step10.value;
          if (_cluster3.marker == null) {
            continue;
          }
          if (_cluster3.markers.length == 1) {
            if (!singleMarker.has(_cluster3.marker)) {
              MarkerUtils.setMap(_cluster3.marker, null);
            }
          } else {
            groupMarkers.push(_cluster3.marker);
          }
        }
        this.clusters = clusters;
        this.renderClusters();
        requestAnimationFrame(function () {
          return groupMarkers.forEach(function (marker) {
            return MarkerUtils.setMap(marker, null);
          });
        });
      }
      google.maps.event.trigger(this, MarkerClustererEvents.CLUSTERING_END, this);
    }
  };
  _proto11.onAdd = function onAdd() {
    this.idleListener = this.getMap().addListener("idle", this.render.bind(this));
    this.render();
  };
  _proto11.onRemove = function onRemove() {
    google.maps.event.removeListener(this.idleListener);
    this.reset();
  };
  _proto11.reset = function reset() {
    this.markers.forEach(function (marker) {
      return MarkerUtils.setMap(marker, null);
    });
    this.clusters.forEach(function (cluster) {
      return cluster["delete"]();
    });
    this.clusters = [];
  };
  _proto11.renderClusters = function renderClusters() {
    var _this14 = this;
    var stats = new ClusterStats(this.markers, this.clusters);
    var map = this.getMap();
    this.clusters.forEach(function (cluster) {
      if (cluster.markers.length === 1) {
        cluster.marker = cluster.markers[0];
      } else {
        cluster.marker = _this14.renderer.render(cluster, stats, map);
        cluster.markers.forEach(function (marker) {
          return MarkerUtils.setMap(marker, null);
        });
        if (_this14.onClusterClick) {
          cluster.marker.addListener("click", function (event) {
            google.maps.event.trigger(_this14, MarkerClustererEvents.CLUSTER_CLICK, cluster);
            _this14.onClusterClick(event, cluster, map);
          });
        }
      }
      MarkerUtils.setMap(cluster.marker, map);
    });
  };
  return MarkerClusterer;
}(OverlayViewSafe);
function useGoogleMarkerClusterer(options) {
  var map = useGoogleMap();
  var _a = React.useState(null),
    markerClusterer = _a[0],
    setMarkerClusterer = _a[1];
  React.useEffect(function () {
    if (map && markerClusterer === null) {
      var markerCluster = new MarkerClusterer(_assign(_assign({}, options), {
        map: map
      }));
      setMarkerClusterer(markerCluster);
    }
  }, [map]);
  return markerClusterer;
}
function GoogleMarkerClusterer(_a) {
  var children = _a.children,
    options = _a.options;
  var markerClusterer = useGoogleMarkerClusterer(options);
  return markerClusterer !== null ? children(markerClusterer) : null;
}
var GoogleMarkerClusterer$1 = React.memo(GoogleMarkerClusterer);
var eventMap$c = {
  onCloseClick: 'closeclick',
  onContentChanged: 'content_changed',
  onDomReady: 'domready',
  onPositionChanged: 'position_changed',
  onZindexChanged: 'zindex_changed'
};
var updaterMap$c = {
  options: function options(instance, _options6) {
    instance.setOptions(_options6);
  },
  position: function position(instance, _position3) {
    instance.setPosition(_position3);
  },
  zIndex: function zIndex(instance, _zIndex3) {
    instance.setZIndex(_zIndex3);
  }
};
function InfoWindowFunctional(_a) {
  var children = _a.children,
    anchor = _a.anchor,
    options = _a.options,
    position = _a.position,
    zIndex = _a.zIndex,
    onCloseClick = _a.onCloseClick,
    onDomReady = _a.onDomReady,
    onContentChanged = _a.onContentChanged,
    onPositionChanged = _a.onPositionChanged,
    onZindexChanged = _a.onZindexChanged,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  var _c = React.useState(null),
    closeclickListener = _c[0],
    setCloseClickListener = _c[1];
  var _d = React.useState(null),
    domreadyclickListener = _d[0],
    setDomReadyClickListener = _d[1];
  var _e = React.useState(null),
    contentchangedclickListener = _e[0],
    setContentChangedClickListener = _e[1];
  var _f = React.useState(null),
    positionchangedclickListener = _f[0],
    setPositionChangedClickListener = _f[1];
  var _g = React.useState(null),
    zindexchangedclickListener = _g[0],
    setZindexChangedClickListener = _g[1];
  var containerElementRef = React.useRef(null);
  React.useEffect(function () {
    if (instance !== null) {
      instance.close();
      if (anchor) {
        instance.open(map, anchor);
      } else if (instance.getPosition()) {
        instance.open(map);
      }
    }
  }, [map, instance, anchor]);
  React.useEffect(function () {
    if (options && instance !== null) {
      instance.setOptions(options);
    }
  }, [instance, options]);
  React.useEffect(function () {
    if (position && instance !== null) {
      instance.setPosition(position);
    }
  }, [position]);
  React.useEffect(function () {
    if (typeof zIndex === 'number' && instance !== null) {
      instance.setZIndex(zIndex);
    }
  }, [zIndex]);
  React.useEffect(function () {
    if (instance && onCloseClick) {
      if (closeclickListener !== null) {
        google.maps.event.removeListener(closeclickListener);
      }
      setCloseClickListener(google.maps.event.addListener(instance, 'closeclick', onCloseClick));
    }
  }, [onCloseClick]);
  React.useEffect(function () {
    if (instance && onDomReady) {
      if (domreadyclickListener !== null) {
        google.maps.event.removeListener(domreadyclickListener);
      }
      setDomReadyClickListener(google.maps.event.addListener(instance, 'domready', onDomReady));
    }
  }, [onDomReady]);
  React.useEffect(function () {
    if (instance && onContentChanged) {
      if (contentchangedclickListener !== null) {
        google.maps.event.removeListener(contentchangedclickListener);
      }
      setContentChangedClickListener(google.maps.event.addListener(instance, 'content_changed', onContentChanged));
    }
  }, [onContentChanged]);
  React.useEffect(function () {
    if (instance && onPositionChanged) {
      if (positionchangedclickListener !== null) {
        google.maps.event.removeListener(positionchangedclickListener);
      }
      setPositionChangedClickListener(google.maps.event.addListener(instance, 'position_changed', onPositionChanged));
    }
  }, [onPositionChanged]);
  React.useEffect(function () {
    if (instance && onZindexChanged) {
      if (zindexchangedclickListener !== null) {
        google.maps.event.removeListener(zindexchangedclickListener);
      }
      setZindexChangedClickListener(google.maps.event.addListener(instance, 'zindex_changed', onZindexChanged));
    }
  }, [onZindexChanged]);
  React.useEffect(function () {
    var infoWindow = new google.maps.InfoWindow(_assign({}, options || {}));
    setInstance(infoWindow);
    containerElementRef.current = document.createElement('div');
    if (onCloseClick) {
      setCloseClickListener(google.maps.event.addListener(infoWindow, 'closeclick', onCloseClick));
    }
    if (onDomReady) {
      setDomReadyClickListener(google.maps.event.addListener(infoWindow, 'domready', onDomReady));
    }
    if (onContentChanged) {
      setContentChangedClickListener(google.maps.event.addListener(infoWindow, 'content_changed', onContentChanged));
    }
    if (onPositionChanged) {
      setPositionChangedClickListener(google.maps.event.addListener(infoWindow, 'position_changed', onPositionChanged));
    }
    if (onZindexChanged) {
      setZindexChangedClickListener(google.maps.event.addListener(infoWindow, 'zindex_changed', onZindexChanged));
    }
    infoWindow.setContent(containerElementRef.current);
    if (position) {
      infoWindow.setPosition(position);
    }
    if (zIndex) {
      infoWindow.setZIndex(zIndex);
    }
    if (anchor) {
      infoWindow.open(map, anchor);
    } else if (infoWindow.getPosition()) {
      infoWindow.open(map);
    } else {
      invariant$1(false, "You must provide either an anchor (typically render it inside a <Marker>) or a position props for <InfoWindow>.");
    }
    if (onLoad) {
      onLoad(infoWindow);
    }
    return function () {
      if (closeclickListener) {
        google.maps.event.removeListener(closeclickListener);
      }
      if (contentchangedclickListener) {
        google.maps.event.removeListener(contentchangedclickListener);
      }
      if (domreadyclickListener) {
        google.maps.event.removeListener(domreadyclickListener);
      }
      if (positionchangedclickListener) {
        google.maps.event.removeListener(positionchangedclickListener);
      }
      if (zindexchangedclickListener) {
        google.maps.event.removeListener(zindexchangedclickListener);
      }
      if (onUnmount) {
        onUnmount(infoWindow);
      }
      infoWindow.close();
    };
  }, []);
  return containerElementRef.current ? ReactDOM.createPortal(React.Children.only(children), containerElementRef.current) : null;
}
var InfoWindowF = React.memo(InfoWindowFunctional);
var InfoWindow = function (_super) {
  __extends(InfoWindow, _super);
  function InfoWindow() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.containerElement = null;
    _this.state = {
      infoWindow: null
    };
    _this.open = function (infoWindow, anchor) {
      if (anchor) {
        infoWindow.open(_this.context, anchor);
      } else if (infoWindow.getPosition()) {
        infoWindow.open(_this.context);
      } else {
        invariant$1(false, "You must provide either an anchor (typically render it inside a <Marker>) or a position props for <InfoWindow>.");
      }
    };
    _this.setInfoWindowCallback = function () {
      if (_this.state.infoWindow !== null && _this.containerElement !== null) {
        _this.state.infoWindow.setContent(_this.containerElement);
        _this.open(_this.state.infoWindow, _this.props.anchor);
        if (_this.props.onLoad) {
          _this.props.onLoad(_this.state.infoWindow);
        }
      }
    };
    return _this;
  }
  InfoWindow.prototype.componentDidMount = function () {
    var infoWindow = new google.maps.InfoWindow(_assign({}, this.props.options || {}));
    this.containerElement = document.createElement('div');
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$c,
      eventMap: eventMap$c,
      prevProps: {},
      nextProps: this.props,
      instance: infoWindow
    });
    this.setState(function () {
      return {
        infoWindow: infoWindow
      };
    }, this.setInfoWindowCallback);
  };
  InfoWindow.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.infoWindow !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$c,
        eventMap: eventMap$c,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.infoWindow
      });
    }
  };
  InfoWindow.prototype.componentWillUnmount = function () {
    if (this.state.infoWindow !== null) {
      unregisterEvents(this.registeredEvents);
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.infoWindow);
      }
      this.state.infoWindow.close();
    }
  };
  InfoWindow.prototype.render = function () {
    return this.containerElement ? ReactDOM.createPortal(React.Children.only(this.props.children), this.containerElement) : null;
  };
  InfoWindow.contextType = MapContext;
  return InfoWindow;
}(React.PureComponent);
var eventMap$b = {
  onClick: 'click',
  onDblClick: 'dblclick',
  onDrag: 'drag',
  onDragEnd: 'dragend',
  onDragStart: 'dragstart',
  onMouseDown: 'mousedown',
  onMouseMove: 'mousemove',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover',
  onMouseUp: 'mouseup',
  onRightClick: 'rightclick'
};
var updaterMap$b = {
  draggable: function draggable(instance, _draggable2) {
    instance.setDraggable(_draggable2);
  },
  editable: function editable(instance, _editable) {
    instance.setEditable(_editable);
  },
  map: function map(instance, _map2) {
    instance.setMap(_map2);
  },
  options: function options(instance, _options7) {
    instance.setOptions(_options7);
  },
  path: function path(instance, _path) {
    instance.setPath(_path);
  },
  visible: function visible(instance, _visible3) {
    instance.setVisible(_visible3);
  }
};
var defaultOptions$1 = {};
function PolylineFunctional(_a) {
  var options = _a.options,
    draggable = _a.draggable,
    editable = _a.editable,
    visible = _a.visible,
    path = _a.path,
    onDblClick = _a.onDblClick,
    onDragEnd = _a.onDragEnd,
    onDragStart = _a.onDragStart,
    onMouseDown = _a.onMouseDown,
    onMouseMove = _a.onMouseMove,
    onMouseOut = _a.onMouseOut,
    onMouseOver = _a.onMouseOver,
    onMouseUp = _a.onMouseUp,
    onRightClick = _a.onRightClick,
    onClick = _a.onClick,
    onDrag = _a.onDrag,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  var _c = React.useState(null),
    dblclickListener = _c[0],
    setDblclickListener = _c[1];
  var _d = React.useState(null),
    dragendListener = _d[0],
    setDragendListener = _d[1];
  var _e = React.useState(null),
    dragstartListener = _e[0],
    setDragstartListener = _e[1];
  var _f = React.useState(null),
    mousedownListener = _f[0],
    setMousedownListener = _f[1];
  var _g = React.useState(null),
    mousemoveListener = _g[0],
    setMousemoveListener = _g[1];
  var _h = React.useState(null),
    mouseoutListener = _h[0],
    setMouseoutListener = _h[1];
  var _j = React.useState(null),
    mouseoverListener = _j[0],
    setMouseoverListener = _j[1];
  var _k = React.useState(null),
    mouseupListener = _k[0],
    setMouseupListener = _k[1];
  var _l = React.useState(null),
    rightclickListener = _l[0],
    setRightclickListener = _l[1];
  var _m = React.useState(null),
    clickListener = _m[0],
    setClickListener = _m[1];
  var _o = React.useState(null),
    dragListener = _o[0],
    setDragListener = _o[1];
  React.useEffect(function () {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    if (typeof options !== 'undefined' && instance !== null) {
      instance.setOptions(options);
    }
  }, [instance, options]);
  React.useEffect(function () {
    if (typeof draggable !== 'undefined' && instance !== null) {
      instance.setDraggable(draggable);
    }
  }, [instance, draggable]);
  React.useEffect(function () {
    if (typeof editable !== 'undefined' && instance !== null) {
      instance.setEditable(editable);
    }
  }, [instance, editable]);
  React.useEffect(function () {
    if (typeof visible !== 'undefined' && instance !== null) {
      instance.setVisible(visible);
    }
  }, [instance, visible]);
  React.useEffect(function () {
    if (typeof path !== 'undefined' && instance !== null) {
      instance.setPath(path);
    }
  }, [instance, path]);
  React.useEffect(function () {
    if (instance && onDblClick) {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      setDblclickListener(google.maps.event.addListener(instance, 'dblclick', onDblClick));
    }
  }, [onDblClick]);
  React.useEffect(function () {
    if (instance && onDragEnd) {
      if (dragendListener !== null) {
        google.maps.event.removeListener(dragendListener);
      }
      setDragendListener(google.maps.event.addListener(instance, 'dragend', onDragEnd));
    }
  }, [onDragEnd]);
  React.useEffect(function () {
    if (instance && onDragStart) {
      if (dragstartListener !== null) {
        google.maps.event.removeListener(dragstartListener);
      }
      setDragstartListener(google.maps.event.addListener(instance, 'dragstart', onDragStart));
    }
  }, [onDragStart]);
  React.useEffect(function () {
    if (instance && onMouseDown) {
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      setMousedownListener(google.maps.event.addListener(instance, 'mousedown', onMouseDown));
    }
  }, [onMouseDown]);
  React.useEffect(function () {
    if (instance && onMouseMove) {
      if (mousemoveListener !== null) {
        google.maps.event.removeListener(mousemoveListener);
      }
      setMousemoveListener(google.maps.event.addListener(instance, 'mousemove', onMouseMove));
    }
  }, [onMouseMove]);
  React.useEffect(function () {
    if (instance && onMouseOut) {
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      setMouseoutListener(google.maps.event.addListener(instance, 'mouseout', onMouseOut));
    }
  }, [onMouseOut]);
  React.useEffect(function () {
    if (instance && onMouseOver) {
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      setMouseoverListener(google.maps.event.addListener(instance, 'mouseover', onMouseOver));
    }
  }, [onMouseOver]);
  React.useEffect(function () {
    if (instance && onMouseUp) {
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      setMouseupListener(google.maps.event.addListener(instance, 'mouseup', onMouseUp));
    }
  }, [onMouseUp]);
  React.useEffect(function () {
    if (instance && onRightClick) {
      if (rightclickListener !== null) {
        google.maps.event.removeListener(rightclickListener);
      }
      setRightclickListener(google.maps.event.addListener(instance, 'rightclick', onRightClick));
    }
  }, [onRightClick]);
  React.useEffect(function () {
    if (instance && onClick) {
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      setClickListener(google.maps.event.addListener(instance, 'click', onClick));
    }
  }, [onClick]);
  React.useEffect(function () {
    if (instance && onDrag) {
      if (dragListener !== null) {
        google.maps.event.removeListener(dragListener);
      }
      setDragListener(google.maps.event.addListener(instance, 'drag', onDrag));
    }
  }, [onDrag]);
  React.useEffect(function () {
    var polyline = new google.maps.Polyline(_assign(_assign({}, options || defaultOptions$1), {
      map: map
    }));
    if (path) {
      polyline.setPath(path);
    }
    if (typeof visible !== 'undefined') {
      polyline.setVisible(visible);
    }
    if (typeof editable !== 'undefined') {
      polyline.setEditable(editable);
    }
    if (typeof draggable !== 'undefined') {
      polyline.setDraggable(draggable);
    }
    if (onDblClick) {
      setDblclickListener(google.maps.event.addListener(polyline, 'dblclick', onDblClick));
    }
    if (onDragEnd) {
      setDragendListener(google.maps.event.addListener(polyline, 'dragend', onDragEnd));
    }
    if (onDragStart) {
      setDragstartListener(google.maps.event.addListener(polyline, 'dragstart', onDragStart));
    }
    if (onMouseDown) {
      setMousedownListener(google.maps.event.addListener(polyline, 'mousedown', onMouseDown));
    }
    if (onMouseMove) {
      setMousemoveListener(google.maps.event.addListener(polyline, 'mousemove', onMouseMove));
    }
    if (onMouseOut) {
      setMouseoutListener(google.maps.event.addListener(polyline, 'mouseout', onMouseOut));
    }
    if (onMouseOver) {
      setMouseoverListener(google.maps.event.addListener(polyline, 'mouseover', onMouseOver));
    }
    if (onMouseUp) {
      setMouseupListener(google.maps.event.addListener(polyline, 'mouseup', onMouseUp));
    }
    if (onRightClick) {
      setRightclickListener(google.maps.event.addListener(polyline, 'rightclick', onRightClick));
    }
    if (onClick) {
      setClickListener(google.maps.event.addListener(polyline, 'click', onClick));
    }
    if (onDrag) {
      setDragListener(google.maps.event.addListener(polyline, 'drag', onDrag));
    }
    setInstance(polyline);
    if (onLoad) {
      onLoad(polyline);
    }
    return function () {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      if (dragendListener !== null) {
        google.maps.event.removeListener(dragendListener);
      }
      if (dragstartListener !== null) {
        google.maps.event.removeListener(dragstartListener);
      }
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      if (mousemoveListener !== null) {
        google.maps.event.removeListener(mousemoveListener);
      }
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      if (rightclickListener !== null) {
        google.maps.event.removeListener(rightclickListener);
      }
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      if (onUnmount) {
        onUnmount(polyline);
      }
      polyline.setMap(null);
    };
  }, []);
  return null;
}
var PolylineF = React.memo(PolylineFunctional);
var Polyline = function (_super) {
  __extends(Polyline, _super);
  function Polyline() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.state = {
      polyline: null
    };
    _this.setPolylineCallback = function () {
      if (_this.state.polyline !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.polyline);
      }
    };
    return _this;
  }
  Polyline.prototype.componentDidMount = function () {
    var polyline = new google.maps.Polyline(_assign(_assign({}, this.props.options || {}), {
      map: this.context
    }));
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$b,
      eventMap: eventMap$b,
      prevProps: {},
      nextProps: this.props,
      instance: polyline
    });
    this.setState(function setPolyline() {
      return {
        polyline: polyline
      };
    }, this.setPolylineCallback);
  };
  Polyline.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.polyline !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$b,
        eventMap: eventMap$b,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.polyline
      });
    }
  };
  Polyline.prototype.componentWillUnmount = function () {
    if (this.state.polyline !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.polyline);
      }
      unregisterEvents(this.registeredEvents);
      this.state.polyline.setMap(null);
    }
  };
  Polyline.prototype.render = function () {
    return null;
  };
  Polyline.contextType = MapContext;
  return Polyline;
}(React.PureComponent);
var eventMap$a = {
  onClick: 'click',
  onDblClick: 'dblclick',
  onDrag: 'drag',
  onDragEnd: 'dragend',
  onDragStart: 'dragstart',
  onMouseDown: 'mousedown',
  onMouseMove: 'mousemove',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover',
  onMouseUp: 'mouseup',
  onRightClick: 'rightclick'
};
var updaterMap$a = {
  draggable: function draggable(instance, _draggable3) {
    instance.setDraggable(_draggable3);
  },
  editable: function editable(instance, _editable2) {
    instance.setEditable(_editable2);
  },
  map: function map(instance, _map3) {
    instance.setMap(_map3);
  },
  options: function options(instance, _options8) {
    instance.setOptions(_options8);
  },
  path: function path(instance, _path2) {
    instance.setPath(_path2);
  },
  paths: function paths(instance, _paths) {
    instance.setPaths(_paths);
  },
  visible: function visible(instance, _visible4) {
    instance.setVisible(_visible4);
  }
};
function PolygonFunctional(_a) {
  var options = _a.options,
    draggable = _a.draggable,
    editable = _a.editable,
    visible = _a.visible,
    path = _a.path,
    paths = _a.paths,
    onDblClick = _a.onDblClick,
    onDragEnd = _a.onDragEnd,
    onDragStart = _a.onDragStart,
    onMouseDown = _a.onMouseDown,
    onMouseMove = _a.onMouseMove,
    onMouseOut = _a.onMouseOut,
    onMouseOver = _a.onMouseOver,
    onMouseUp = _a.onMouseUp,
    onRightClick = _a.onRightClick,
    onClick = _a.onClick,
    onDrag = _a.onDrag,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  var _c = React.useState(null),
    dblclickListener = _c[0],
    setDblclickListener = _c[1];
  var _d = React.useState(null),
    dragendListener = _d[0],
    setDragendListener = _d[1];
  var _e = React.useState(null),
    dragstartListener = _e[0],
    setDragstartListener = _e[1];
  var _f = React.useState(null),
    mousedownListener = _f[0],
    setMousedownListener = _f[1];
  var _g = React.useState(null),
    mousemoveListener = _g[0],
    setMousemoveListener = _g[1];
  var _h = React.useState(null),
    mouseoutListener = _h[0],
    setMouseoutListener = _h[1];
  var _j = React.useState(null),
    mouseoverListener = _j[0],
    setMouseoverListener = _j[1];
  var _k = React.useState(null),
    mouseupListener = _k[0],
    setMouseupListener = _k[1];
  var _l = React.useState(null),
    rightclickListener = _l[0],
    setRightclickListener = _l[1];
  var _m = React.useState(null),
    clickListener = _m[0],
    setClickListener = _m[1];
  var _o = React.useState(null),
    dragListener = _o[0],
    setDragListener = _o[1];
  React.useEffect(function () {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    if (typeof options !== 'undefined' && instance !== null) {
      instance.setOptions(options);
    }
  }, [instance, options]);
  React.useEffect(function () {
    if (typeof draggable !== 'undefined' && instance !== null) {
      instance.setDraggable(draggable);
    }
  }, [instance, draggable]);
  React.useEffect(function () {
    if (typeof editable !== 'undefined' && instance !== null) {
      instance.setEditable(editable);
    }
  }, [instance, editable]);
  React.useEffect(function () {
    if (typeof visible !== 'undefined' && instance !== null) {
      instance.setVisible(visible);
    }
  }, [instance, visible]);
  React.useEffect(function () {
    if (typeof path !== 'undefined' && instance !== null) {
      instance.setPath(path);
    }
  }, [instance, path]);
  React.useEffect(function () {
    if (typeof paths !== 'undefined' && instance !== null) {
      instance.setPaths(paths);
    }
  }, [instance, paths]);
  React.useEffect(function () {
    if (instance && onDblClick) {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      setDblclickListener(google.maps.event.addListener(instance, 'dblclick', onDblClick));
    }
  }, [onDblClick]);
  React.useEffect(function () {
    if (instance && onDragEnd) {
      if (dragendListener !== null) {
        google.maps.event.removeListener(dragendListener);
      }
      setDragendListener(google.maps.event.addListener(instance, 'dragend', onDragEnd));
    }
  }, [onDragEnd]);
  React.useEffect(function () {
    if (instance && onDragStart) {
      if (dragstartListener !== null) {
        google.maps.event.removeListener(dragstartListener);
      }
      setDragstartListener(google.maps.event.addListener(instance, 'dragstart', onDragStart));
    }
  }, [onDragStart]);
  React.useEffect(function () {
    if (instance && onMouseDown) {
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      setMousedownListener(google.maps.event.addListener(instance, 'mousedown', onMouseDown));
    }
  }, [onMouseDown]);
  React.useEffect(function () {
    if (instance && onMouseMove) {
      if (mousemoveListener !== null) {
        google.maps.event.removeListener(mousemoveListener);
      }
      setMousemoveListener(google.maps.event.addListener(instance, 'mousemove', onMouseMove));
    }
  }, [onMouseMove]);
  React.useEffect(function () {
    if (instance && onMouseOut) {
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      setMouseoutListener(google.maps.event.addListener(instance, 'mouseout', onMouseOut));
    }
  }, [onMouseOut]);
  React.useEffect(function () {
    if (instance && onMouseOver) {
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      setMouseoverListener(google.maps.event.addListener(instance, 'mouseover', onMouseOver));
    }
  }, [onMouseOver]);
  React.useEffect(function () {
    if (instance && onMouseUp) {
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      setMouseupListener(google.maps.event.addListener(instance, 'mouseup', onMouseUp));
    }
  }, [onMouseUp]);
  React.useEffect(function () {
    if (instance && onRightClick) {
      if (rightclickListener !== null) {
        google.maps.event.removeListener(rightclickListener);
      }
      setRightclickListener(google.maps.event.addListener(instance, 'rightclick', onRightClick));
    }
  }, [onRightClick]);
  React.useEffect(function () {
    if (instance && onClick) {
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      setClickListener(google.maps.event.addListener(instance, 'click', onClick));
    }
  }, [onClick]);
  React.useEffect(function () {
    if (instance && onDrag) {
      if (dragListener !== null) {
        google.maps.event.removeListener(dragListener);
      }
      setDragListener(google.maps.event.addListener(instance, 'drag', onDrag));
    }
  }, [onDrag]);
  React.useEffect(function () {
    var polygon = new google.maps.Polygon(_assign(_assign({}, options || {}), {
      map: map
    }));
    if (path) {
      polygon.setPath(path);
    }
    if (paths) {
      polygon.setPaths(paths);
    }
    if (typeof visible !== 'undefined') {
      polygon.setVisible(visible);
    }
    if (typeof editable !== 'undefined') {
      polygon.setEditable(editable);
    }
    if (typeof draggable !== 'undefined') {
      polygon.setDraggable(draggable);
    }
    if (onDblClick) {
      setDblclickListener(google.maps.event.addListener(polygon, 'dblclick', onDblClick));
    }
    if (onDragEnd) {
      setDragendListener(google.maps.event.addListener(polygon, 'dragend', onDragEnd));
    }
    if (onDragStart) {
      setDragstartListener(google.maps.event.addListener(polygon, 'dragstart', onDragStart));
    }
    if (onMouseDown) {
      setMousedownListener(google.maps.event.addListener(polygon, 'mousedown', onMouseDown));
    }
    if (onMouseMove) {
      setMousemoveListener(google.maps.event.addListener(polygon, 'mousemove', onMouseMove));
    }
    if (onMouseOut) {
      setMouseoutListener(google.maps.event.addListener(polygon, 'mouseout', onMouseOut));
    }
    if (onMouseOver) {
      setMouseoverListener(google.maps.event.addListener(polygon, 'mouseover', onMouseOver));
    }
    if (onMouseUp) {
      setMouseupListener(google.maps.event.addListener(polygon, 'mouseup', onMouseUp));
    }
    if (onRightClick) {
      setRightclickListener(google.maps.event.addListener(polygon, 'rightclick', onRightClick));
    }
    if (onClick) {
      setClickListener(google.maps.event.addListener(polygon, 'click', onClick));
    }
    if (onDrag) {
      setDragListener(google.maps.event.addListener(polygon, 'drag', onDrag));
    }
    setInstance(polygon);
    if (onLoad) {
      onLoad(polygon);
    }
    return function () {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      if (dragendListener !== null) {
        google.maps.event.removeListener(dragendListener);
      }
      if (dragstartListener !== null) {
        google.maps.event.removeListener(dragstartListener);
      }
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      if (mousemoveListener !== null) {
        google.maps.event.removeListener(mousemoveListener);
      }
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      if (rightclickListener !== null) {
        google.maps.event.removeListener(rightclickListener);
      }
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      if (onUnmount) {
        onUnmount(polygon);
      }
      polygon.setMap(null);
    };
  }, []);
  return null;
}
var PolygonF = React.memo(PolygonFunctional);
var Polygon = function (_super) {
  __extends(Polygon, _super);
  function Polygon() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.state = {
      polygon: null
    };
    _this.setPolygonCallback = function () {
      if (_this.state.polygon !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.polygon);
      }
    };
    return _this;
  }
  Polygon.prototype.componentDidMount = function () {
    var polygon = new google.maps.Polygon(_assign(_assign({}, this.props.options || {}), {
      map: this.context
    }));
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$a,
      eventMap: eventMap$a,
      prevProps: {},
      nextProps: this.props,
      instance: polygon
    });
    this.setState(function setPolygon() {
      return {
        polygon: polygon
      };
    }, this.setPolygonCallback);
  };
  Polygon.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.polygon !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$a,
        eventMap: eventMap$a,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.polygon
      });
    }
  };
  Polygon.prototype.componentWillUnmount = function () {
    if (this.state.polygon !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.polygon);
      }
      unregisterEvents(this.registeredEvents);
      this.state.polygon && this.state.polygon.setMap(null);
    }
  };
  Polygon.prototype.render = function () {
    return null;
  };
  Polygon.contextType = MapContext;
  return Polygon;
}(React.PureComponent);
var eventMap$9 = {
  onBoundsChanged: 'bounds_changed',
  onClick: 'click',
  onDblClick: 'dblclick',
  onDrag: 'drag',
  onDragEnd: 'dragend',
  onDragStart: 'dragstart',
  onMouseDown: 'mousedown',
  onMouseMove: 'mousemove',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover',
  onMouseUp: 'mouseup',
  onRightClick: 'rightclick'
};
var updaterMap$9 = {
  bounds: function bounds(instance, _bounds) {
    instance.setBounds(_bounds);
  },
  draggable: function draggable(instance, _draggable4) {
    instance.setDraggable(_draggable4);
  },
  editable: function editable(instance, _editable3) {
    instance.setEditable(_editable3);
  },
  map: function map(instance, _map4) {
    instance.setMap(_map4);
  },
  options: function options(instance, _options9) {
    instance.setOptions(_options9);
  },
  visible: function visible(instance, _visible5) {
    instance.setVisible(_visible5);
  }
};
function RectangleFunctional(_a) {
  var options = _a.options,
    bounds = _a.bounds,
    draggable = _a.draggable,
    editable = _a.editable,
    visible = _a.visible,
    onDblClick = _a.onDblClick,
    onDragEnd = _a.onDragEnd,
    onDragStart = _a.onDragStart,
    onMouseDown = _a.onMouseDown,
    onMouseMove = _a.onMouseMove,
    onMouseOut = _a.onMouseOut,
    onMouseOver = _a.onMouseOver,
    onMouseUp = _a.onMouseUp,
    onRightClick = _a.onRightClick,
    onClick = _a.onClick,
    onDrag = _a.onDrag,
    onBoundsChanged = _a.onBoundsChanged,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  var _c = React.useState(null),
    dblclickListener = _c[0],
    setDblclickListener = _c[1];
  var _d = React.useState(null),
    dragendListener = _d[0],
    setDragendListener = _d[1];
  var _e = React.useState(null),
    dragstartListener = _e[0],
    setDragstartListener = _e[1];
  var _f = React.useState(null),
    mousedownListener = _f[0],
    setMousedownListener = _f[1];
  var _g = React.useState(null),
    mousemoveListener = _g[0],
    setMousemoveListener = _g[1];
  var _h = React.useState(null),
    mouseoutListener = _h[0],
    setMouseoutListener = _h[1];
  var _j = React.useState(null),
    mouseoverListener = _j[0],
    setMouseoverListener = _j[1];
  var _k = React.useState(null),
    mouseupListener = _k[0],
    setMouseupListener = _k[1];
  var _l = React.useState(null),
    rightClickListener = _l[0],
    setRightClickListener = _l[1];
  var _m = React.useState(null),
    clickListener = _m[0],
    setClickListener = _m[1];
  var _o = React.useState(null),
    dragListener = _o[0],
    setDragListener = _o[1];
  var _p = React.useState(null),
    boundsChangedListener = _p[0],
    setBoundsChangedListener = _p[1];
  React.useEffect(function () {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    if (typeof options !== 'undefined' && instance !== null) {
      instance.setOptions(options);
    }
  }, [instance, options]);
  React.useEffect(function () {
    if (typeof draggable !== 'undefined' && instance !== null) {
      instance.setDraggable(draggable);
    }
  }, [instance, draggable]);
  React.useEffect(function () {
    if (typeof editable !== 'undefined' && instance !== null) {
      instance.setEditable(editable);
    }
  }, [instance, editable]);
  React.useEffect(function () {
    if (typeof visible !== 'undefined' && instance !== null) {
      instance.setVisible(visible);
    }
  }, [instance, visible]);
  React.useEffect(function () {
    if (typeof bounds !== 'undefined' && instance !== null) {
      instance.setBounds(bounds);
    }
  }, [instance, bounds]);
  React.useEffect(function () {
    if (instance && onDblClick) {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      setDblclickListener(google.maps.event.addListener(instance, 'dblclick', onDblClick));
    }
  }, [onDblClick]);
  React.useEffect(function () {
    if (instance && onDragEnd) {
      if (dragendListener !== null) {
        google.maps.event.removeListener(dragendListener);
      }
      setDragendListener(google.maps.event.addListener(instance, 'dragend', onDragEnd));
    }
  }, [onDragEnd]);
  React.useEffect(function () {
    if (instance && onDragStart) {
      if (dragstartListener !== null) {
        google.maps.event.removeListener(dragstartListener);
      }
      setDragstartListener(google.maps.event.addListener(instance, 'dragstart', onDragStart));
    }
  }, [onDragStart]);
  React.useEffect(function () {
    if (instance && onMouseDown) {
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      setMousedownListener(google.maps.event.addListener(instance, 'mousedown', onMouseDown));
    }
  }, [onMouseDown]);
  React.useEffect(function () {
    if (instance && onMouseMove) {
      if (mousemoveListener !== null) {
        google.maps.event.removeListener(mousemoveListener);
      }
      setMousemoveListener(google.maps.event.addListener(instance, 'mousemove', onMouseMove));
    }
  }, [onMouseMove]);
  React.useEffect(function () {
    if (instance && onMouseOut) {
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      setMouseoutListener(google.maps.event.addListener(instance, 'mouseout', onMouseOut));
    }
  }, [onMouseOut]);
  React.useEffect(function () {
    if (instance && onMouseOver) {
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      setMouseoverListener(google.maps.event.addListener(instance, 'mouseover', onMouseOver));
    }
  }, [onMouseOver]);
  React.useEffect(function () {
    if (instance && onMouseUp) {
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      setMouseupListener(google.maps.event.addListener(instance, 'mouseup', onMouseUp));
    }
  }, [onMouseUp]);
  React.useEffect(function () {
    if (instance && onRightClick) {
      if (rightClickListener !== null) {
        google.maps.event.removeListener(rightClickListener);
      }
      setRightClickListener(google.maps.event.addListener(instance, 'rightclick', onRightClick));
    }
  }, [onRightClick]);
  React.useEffect(function () {
    if (instance && onClick) {
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      setClickListener(google.maps.event.addListener(instance, 'click', onClick));
    }
  }, [onClick]);
  React.useEffect(function () {
    if (instance && onDrag) {
      if (dragListener !== null) {
        google.maps.event.removeListener(dragListener);
      }
      setDragListener(google.maps.event.addListener(instance, 'drag', onDrag));
    }
  }, [onDrag]);
  React.useEffect(function () {
    if (instance && onBoundsChanged) {
      if (boundsChangedListener !== null) {
        google.maps.event.removeListener(boundsChangedListener);
      }
      setBoundsChangedListener(google.maps.event.addListener(instance, 'bounds_changed', onBoundsChanged));
    }
  }, [onBoundsChanged]);
  React.useEffect(function () {
    var rectangle = new google.maps.Rectangle(_assign(_assign({}, options || {}), {
      map: map
    }));
    if (typeof visible !== 'undefined') {
      rectangle.setVisible(visible);
    }
    if (typeof editable !== 'undefined') {
      rectangle.setEditable(editable);
    }
    if (typeof draggable !== 'undefined') {
      rectangle.setDraggable(draggable);
    }
    if (typeof bounds !== 'undefined') {
      rectangle.setBounds(bounds);
    }
    if (onDblClick) {
      setDblclickListener(google.maps.event.addListener(rectangle, 'dblclick', onDblClick));
    }
    if (onDragEnd) {
      setDragendListener(google.maps.event.addListener(rectangle, 'dragend', onDragEnd));
    }
    if (onDragStart) {
      setDragstartListener(google.maps.event.addListener(rectangle, 'dragstart', onDragStart));
    }
    if (onMouseDown) {
      setMousedownListener(google.maps.event.addListener(rectangle, 'mousedown', onMouseDown));
    }
    if (onMouseMove) {
      setMousemoveListener(google.maps.event.addListener(rectangle, 'mousemove', onMouseMove));
    }
    if (onMouseOut) {
      setMouseoutListener(google.maps.event.addListener(rectangle, 'mouseout', onMouseOut));
    }
    if (onMouseOver) {
      setMouseoverListener(google.maps.event.addListener(rectangle, 'mouseover', onMouseOver));
    }
    if (onMouseUp) {
      setMouseupListener(google.maps.event.addListener(rectangle, 'mouseup', onMouseUp));
    }
    if (onRightClick) {
      setRightClickListener(google.maps.event.addListener(rectangle, 'rightclick', onRightClick));
    }
    if (onClick) {
      setClickListener(google.maps.event.addListener(rectangle, 'click', onClick));
    }
    if (onDrag) {
      setDragListener(google.maps.event.addListener(rectangle, 'drag', onDrag));
    }
    if (onBoundsChanged) {
      setBoundsChangedListener(google.maps.event.addListener(rectangle, 'bounds_changed', onBoundsChanged));
    }
    setInstance(rectangle);
    if (onLoad) {
      onLoad(rectangle);
    }
    return function () {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      if (dragendListener !== null) {
        google.maps.event.removeListener(dragendListener);
      }
      if (dragstartListener !== null) {
        google.maps.event.removeListener(dragstartListener);
      }
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      if (mousemoveListener !== null) {
        google.maps.event.removeListener(mousemoveListener);
      }
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      if (rightClickListener !== null) {
        google.maps.event.removeListener(rightClickListener);
      }
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      if (dragListener !== null) {
        google.maps.event.removeListener(dragListener);
      }
      if (boundsChangedListener !== null) {
        google.maps.event.removeListener(boundsChangedListener);
      }
      if (onUnmount) {
        onUnmount(rectangle);
      }
      rectangle.setMap(null);
    };
  }, []);
  return null;
}
var RectangleF = React.memo(RectangleFunctional);
var Rectangle = function (_super) {
  __extends(Rectangle, _super);
  function Rectangle() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.state = {
      rectangle: null
    };
    _this.setRectangleCallback = function () {
      if (_this.state.rectangle !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.rectangle);
      }
    };
    return _this;
  }
  Rectangle.prototype.componentDidMount = function () {
    var rectangle = new google.maps.Rectangle(_assign(_assign({}, this.props.options || {}), {
      map: this.context
    }));
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$9,
      eventMap: eventMap$9,
      prevProps: {},
      nextProps: this.props,
      instance: rectangle
    });
    this.setState(function setRectangle() {
      return {
        rectangle: rectangle
      };
    }, this.setRectangleCallback);
  };
  Rectangle.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.rectangle !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$9,
        eventMap: eventMap$9,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.rectangle
      });
    }
  };
  Rectangle.prototype.componentWillUnmount = function () {
    if (this.state.rectangle !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.rectangle);
      }
      unregisterEvents(this.registeredEvents);
      this.state.rectangle.setMap(null);
    }
  };
  Rectangle.prototype.render = function () {
    return null;
  };
  Rectangle.contextType = MapContext;
  return Rectangle;
}(React.PureComponent);
var eventMap$8 = {
  onCenterChanged: 'center_changed',
  onRadiusChanged: 'radius_changed',
  onClick: 'click',
  onDblClick: 'dblclick',
  onDrag: 'drag',
  onDragEnd: 'dragend',
  onDragStart: 'dragstart',
  onMouseDown: 'mousedown',
  onMouseMove: 'mousemove',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover',
  onMouseUp: 'mouseup',
  onRightClick: 'rightclick'
};
var updaterMap$8 = {
  center: function center(instance, _center2) {
    instance.setCenter(_center2);
  },
  draggable: function draggable(instance, _draggable5) {
    instance.setDraggable(_draggable5);
  },
  editable: function editable(instance, _editable4) {
    instance.setEditable(_editable4);
  },
  map: function map(instance, _map5) {
    instance.setMap(_map5);
  },
  options: function options(instance, _options10) {
    instance.setOptions(_options10);
  },
  radius: function radius(instance, _radius) {
    instance.setRadius(_radius);
  },
  visible: function visible(instance, _visible6) {
    instance.setVisible(_visible6);
  }
};
var defaultOptions = {};
function CircleFunctional(_a) {
  var options = _a.options,
    center = _a.center,
    radius = _a.radius,
    draggable = _a.draggable,
    editable = _a.editable,
    visible = _a.visible,
    onDblClick = _a.onDblClick,
    onDragEnd = _a.onDragEnd,
    onDragStart = _a.onDragStart,
    onMouseDown = _a.onMouseDown,
    onMouseMove = _a.onMouseMove,
    onMouseOut = _a.onMouseOut,
    onMouseOver = _a.onMouseOver,
    onMouseUp = _a.onMouseUp,
    onRightClick = _a.onRightClick,
    onClick = _a.onClick,
    onDrag = _a.onDrag,
    onCenterChanged = _a.onCenterChanged,
    onRadiusChanged = _a.onRadiusChanged,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  var _c = React.useState(null),
    dblclickListener = _c[0],
    setDblclickListener = _c[1];
  var _d = React.useState(null),
    dragendListener = _d[0],
    setDragendListener = _d[1];
  var _e = React.useState(null),
    dragstartListener = _e[0],
    setDragstartListener = _e[1];
  var _f = React.useState(null),
    mousedownListener = _f[0],
    setMousedownListener = _f[1];
  var _g = React.useState(null),
    mousemoveListener = _g[0],
    setMousemoveListener = _g[1];
  var _h = React.useState(null),
    mouseoutListener = _h[0],
    setMouseoutListener = _h[1];
  var _j = React.useState(null),
    mouseoverListener = _j[0],
    setMouseoverListener = _j[1];
  var _k = React.useState(null),
    mouseupListener = _k[0],
    setMouseupListener = _k[1];
  var _l = React.useState(null),
    rightclickListener = _l[0],
    setRightclickListener = _l[1];
  var _m = React.useState(null),
    clickListener = _m[0],
    setClickListener = _m[1];
  var _o = React.useState(null),
    dragListener = _o[0],
    setDragListener = _o[1];
  var _p = React.useState(null),
    centerChangedListener = _p[0],
    setCenterChangedListener = _p[1];
  var _q = React.useState(null),
    radiusChangedListener = _q[0],
    setRadiusChangedListener = _q[1];
  React.useEffect(function () {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    if (typeof options !== 'undefined' && instance !== null) {
      instance.setOptions(options);
    }
  }, [instance, options]);
  React.useEffect(function () {
    if (typeof draggable !== 'undefined' && instance !== null) {
      instance.setDraggable(draggable);
    }
  }, [instance, draggable]);
  React.useEffect(function () {
    if (typeof editable !== 'undefined' && instance !== null) {
      instance.setEditable(editable);
    }
  }, [instance, editable]);
  React.useEffect(function () {
    if (typeof visible !== 'undefined' && instance !== null) {
      instance.setVisible(visible);
    }
  }, [instance, visible]);
  React.useEffect(function () {
    if (typeof radius === 'number' && instance !== null) {
      instance.setRadius(radius);
    }
  }, [instance, radius]);
  React.useEffect(function () {
    if (typeof center !== 'undefined' && instance !== null) {
      instance.setCenter(center);
    }
  }, [instance, center]);
  React.useEffect(function () {
    if (instance && onDblClick) {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      setDblclickListener(google.maps.event.addListener(instance, 'dblclick', onDblClick));
    }
  }, [onDblClick]);
  React.useEffect(function () {
    if (instance && onDragEnd) {
      if (dragendListener !== null) {
        google.maps.event.removeListener(dragendListener);
      }
      setDragendListener(google.maps.event.addListener(instance, 'dragend', onDragEnd));
    }
  }, [onDragEnd]);
  React.useEffect(function () {
    if (instance && onDragStart) {
      if (dragstartListener !== null) {
        google.maps.event.removeListener(dragstartListener);
      }
      setDragstartListener(google.maps.event.addListener(instance, 'dragstart', onDragStart));
    }
  }, [onDragStart]);
  React.useEffect(function () {
    if (instance && onMouseDown) {
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      setMousedownListener(google.maps.event.addListener(instance, 'mousedown', onMouseDown));
    }
  }, [onMouseDown]);
  React.useEffect(function () {
    if (instance && onMouseMove) {
      if (mousemoveListener !== null) {
        google.maps.event.removeListener(mousemoveListener);
      }
      setMousemoveListener(google.maps.event.addListener(instance, 'mousemove', onMouseMove));
    }
  }, [onMouseMove]);
  React.useEffect(function () {
    if (instance && onMouseOut) {
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      setMouseoutListener(google.maps.event.addListener(instance, 'mouseout', onMouseOut));
    }
  }, [onMouseOut]);
  React.useEffect(function () {
    if (instance && onMouseOver) {
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      setMouseoverListener(google.maps.event.addListener(instance, 'mouseover', onMouseOver));
    }
  }, [onMouseOver]);
  React.useEffect(function () {
    if (instance && onMouseUp) {
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      setMouseupListener(google.maps.event.addListener(instance, 'mouseup', onMouseUp));
    }
  }, [onMouseUp]);
  React.useEffect(function () {
    if (instance && onRightClick) {
      if (rightclickListener !== null) {
        google.maps.event.removeListener(rightclickListener);
      }
      setRightclickListener(google.maps.event.addListener(instance, 'rightclick', onRightClick));
    }
  }, [onRightClick]);
  React.useEffect(function () {
    if (instance && onClick) {
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      setClickListener(google.maps.event.addListener(instance, 'click', onClick));
    }
  }, [onClick]);
  React.useEffect(function () {
    if (instance && onDrag) {
      if (dragListener !== null) {
        google.maps.event.removeListener(dragListener);
      }
      setDragListener(google.maps.event.addListener(instance, 'drag', onDrag));
    }
  }, [onDrag]);
  React.useEffect(function () {
    if (instance && onCenterChanged) {
      if (centerChangedListener !== null) {
        google.maps.event.removeListener(centerChangedListener);
      }
      setCenterChangedListener(google.maps.event.addListener(instance, 'center_changed', onCenterChanged));
    }
  }, [onClick]);
  React.useEffect(function () {
    if (instance && onRadiusChanged) {
      if (radiusChangedListener !== null) {
        google.maps.event.removeListener(radiusChangedListener);
      }
      setRadiusChangedListener(google.maps.event.addListener(instance, 'radius_changed', onRadiusChanged));
    }
  }, [onRadiusChanged]);
  React.useEffect(function () {
    var circle = new google.maps.Circle(_assign(_assign({}, options || defaultOptions), {
      map: map
    }));
    if (typeof radius === 'number') {
      circle.setRadius(radius);
    }
    if (typeof center !== 'undefined') {
      circle.setCenter(center);
    }
    if (typeof radius === 'number') {
      circle.setRadius(radius);
    }
    if (typeof visible !== 'undefined') {
      circle.setVisible(visible);
    }
    if (typeof editable !== 'undefined') {
      circle.setEditable(editable);
    }
    if (typeof draggable !== 'undefined') {
      circle.setDraggable(draggable);
    }
    if (onDblClick) {
      setDblclickListener(google.maps.event.addListener(circle, 'dblclick', onDblClick));
    }
    if (onDragEnd) {
      setDragendListener(google.maps.event.addListener(circle, 'dragend', onDragEnd));
    }
    if (onDragStart) {
      setDragstartListener(google.maps.event.addListener(circle, 'dragstart', onDragStart));
    }
    if (onMouseDown) {
      setMousedownListener(google.maps.event.addListener(circle, 'mousedown', onMouseDown));
    }
    if (onMouseMove) {
      setMousemoveListener(google.maps.event.addListener(circle, 'mousemove', onMouseMove));
    }
    if (onMouseOut) {
      setMouseoutListener(google.maps.event.addListener(circle, 'mouseout', onMouseOut));
    }
    if (onMouseOver) {
      setMouseoverListener(google.maps.event.addListener(circle, 'mouseover', onMouseOver));
    }
    if (onMouseUp) {
      setMouseupListener(google.maps.event.addListener(circle, 'mouseup', onMouseUp));
    }
    if (onRightClick) {
      setRightclickListener(google.maps.event.addListener(circle, 'rightclick', onRightClick));
    }
    if (onClick) {
      setClickListener(google.maps.event.addListener(circle, 'click', onClick));
    }
    if (onDrag) {
      setDragListener(google.maps.event.addListener(circle, 'drag', onDrag));
    }
    if (onCenterChanged) {
      setCenterChangedListener(google.maps.event.addListener(circle, 'center_changed', onCenterChanged));
    }
    if (onRadiusChanged) {
      setRadiusChangedListener(google.maps.event.addListener(circle, 'radius_changed', onRadiusChanged));
    }
    setInstance(circle);
    if (onLoad) {
      onLoad(circle);
    }
    return function () {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      if (dragendListener !== null) {
        google.maps.event.removeListener(dragendListener);
      }
      if (dragstartListener !== null) {
        google.maps.event.removeListener(dragstartListener);
      }
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      if (mousemoveListener !== null) {
        google.maps.event.removeListener(mousemoveListener);
      }
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      if (rightclickListener !== null) {
        google.maps.event.removeListener(rightclickListener);
      }
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      if (centerChangedListener !== null) {
        google.maps.event.removeListener(centerChangedListener);
      }
      if (radiusChangedListener !== null) {
        google.maps.event.removeListener(radiusChangedListener);
      }
      if (onUnmount) {
        onUnmount(circle);
      }
      circle.setMap(null);
    };
  }, []);
  return null;
}
var CircleF = React.memo(CircleFunctional);
var Circle = function (_super) {
  __extends(Circle, _super);
  function Circle() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.state = {
      circle: null
    };
    _this.setCircleCallback = function () {
      if (_this.state.circle !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.circle);
      }
    };
    return _this;
  }
  Circle.prototype.componentDidMount = function () {
    var circle = new google.maps.Circle(_assign(_assign({}, this.props.options || {}), {
      map: this.context
    }));
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$8,
      eventMap: eventMap$8,
      prevProps: {},
      nextProps: this.props,
      instance: circle
    });
    this.setState(function setCircle() {
      return {
        circle: circle
      };
    }, this.setCircleCallback);
  };
  Circle.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.circle !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$8,
        eventMap: eventMap$8,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.circle
      });
    }
  };
  Circle.prototype.componentWillUnmount = function () {
    var _a;
    if (this.state.circle !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.circle);
      }
      unregisterEvents(this.registeredEvents);
      (_a = this.state.circle) === null || _a === void 0 ? void 0 : _a.setMap(null);
    }
  };
  Circle.prototype.render = function () {
    return null;
  };
  Circle.contextType = MapContext;
  return Circle;
}(React.PureComponent);
var eventMap$7 = {
  onClick: 'click',
  onDblClick: 'dblclick',
  onMouseDown: 'mousedown',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover',
  onMouseUp: 'mouseup',
  onRightClick: 'rightclick',
  onAddFeature: 'addfeature',
  onRemoveFeature: 'removefeature',
  onRemoveProperty: 'removeproperty',
  onSetGeometry: 'setgeometry',
  onSetProperty: 'setproperty'
};
var updaterMap$7 = {
  add: function add(instance, feature) {
    instance.add(feature);
  },
  addgeojson: function addgeojson(instance, geojson, options) {
    instance.addGeoJson(geojson, options);
  },
  contains: function contains(instance, feature) {
    instance.contains(feature);
  },
  foreach: function foreach(instance, callback) {
    instance.forEach(callback);
  },
  loadgeojson: function loadgeojson(instance, url, options, callback) {
    instance.loadGeoJson(url, options, callback);
  },
  overridestyle: function overridestyle(instance, feature, style) {
    instance.overrideStyle(feature, style);
  },
  remove: function remove(instance, feature) {
    instance.remove(feature);
  },
  revertstyle: function revertstyle(instance, feature) {
    instance.revertStyle(feature);
  },
  controlposition: function controlposition(instance, controlPosition) {
    instance.setControlPosition(controlPosition);
  },
  controls: function controls(instance, _controls) {
    instance.setControls(_controls);
  },
  drawingmode: function drawingmode(instance, mode) {
    instance.setDrawingMode(mode);
  },
  map: function map(instance, _map6) {
    instance.setMap(_map6);
  },
  style: function style(instance, _style) {
    instance.setStyle(_style);
  },
  togeojson: function togeojson(instance, callback) {
    instance.toGeoJson(callback);
  }
};
function DataFunctional(_a) {
  var options = _a.options,
    onClick = _a.onClick,
    onDblClick = _a.onDblClick,
    onMouseDown = _a.onMouseDown,
    onMouseMove = _a.onMouseMove,
    onMouseOut = _a.onMouseOut,
    onMouseOver = _a.onMouseOver,
    onMouseUp = _a.onMouseUp,
    onRightClick = _a.onRightClick,
    onAddFeature = _a.onAddFeature,
    onRemoveFeature = _a.onRemoveFeature,
    onRemoveProperty = _a.onRemoveProperty,
    onSetGeometry = _a.onSetGeometry,
    onSetProperty = _a.onSetProperty,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  var _c = React.useState(null),
    dblclickListener = _c[0],
    setDblclickListener = _c[1];
  var _d = React.useState(null),
    mousedownListener = _d[0],
    setMousedownListener = _d[1];
  var _e = React.useState(null),
    mousemoveListener = _e[0],
    setMousemoveListener = _e[1];
  var _f = React.useState(null),
    mouseoutListener = _f[0],
    setMouseoutListener = _f[1];
  var _g = React.useState(null),
    mouseoverListener = _g[0],
    setMouseoverListener = _g[1];
  var _h = React.useState(null),
    mouseupListener = _h[0],
    setMouseupListener = _h[1];
  var _j = React.useState(null),
    rightclickListener = _j[0],
    setRightclickListener = _j[1];
  var _k = React.useState(null),
    clickListener = _k[0],
    setClickListener = _k[1];
  var _l = React.useState(null),
    addFeatureListener = _l[0],
    setAddFeatureListener = _l[1];
  var _m = React.useState(null),
    removeFeatureListener = _m[0],
    setRemoveFeatureListener = _m[1];
  var _o = React.useState(null),
    removePropertyListener = _o[0],
    setRemovePropertyListener = _o[1];
  var _p = React.useState(null),
    setGeometryListener = _p[0],
    setSetGeometryListener = _p[1];
  var _q = React.useState(null),
    setPropertyListener = _q[0],
    setSetPropertyListener = _q[1];
  React.useEffect(function () {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    if (instance && onDblClick) {
      if (dblclickListener !== null) {
        google.maps.event.removeListener(dblclickListener);
      }
      setDblclickListener(google.maps.event.addListener(instance, 'dblclick', onDblClick));
    }
  }, [onDblClick]);
  React.useEffect(function () {
    if (instance && onMouseDown) {
      if (mousedownListener !== null) {
        google.maps.event.removeListener(mousedownListener);
      }
      setMousedownListener(google.maps.event.addListener(instance, 'mousedown', onMouseDown));
    }
  }, [onMouseDown]);
  React.useEffect(function () {
    if (instance && onMouseMove) {
      if (mousemoveListener !== null) {
        google.maps.event.removeListener(mousemoveListener);
      }
      setMousemoveListener(google.maps.event.addListener(instance, 'mousemove', onMouseMove));
    }
  }, [onMouseMove]);
  React.useEffect(function () {
    if (instance && onMouseOut) {
      if (mouseoutListener !== null) {
        google.maps.event.removeListener(mouseoutListener);
      }
      setMouseoutListener(google.maps.event.addListener(instance, 'mouseout', onMouseOut));
    }
  }, [onMouseOut]);
  React.useEffect(function () {
    if (instance && onMouseOver) {
      if (mouseoverListener !== null) {
        google.maps.event.removeListener(mouseoverListener);
      }
      setMouseoverListener(google.maps.event.addListener(instance, 'mouseover', onMouseOver));
    }
  }, [onMouseOver]);
  React.useEffect(function () {
    if (instance && onMouseUp) {
      if (mouseupListener !== null) {
        google.maps.event.removeListener(mouseupListener);
      }
      setMouseupListener(google.maps.event.addListener(instance, 'mouseup', onMouseUp));
    }
  }, [onMouseUp]);
  React.useEffect(function () {
    if (instance && onRightClick) {
      if (rightclickListener !== null) {
        google.maps.event.removeListener(rightclickListener);
      }
      setRightclickListener(google.maps.event.addListener(instance, 'rightclick', onRightClick));
    }
  }, [onRightClick]);
  React.useEffect(function () {
    if (instance && onClick) {
      if (clickListener !== null) {
        google.maps.event.removeListener(clickListener);
      }
      setClickListener(google.maps.event.addListener(instance, 'click', onClick));
    }
  }, [onClick]);
  React.useEffect(function () {
    if (instance && onAddFeature) {
      if (addFeatureListener !== null) {
        google.maps.event.removeListener(addFeatureListener);
      }
      setAddFeatureListener(google.maps.event.addListener(instance, 'addfeature', onAddFeature));
    }
  }, [onAddFeature]);
  React.useEffect(function () {
    if (instance && onRemoveFeature) {
      if (removeFeatureListener !== null) {
        google.maps.event.removeListener(removeFeatureListener);
      }
      setRemoveFeatureListener(google.maps.event.addListener(instance, 'removefeature', onRemoveFeature));
    }
  }, [onRemoveFeature]);
  React.useEffect(function () {
    if (instance && onRemoveProperty) {
      if (removePropertyListener !== null) {
        google.maps.event.removeListener(removePropertyListener);
      }
      setRemovePropertyListener(google.maps.event.addListener(instance, 'removeproperty', onRemoveProperty));
    }
  }, [onRemoveProperty]);
  React.useEffect(function () {
    if (instance && onSetGeometry) {
      if (setGeometryListener !== null) {
        google.maps.event.removeListener(setGeometryListener);
      }
      setSetGeometryListener(google.maps.event.addListener(instance, 'setgeometry', onSetGeometry));
    }
  }, [onSetGeometry]);
  React.useEffect(function () {
    if (instance && onSetProperty) {
      if (setPropertyListener !== null) {
        google.maps.event.removeListener(setPropertyListener);
      }
      setSetPropertyListener(google.maps.event.addListener(instance, 'setproperty', onSetProperty));
    }
  }, [onSetProperty]);
  React.useEffect(function () {
    if (map !== null) {
      var data = new google.maps.Data(_assign(_assign({}, options || {}), {
        map: map
      }));
      if (onDblClick) {
        setDblclickListener(google.maps.event.addListener(data, 'dblclick', onDblClick));
      }
      if (onMouseDown) {
        setMousedownListener(google.maps.event.addListener(data, 'mousedown', onMouseDown));
      }
      if (onMouseMove) {
        setMousemoveListener(google.maps.event.addListener(data, 'mousemove', onMouseMove));
      }
      if (onMouseOut) {
        setMouseoutListener(google.maps.event.addListener(data, 'mouseout', onMouseOut));
      }
      if (onMouseOver) {
        setMouseoverListener(google.maps.event.addListener(data, 'mouseover', onMouseOver));
      }
      if (onMouseUp) {
        setMouseupListener(google.maps.event.addListener(data, 'mouseup', onMouseUp));
      }
      if (onRightClick) {
        setRightclickListener(google.maps.event.addListener(data, 'rightclick', onRightClick));
      }
      if (onClick) {
        setClickListener(google.maps.event.addListener(data, 'click', onClick));
      }
      if (onAddFeature) {
        setAddFeatureListener(google.maps.event.addListener(data, 'addfeature', onAddFeature));
      }
      if (onRemoveFeature) {
        setRemoveFeatureListener(google.maps.event.addListener(data, 'removefeature', onRemoveFeature));
      }
      if (onRemoveProperty) {
        setRemovePropertyListener(google.maps.event.addListener(data, 'removeproperty', onRemoveProperty));
      }
      if (onSetGeometry) {
        setSetGeometryListener(google.maps.event.addListener(data, 'setgeometry', onSetGeometry));
      }
      if (onSetProperty) {
        setSetPropertyListener(google.maps.event.addListener(data, 'setproperty', onSetProperty));
      }
      setInstance(data);
      if (onLoad) {
        onLoad(data);
      }
    }
    return function () {
      if (instance) {
        if (dblclickListener !== null) {
          google.maps.event.removeListener(dblclickListener);
        }
        if (mousedownListener !== null) {
          google.maps.event.removeListener(mousedownListener);
        }
        if (mousemoveListener !== null) {
          google.maps.event.removeListener(mousemoveListener);
        }
        if (mouseoutListener !== null) {
          google.maps.event.removeListener(mouseoutListener);
        }
        if (mouseoverListener !== null) {
          google.maps.event.removeListener(mouseoverListener);
        }
        if (mouseupListener !== null) {
          google.maps.event.removeListener(mouseupListener);
        }
        if (rightclickListener !== null) {
          google.maps.event.removeListener(rightclickListener);
        }
        if (clickListener !== null) {
          google.maps.event.removeListener(clickListener);
        }
        if (addFeatureListener !== null) {
          google.maps.event.removeListener(addFeatureListener);
        }
        if (removeFeatureListener !== null) {
          google.maps.event.removeListener(removeFeatureListener);
        }
        if (removePropertyListener !== null) {
          google.maps.event.removeListener(removePropertyListener);
        }
        if (setGeometryListener !== null) {
          google.maps.event.removeListener(setGeometryListener);
        }
        if (setPropertyListener !== null) {
          google.maps.event.removeListener(setPropertyListener);
        }
        if (onUnmount) {
          onUnmount(instance);
        }
        instance.setMap(null);
      }
    };
  }, []);
  return null;
}
var DataF = React.memo(DataFunctional);
var Data = function (_super) {
  __extends(Data, _super);
  function Data() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.state = {
      data: null
    };
    _this.setDataCallback = function () {
      if (_this.state.data !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.data);
      }
    };
    return _this;
  }
  Data.prototype.componentDidMount = function () {
    if (this.context !== null) {
      var data_1 = new google.maps.Data(_assign(_assign({}, this.props.options || {}), {
        map: this.context
      }));
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$7,
        eventMap: eventMap$7,
        prevProps: {},
        nextProps: this.props,
        instance: data_1
      });
      this.setState(function () {
        return {
          data: data_1
        };
      }, this.setDataCallback);
    }
  };
  Data.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.data !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$7,
        eventMap: eventMap$7,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.data
      });
    }
  };
  Data.prototype.componentWillUnmount = function () {
    if (this.state.data !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.data);
      }
      unregisterEvents(this.registeredEvents);
      if (this.state.data) {
        this.state.data.setMap(null);
      }
    }
  };
  Data.prototype.render = function () {
    return null;
  };
  Data.contextType = MapContext;
  return Data;
}(React.PureComponent);
var eventMap$6 = {
  onClick: 'click',
  onDefaultViewportChanged: 'defaultviewport_changed',
  onStatusChanged: 'status_changed'
};
var updaterMap$6 = {
  options: function options(instance, _options11) {
    instance.setOptions(_options11);
  },
  url: function url(instance, _url) {
    instance.setUrl(_url);
  },
  zIndex: function zIndex(instance, _zIndex4) {
    instance.setZIndex(_zIndex4);
  }
};
var KmlLayer = function (_super) {
  __extends(KmlLayer, _super);
  function KmlLayer() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.state = {
      kmlLayer: null
    };
    _this.setKmlLayerCallback = function () {
      if (_this.state.kmlLayer !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.kmlLayer);
      }
    };
    return _this;
  }
  KmlLayer.prototype.componentDidMount = function () {
    var kmlLayer = new google.maps.KmlLayer(_assign(_assign({}, this.props.options), {
      map: this.context
    }));
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$6,
      eventMap: eventMap$6,
      prevProps: {},
      nextProps: this.props,
      instance: kmlLayer
    });
    this.setState(function setLmlLayer() {
      return {
        kmlLayer: kmlLayer
      };
    }, this.setKmlLayerCallback);
  };
  KmlLayer.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.kmlLayer !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$6,
        eventMap: eventMap$6,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.kmlLayer
      });
    }
  };
  KmlLayer.prototype.componentWillUnmount = function () {
    if (this.state.kmlLayer !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.kmlLayer);
      }
      unregisterEvents(this.registeredEvents);
      this.state.kmlLayer.setMap(null);
    }
  };
  KmlLayer.prototype.render = function () {
    return null;
  };
  KmlLayer.contextType = MapContext;
  return KmlLayer;
}(React.PureComponent);
function getOffsetOverride(containerElement, getPixelPositionOffset) {
  return typeof getPixelPositionOffset === 'function' ? getPixelPositionOffset(containerElement.offsetWidth, containerElement.offsetHeight) : {
    x: 0,
    y: 0
  };
}
function createLatLng(inst, Type) {
  return new Type(inst.lat, inst.lng);
}
function createLatLngBounds(inst, Type) {
  return new Type(new google.maps.LatLng(inst.ne.lat, inst.ne.lng), new google.maps.LatLng(inst.sw.lat, inst.sw.lng));
}
function ensureOfType(inst, type, factory) {
  return inst instanceof type ? inst : factory(inst, type);
}
function ensureOfTypeBounds(inst, type, factory) {
  return inst instanceof type ? inst : factory(inst, type);
}
function getLayoutStylesByBounds(mapCanvasProjection, offset, bounds) {
  var ne = mapCanvasProjection && mapCanvasProjection.fromLatLngToDivPixel(bounds.getNorthEast());
  var sw = mapCanvasProjection && mapCanvasProjection.fromLatLngToDivPixel(bounds.getSouthWest());
  if (ne && sw) {
    return {
      left: "".concat(sw.x + offset.x, "px"),
      top: "".concat(ne.y + offset.y, "px"),
      width: "".concat(ne.x - sw.x - offset.x, "px"),
      height: "".concat(sw.y - ne.y - offset.y, "px")
    };
  }
  return {
    left: '-9999px',
    top: '-9999px'
  };
}
function getLayoutStylesByPosition(mapCanvasProjection, offset, position) {
  var point = mapCanvasProjection && mapCanvasProjection.fromLatLngToDivPixel(position);
  if (point) {
    var x = point.x,
      y = point.y;
    return {
      left: "".concat(x + offset.x, "px"),
      top: "".concat(y + offset.y, "px")
    };
  }
  return {
    left: '-9999px',
    top: '-9999px'
  };
}
function getLayoutStyles(mapCanvasProjection, offset, bounds, position) {
  return bounds !== undefined ? getLayoutStylesByBounds(mapCanvasProjection, offset, ensureOfTypeBounds(bounds, google.maps.LatLngBounds, createLatLngBounds)) : getLayoutStylesByPosition(mapCanvasProjection, offset, ensureOfType(position, google.maps.LatLng, createLatLng));
}
function arePositionsEqual(currentPosition, previousPosition) {
  return currentPosition.left === previousPosition.left && currentPosition.top === previousPosition.top && currentPosition.width === previousPosition.height && currentPosition.height === previousPosition.height;
}
function createOverlay(container, pane, position, bounds, getPixelPositionOffset) {
  var Overlay = function (_super) {
    __extends(Overlay, _super);
    function Overlay(container, pane, position, bounds) {
      var _this = _super.call(this) || this;
      _this.container = container;
      _this.pane = pane;
      _this.position = position;
      _this.bounds = bounds;
      return _this;
    }
    Overlay.prototype.onAdd = function () {
      var _a;
      var pane = (_a = this.getPanes()) === null || _a === void 0 ? void 0 : _a[this.pane];
      pane === null || pane === void 0 ? void 0 : pane.appendChild(this.container);
    };
    Overlay.prototype.draw = function () {
      var projection = this.getProjection();
      var offset = _assign({}, this.container ? getOffsetOverride(this.container, getPixelPositionOffset) : {
        x: 0,
        y: 0
      });
      var layoutStyles = getLayoutStyles(projection, offset, this.bounds, this.position);
      for (var _i = 0, _a = Object.entries(layoutStyles); _i < _a.length; _i++) {
        var _b = _a[_i],
          key = _b[0],
          value = _b[1];
        this.container.style[key] = value;
      }
    };
    Overlay.prototype.onRemove = function () {
      if (this.container.parentNode !== null) {
        this.container.parentNode.removeChild(this.container);
      }
    };
    return Overlay;
  }(google.maps.OverlayView);
  return new Overlay(container, pane, position, bounds);
}
function convertToLatLngString(latLngLike) {
  if (!latLngLike) {
    return '';
  }
  var latLng = latLngLike instanceof google.maps.LatLng ? latLngLike : new google.maps.LatLng(latLngLike.lat, latLngLike.lng);
  return latLng + '';
}
function convertToLatLngBoundsString(latLngBoundsLike) {
  if (!latLngBoundsLike) {
    return '';
  }
  var latLngBounds = latLngBoundsLike instanceof google.maps.LatLngBounds ? latLngBoundsLike : new google.maps.LatLngBounds(new google.maps.LatLng(latLngBoundsLike.south, latLngBoundsLike.east), new google.maps.LatLng(latLngBoundsLike.north, latLngBoundsLike.west));
  return latLngBounds + '';
}
function OverlayViewFunctional(_a) {
  var position = _a.position,
    bounds = _a.bounds,
    mapPaneName = _a.mapPaneName,
    zIndex = _a.zIndex,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount,
    getPixelPositionOffset = _a.getPixelPositionOffset,
    children = _a.children;
  var map = React.useContext(MapContext);
  var container = React.useMemo(function () {
    var div = document.createElement('div');
    div.style.position = 'absolute';
    return div;
  }, []);
  var overlay = React.useMemo(function () {
    return createOverlay(container, mapPaneName, position, bounds, getPixelPositionOffset);
  }, [container, mapPaneName, position, bounds]);
  React.useEffect(function () {
    onLoad === null || onLoad === void 0 ? void 0 : onLoad(overlay);
    overlay === null || overlay === void 0 ? void 0 : overlay.setMap(map);
    return function () {
      onUnmount === null || onUnmount === void 0 ? void 0 : onUnmount(overlay);
      overlay === null || overlay === void 0 ? void 0 : overlay.setMap(null);
    };
  }, [map, overlay]);
  React.useEffect(function () {
    container.style.zIndex = "".concat(zIndex);
  }, [zIndex, container]);
  return ReactDOM.createPortal(children, container);
}
var OverlayViewF = React.memo(OverlayViewFunctional);
var OverlayView = function (_super) {
  __extends(OverlayView, _super);
  function OverlayView(props) {
    var _this = _super.call(this, props) || this;
    _this.state = {
      paneEl: null,
      containerStyle: {
        position: 'absolute'
      }
    };
    _this.updatePane = function () {
      var mapPaneName = _this.props.mapPaneName;
      var mapPanes = _this.overlayView.getPanes();
      invariant$1(!!mapPaneName, "OverlayView requires props.mapPaneName but got %s", mapPaneName);
      if (mapPanes) {
        _this.setState({
          paneEl: mapPanes[mapPaneName]
        });
      } else {
        _this.setState({
          paneEl: null
        });
      }
    };
    _this.onAdd = function () {
      var _a, _b;
      _this.updatePane();
      (_b = (_a = _this.props).onLoad) === null || _b === void 0 ? void 0 : _b.call(_a, _this.overlayView);
    };
    _this.onPositionElement = function () {
      var _a;
      var mapCanvasProjection = _this.overlayView.getProjection();
      var offset = _assign({
        x: 0,
        y: 0
      }, _this.containerRef.current ? getOffsetOverride(_this.containerRef.current, _this.props.getPixelPositionOffset) : {});
      var layoutStyles = getLayoutStyles(mapCanvasProjection, offset, _this.props.bounds, _this.props.position);
      var left = (_a = _this.state.containerStyle, _a.left),
        top = _a.top,
        width = _a.width,
        height = _a.height;
      if (!arePositionsEqual(layoutStyles, {
        left: left,
        top: top,
        width: width,
        height: height
      })) {
        _this.setState({
          containerStyle: {
            top: layoutStyles.top || 0,
            left: layoutStyles.left || 0,
            width: layoutStyles.width || 0,
            height: layoutStyles.height || 0,
            position: 'absolute'
          }
        });
      }
    };
    _this.draw = function () {
      _this.onPositionElement();
    };
    _this.onRemove = function () {
      var _a, _b;
      _this.setState(function () {
        return {
          paneEl: null
        };
      });
      (_b = (_a = _this.props).onUnmount) === null || _b === void 0 ? void 0 : _b.call(_a, _this.overlayView);
    };
    _this.containerRef = React.createRef();
    var overlayView = new google.maps.OverlayView();
    overlayView.onAdd = _this.onAdd;
    overlayView.draw = _this.draw;
    overlayView.onRemove = _this.onRemove;
    _this.overlayView = overlayView;
    return _this;
  }
  OverlayView.prototype.componentDidMount = function () {
    this.overlayView.setMap(this.context);
  };
  OverlayView.prototype.componentDidUpdate = function (prevProps) {
    var prevPositionString = convertToLatLngString(prevProps.position);
    var positionString = convertToLatLngString(this.props.position);
    var prevBoundsString = convertToLatLngBoundsString(prevProps.bounds);
    var boundsString = convertToLatLngBoundsString(this.props.bounds);
    if (prevPositionString !== positionString || prevBoundsString !== boundsString) {
      this.overlayView.draw();
    }
    if (prevProps.mapPaneName !== this.props.mapPaneName) {
      this.updatePane();
    }
  };
  OverlayView.prototype.componentWillUnmount = function () {
    this.overlayView.setMap(null);
  };
  OverlayView.prototype.render = function () {
    var paneEl = this.state.paneEl;
    if (paneEl) {
      return ReactDOM.createPortal(jsxRuntime.jsx("div", {
        ref: this.containerRef,
        style: this.state.containerStyle,
        children: React.Children.only(this.props.children)
      }), paneEl);
    } else {
      return null;
    }
  };
  OverlayView.FLOAT_PANE = "floatPane";
  OverlayView.MAP_PANE = "mapPane";
  OverlayView.MARKER_LAYER = "markerLayer";
  OverlayView.OVERLAY_LAYER = "overlayLayer";
  OverlayView.OVERLAY_MOUSE_TARGET = "overlayMouseTarget";
  OverlayView.contextType = MapContext;
  return OverlayView;
}(React.PureComponent);
function noop() {
  return;
}
var eventMap$5 = {
  onDblClick: 'dblclick',
  onClick: 'click'
};
var updaterMap$5 = {
  opacity: function opacity(instance, _opacity2) {
    instance.setOpacity(_opacity2);
  }
};
function GroundOverlayFunctional(_a) {
  var url = _a.url,
    bounds = _a.bounds,
    options = _a.options,
    visible = _a.visible;
  var map = React.useContext(MapContext);
  var imageBounds = new google.maps.LatLngBounds(new google.maps.LatLng(bounds.south, bounds.west), new google.maps.LatLng(bounds.north, bounds.east));
  var groundOverlay = React.useMemo(function () {
    var overlay = new google.maps.GroundOverlay(url, imageBounds, _assign({}, options));
    return overlay;
  }, []);
  React.useEffect(function () {
    if (groundOverlay !== null) {
      groundOverlay.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    if (typeof url !== 'undefined' && groundOverlay !== null) {
      groundOverlay.set("url", url);
      groundOverlay.setMap(map);
    }
  }, [groundOverlay, url]);
  React.useEffect(function () {
    if (typeof visible !== 'undefined' && groundOverlay !== null) {
      groundOverlay.setOpacity(visible ? 1 : 0);
    }
  }, [groundOverlay, visible]);
  React.useEffect(function () {
    var newBounds = new google.maps.LatLngBounds(new google.maps.LatLng(bounds.south, bounds.west), new google.maps.LatLng(bounds.north, bounds.east));
    if (typeof bounds !== 'undefined' && groundOverlay !== null) {
      groundOverlay.set("bounds", newBounds);
      groundOverlay.setMap(map);
    }
  }, [groundOverlay, bounds]);
  return null;
}
var GroundOverlayF = React.memo(GroundOverlayFunctional);
var GroundOverlay = function (_super) {
  __extends(GroundOverlay, _super);
  function GroundOverlay() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.state = {
      groundOverlay: null
    };
    _this.setGroundOverlayCallback = function () {
      if (_this.state.groundOverlay !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.groundOverlay);
      }
    };
    return _this;
  }
  GroundOverlay.prototype.componentDidMount = function () {
    invariant$1(!!this.props.url || !!this.props.bounds, "For GroundOverlay, url and bounds are passed in to constructor and are immutable after instantiated. This is the behavior of Google Maps JavaScript API v3 ( See https://developers.google.com/maps/documentation/javascript/reference#GroundOverlay) Hence, use the corresponding two props provided by `react-google-maps-api`, url and bounds. In some cases, you'll need the GroundOverlay component to reflect the changes of url and bounds. You can leverage the React's key property to remount the component. Typically, just `key={url}` would serve your need. See https://github.com/tomchentw/react-google-maps/issues/655");
    var groundOverlay = new google.maps.GroundOverlay(this.props.url, this.props.bounds, _assign(_assign({}, this.props.options), {
      map: this.context
    }));
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$5,
      eventMap: eventMap$5,
      prevProps: {},
      nextProps: this.props,
      instance: groundOverlay
    });
    this.setState(function setGroundOverlay() {
      return {
        groundOverlay: groundOverlay
      };
    }, this.setGroundOverlayCallback);
  };
  GroundOverlay.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.groundOverlay !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$5,
        eventMap: eventMap$5,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.groundOverlay
      });
    }
  };
  GroundOverlay.prototype.componentWillUnmount = function () {
    if (this.state.groundOverlay) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.groundOverlay);
      }
      this.state.groundOverlay.setMap(null);
    }
  };
  GroundOverlay.prototype.render = function () {
    return null;
  };
  GroundOverlay.defaultProps = {
    onLoad: noop
  };
  GroundOverlay.contextType = MapContext;
  return GroundOverlay;
}(React.PureComponent);
var eventMap$4 = {};
var updaterMap$4 = {
  data: function data(instance, _data) {
    instance.setData(_data);
  },
  map: function map(instance, _map7) {
    instance.setMap(_map7);
  },
  options: function options(instance, _options12) {
    instance.setOptions(_options12);
  }
};
function HeatmapLayerFunctional(_a) {
  var data = _a.data,
    onLoad = _a.onLoad,
    onUnmount = _a.onUnmount,
    options = _a.options;
  var map = React.useContext(MapContext);
  var _b = React.useState(null),
    instance = _b[0],
    setInstance = _b[1];
  React.useEffect(function () {
    if (!google.maps.visualization) {
      invariant$1(!!google.maps.visualization, 'Did you include prop libraries={["visualization"]} in useJsApiScript? %s', google.maps.visualization);
    }
  }, []);
  React.useEffect(function () {
    invariant$1(!!data, 'data property is required in HeatmapLayer %s', data);
  }, [data]);
  React.useEffect(function () {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);
  React.useEffect(function () {
    if (options && instance !== null) {
      instance.setOptions(options);
    }
  }, [instance, options]);
  React.useEffect(function () {
    var heatmapLayer = new google.maps.visualization.HeatmapLayer(_assign(_assign({}, options || {}), {
      data: data,
      map: map
    }));
    setInstance(heatmapLayer);
    if (onLoad) {
      onLoad(heatmapLayer);
    }
    return function () {
      if (instance !== null) {
        if (onUnmount) {
          onUnmount(instance);
        }
        instance.setMap(null);
      }
    };
  }, []);
  return null;
}
var HeatmapLayerF = React.memo(HeatmapLayerFunctional);
var HeatmapLayer = function (_super) {
  __extends(HeatmapLayer, _super);
  function HeatmapLayer() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.state = {
      heatmapLayer: null
    };
    _this.setHeatmapLayerCallback = function () {
      if (_this.state.heatmapLayer !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.heatmapLayer);
      }
    };
    return _this;
  }
  HeatmapLayer.prototype.componentDidMount = function () {
    invariant$1(!!google.maps.visualization, 'Did you include prop libraries={["visualization"]} to <LoadScript />? %s', google.maps.visualization);
    invariant$1(!!this.props.data, 'data property is required in HeatmapLayer %s', this.props.data);
    var heatmapLayer = new google.maps.visualization.HeatmapLayer(_assign(_assign({}, this.props.options || {}), {
      data: this.props.data,
      map: this.context
    }));
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$4,
      eventMap: eventMap$4,
      prevProps: {},
      nextProps: this.props,
      instance: heatmapLayer
    });
    this.setState(function setHeatmapLayer() {
      return {
        heatmapLayer: heatmapLayer
      };
    }, this.setHeatmapLayerCallback);
  };
  HeatmapLayer.prototype.componentDidUpdate = function (prevProps) {
    unregisterEvents(this.registeredEvents);
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$4,
      eventMap: eventMap$4,
      prevProps: prevProps,
      nextProps: this.props,
      instance: this.state.heatmapLayer
    });
  };
  HeatmapLayer.prototype.componentWillUnmount = function () {
    if (this.state.heatmapLayer !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.heatmapLayer);
      }
      unregisterEvents(this.registeredEvents);
      this.state.heatmapLayer.setMap(null);
    }
  };
  HeatmapLayer.prototype.render = function () {
    return null;
  };
  HeatmapLayer.contextType = MapContext;
  return HeatmapLayer;
}(React.PureComponent);
var eventMap$3 = {
  onCloseClick: 'closeclick',
  onPanoChanged: 'pano_changed',
  onPositionChanged: 'position_changed',
  onPovChanged: 'pov_changed',
  onResize: 'resize',
  onStatusChanged: 'status_changed',
  onVisibleChanged: 'visible_changed',
  onZoomChanged: 'zoom_changed'
};
var updaterMap$3 = {
  register: function register(instance, provider, options) {
    instance.registerPanoProvider(provider, options);
  },
  links: function links(instance, _links) {
    instance.setLinks(_links);
  },
  motionTracking: function motionTracking(instance, _motionTracking) {
    instance.setMotionTracking(_motionTracking);
  },
  options: function options(instance, _options13) {
    instance.setOptions(_options13);
  },
  pano: function pano(instance, _pano) {
    instance.setPano(_pano);
  },
  position: function position(instance, _position4) {
    instance.setPosition(_position4);
  },
  pov: function pov(instance, _pov) {
    instance.setPov(_pov);
  },
  visible: function visible(instance, _visible7) {
    instance.setVisible(_visible7);
  },
  zoom: function zoom(instance, _zoom2) {
    instance.setZoom(_zoom2);
  }
};
var StreetViewPanorama = function (_super) {
  __extends(StreetViewPanorama, _super);
  function StreetViewPanorama() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.state = {
      streetViewPanorama: null
    };
    _this.setStreetViewPanoramaCallback = function () {
      if (_this.state.streetViewPanorama !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.streetViewPanorama);
      }
    };
    return _this;
  }
  StreetViewPanorama.prototype.componentDidMount = function () {
    var _a, _b;
    var streetViewPanorama = (_b = (_a = this.context) === null || _a === void 0 ? void 0 : _a.getStreetView()) !== null && _b !== void 0 ? _b : null;
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$3,
      eventMap: eventMap$3,
      prevProps: {},
      nextProps: this.props,
      instance: streetViewPanorama
    });
    this.setState(function () {
      return {
        streetViewPanorama: streetViewPanorama
      };
    }, this.setStreetViewPanoramaCallback);
  };
  StreetViewPanorama.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.streetViewPanorama !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$3,
        eventMap: eventMap$3,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.streetViewPanorama
      });
    }
  };
  StreetViewPanorama.prototype.componentWillUnmount = function () {
    if (this.state.streetViewPanorama !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.streetViewPanorama);
      }
      unregisterEvents(this.registeredEvents);
      this.state.streetViewPanorama.setVisible(false);
    }
  };
  StreetViewPanorama.prototype.render = function () {
    return null;
  };
  StreetViewPanorama.contextType = MapContext;
  return StreetViewPanorama;
}(React.PureComponent);
var StreetViewService = function (_super) {
  __extends(StreetViewService, _super);
  function StreetViewService() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.state = {
      streetViewService: null
    };
    _this.setStreetViewServiceCallback = function () {
      if (_this.state.streetViewService !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.streetViewService);
      }
    };
    return _this;
  }
  StreetViewService.prototype.componentDidMount = function () {
    var streetViewService = new google.maps.StreetViewService();
    this.setState(function setStreetViewService() {
      return {
        streetViewService: streetViewService
      };
    }, this.setStreetViewServiceCallback);
  };
  StreetViewService.prototype.componentWillUnmount = function () {
    if (this.state.streetViewService !== null && this.props.onUnmount) {
      this.props.onUnmount(this.state.streetViewService);
    }
  };
  StreetViewService.prototype.render = function () {
    return null;
  };
  StreetViewService.contextType = MapContext;
  return StreetViewService;
}(React.PureComponent);
var DirectionsService = function (_super) {
  __extends(DirectionsService, _super);
  function DirectionsService() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.state = {
      directionsService: null
    };
    _this.setDirectionsServiceCallback = function () {
      if (_this.state.directionsService !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.directionsService);
      }
    };
    return _this;
  }
  DirectionsService.prototype.componentDidMount = function () {
    invariant$1(!!this.props.options, 'DirectionsService expected options object as parameter, but got %s', this.props.options);
    var directionsService = new google.maps.DirectionsService();
    this.setState(function setDirectionsService() {
      return {
        directionsService: directionsService
      };
    }, this.setDirectionsServiceCallback);
  };
  DirectionsService.prototype.componentDidUpdate = function () {
    if (this.state.directionsService !== null) {
      this.state.directionsService.route(this.props.options, this.props.callback);
    }
  };
  DirectionsService.prototype.componentWillUnmount = function () {
    if (this.state.directionsService !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.directionsService);
      }
    }
  };
  DirectionsService.prototype.render = function () {
    return null;
  };
  return DirectionsService;
}(React.PureComponent);
var eventMap$2 = {
  onDirectionsChanged: 'directions_changed'
};
var updaterMap$2 = {
  directions: function directions(instance, _directions) {
    instance.setDirections(_directions);
  },
  map: function map(instance, _map8) {
    instance.setMap(_map8);
  },
  options: function options(instance, _options14) {
    instance.setOptions(_options14);
  },
  panel: function panel(instance, _panel) {
    instance.setPanel(_panel);
  },
  routeIndex: function routeIndex(instance, _routeIndex) {
    instance.setRouteIndex(_routeIndex);
  }
};
var DirectionsRenderer = function (_super) {
  __extends(DirectionsRenderer, _super);
  function DirectionsRenderer() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.state = {
      directionsRenderer: null
    };
    _this.setDirectionsRendererCallback = function () {
      if (_this.state.directionsRenderer !== null) {
        _this.state.directionsRenderer.setMap(_this.context);
        if (_this.props.onLoad) {
          _this.props.onLoad(_this.state.directionsRenderer);
        }
      }
    };
    return _this;
  }
  DirectionsRenderer.prototype.componentDidMount = function () {
    var directionsRenderer = new google.maps.DirectionsRenderer(this.props.options);
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap$2,
      eventMap: eventMap$2,
      prevProps: {},
      nextProps: this.props,
      instance: directionsRenderer
    });
    this.setState(function setDirectionsRenderer() {
      return {
        directionsRenderer: directionsRenderer
      };
    }, this.setDirectionsRendererCallback);
  };
  DirectionsRenderer.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.directionsRenderer !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$2,
        eventMap: eventMap$2,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.directionsRenderer
      });
    }
  };
  DirectionsRenderer.prototype.componentWillUnmount = function () {
    if (this.state.directionsRenderer !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.directionsRenderer);
      }
      unregisterEvents(this.registeredEvents);
      if (this.state.directionsRenderer) {
        this.state.directionsRenderer.setMap(null);
      }
    }
  };
  DirectionsRenderer.prototype.render = function () {
    return jsxRuntime.jsx(jsxRuntime.Fragment, {});
  };
  DirectionsRenderer.contextType = MapContext;
  return DirectionsRenderer;
}(React.PureComponent);
var DistanceMatrixService = function (_super) {
  __extends(DistanceMatrixService, _super);
  function DistanceMatrixService() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.state = {
      distanceMatrixService: null
    };
    _this.setDistanceMatrixServiceCallback = function () {
      if (_this.state.distanceMatrixService !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.distanceMatrixService);
      }
    };
    return _this;
  }
  DistanceMatrixService.prototype.componentDidMount = function () {
    invariant$1(!!this.props.options, 'DistanceMatrixService expected options object as parameter, but go %s', this.props.options);
    var distanceMatrixService = new google.maps.DistanceMatrixService();
    this.setState(function setDistanceMatrixService() {
      return {
        distanceMatrixService: distanceMatrixService
      };
    }, this.setDistanceMatrixServiceCallback);
  };
  DistanceMatrixService.prototype.componentDidUpdate = function () {
    if (this.state.distanceMatrixService !== null) {
      this.state.distanceMatrixService.getDistanceMatrix(this.props.options, this.props.callback);
    }
  };
  DistanceMatrixService.prototype.componentWillUnmount = function () {
    if (this.state.distanceMatrixService !== null && this.props.onUnmount) {
      this.props.onUnmount(this.state.distanceMatrixService);
    }
  };
  DistanceMatrixService.prototype.render = function () {
    return null;
  };
  return DistanceMatrixService;
}(React.PureComponent);
var eventMap$1 = {
  onPlacesChanged: 'places_changed'
};
var updaterMap$1 = {
  bounds: function bounds(instance, _bounds2) {
    instance.setBounds(_bounds2);
  }
};
var StandaloneSearchBox = function (_super) {
  __extends(StandaloneSearchBox, _super);
  function StandaloneSearchBox() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.containerElement = React.createRef();
    _this.state = {
      searchBox: null
    };
    _this.setSearchBoxCallback = function () {
      if (_this.state.searchBox !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.searchBox);
      }
    };
    return _this;
  }
  StandaloneSearchBox.prototype.componentDidMount = function () {
    invariant$1(!!google.maps.places, 'You need to provide libraries={["places"]} prop to <LoadScript /> component %s', google.maps.places);
    if (this.containerElement !== null && this.containerElement.current !== null) {
      var input = this.containerElement.current.querySelector('input');
      if (input !== null) {
        var searchBox_1 = new google.maps.places.SearchBox(input, this.props.options);
        this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
          updaterMap: updaterMap$1,
          eventMap: eventMap$1,
          prevProps: {},
          nextProps: this.props,
          instance: searchBox_1
        });
        this.setState(function setSearchBox() {
          return {
            searchBox: searchBox_1
          };
        }, this.setSearchBoxCallback);
      }
    }
  };
  StandaloneSearchBox.prototype.componentDidUpdate = function (prevProps) {
    if (this.state.searchBox !== null) {
      unregisterEvents(this.registeredEvents);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap$1,
        eventMap: eventMap$1,
        prevProps: prevProps,
        nextProps: this.props,
        instance: this.state.searchBox
      });
    }
  };
  StandaloneSearchBox.prototype.componentWillUnmount = function () {
    if (this.state.searchBox !== null) {
      if (this.props.onUnmount) {
        this.props.onUnmount(this.state.searchBox);
      }
      unregisterEvents(this.registeredEvents);
    }
  };
  StandaloneSearchBox.prototype.render = function () {
    return jsxRuntime.jsx("div", {
      ref: this.containerElement,
      children: React.Children.only(this.props.children)
    });
  };
  StandaloneSearchBox.contextType = MapContext;
  return StandaloneSearchBox;
}(React.PureComponent);
var eventMap = {
  onPlaceChanged: 'place_changed'
};
var updaterMap = {
  bounds: function bounds(instance, _bounds3) {
    instance.setBounds(_bounds3);
  },
  restrictions: function restrictions(instance, _restrictions) {
    instance.setComponentRestrictions(_restrictions);
  },
  fields: function fields(instance, _fields) {
    instance.setFields(_fields);
  },
  options: function options(instance, _options15) {
    instance.setOptions(_options15);
  },
  types: function types(instance, _types) {
    instance.setTypes(_types);
  }
};
var Autocomplete = function (_super) {
  __extends(Autocomplete, _super);
  function Autocomplete() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    _this.registeredEvents = [];
    _this.containerElement = React.createRef();
    _this.state = {
      autocomplete: null
    };
    _this.setAutocompleteCallback = function () {
      if (_this.state.autocomplete !== null && _this.props.onLoad) {
        _this.props.onLoad(_this.state.autocomplete);
      }
    };
    return _this;
  }
  Autocomplete.prototype.componentDidMount = function () {
    var _a;
    invariant$1(!!google.maps.places, 'You need to provide libraries={["places"]} prop to <LoadScript /> component %s', google.maps.places);
    var input = (_a = this.containerElement.current) === null || _a === void 0 ? void 0 : _a.querySelector('input');
    if (input) {
      var autocomplete_1 = new google.maps.places.Autocomplete(input, this.props.options);
      this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
        updaterMap: updaterMap,
        eventMap: eventMap,
        prevProps: {},
        nextProps: this.props,
        instance: autocomplete_1
      });
      this.setState(function () {
        return {
          autocomplete: autocomplete_1
        };
      }, this.setAutocompleteCallback);
    }
  };
  Autocomplete.prototype.componentDidUpdate = function (prevProps) {
    unregisterEvents(this.registeredEvents);
    this.registeredEvents = applyUpdatersToPropsAndRegisterEvents({
      updaterMap: updaterMap,
      eventMap: eventMap,
      prevProps: prevProps,
      nextProps: this.props,
      instance: this.state.autocomplete
    });
  };
  Autocomplete.prototype.componentWillUnmount = function () {
    if (this.state.autocomplete !== null) {
      unregisterEvents(this.registeredEvents);
    }
  };
  Autocomplete.prototype.render = function () {
    return jsxRuntime.jsx("div", {
      ref: this.containerElement,
      className: this.props.className,
      children: React.Children.only(this.props.children)
    });
  };
  Autocomplete.defaultProps = {
    className: ''
  };
  Autocomplete.contextType = MapContext;
  return Autocomplete;
}(React.PureComponent);

(function (GooglePlacesServices) {
  GooglePlacesServices["AUTOCOMPLETE"] = "autocomplete";
  GooglePlacesServices["PLACES"] = "places";
})(exports.GooglePlacesServices || (exports.GooglePlacesServices = {}));
var libraries = ['places'];
var useGoogleMap$1 = function useGoogleMap(services, apiKey) {
  var _React$useState = React.useState(undefined),
    autocompleteService = _React$useState[0],
    setAutocompleteService = _React$useState[1];
  var _React$useState2 = React.useState(undefined),
    placesService = _React$useState2[0],
    setPlacesService = _React$useState2[1];
  var _useLoadScript = useLoadScript({
      libraries: libraries,
      region: 'CH',
      preventGoogleFontsLoading: false,
      googleMapsApiKey: apiKey || String(process.env.REACT_APP_GOOGLE_API_KEY)
    }),
    isLoaded = _useLoadScript.isLoaded,
    loadError = _useLoadScript.loadError;
  React.useEffect(function () {
    if (isLoaded && google && services && !placesService && !autocompleteService) {
      services.forEach(function (s) {
        switch (s) {
          case 'autocomplete':
            setAutocompleteService(new google.maps.places.AutocompleteService());
            break;
          case 'places':
            setPlacesService(new google.maps.places.PlacesService(document.createElement('div')));
            break;
        }
      });
    }
  }, [isLoaded, services, placesService, autocompleteService]);
  return {
    isLoaded: isLoaded,
    loadError: loadError,
    autocompleteService: autocompleteService,
    placesService: placesService
  };
};

(function (IPasswordStrengthIndicator) {
  IPasswordStrengthIndicator[IPasswordStrengthIndicator["NO_PASSWORD"] = 0] = "NO_PASSWORD";
  IPasswordStrengthIndicator[IPasswordStrengthIndicator["VERY_WEAK"] = 1] = "VERY_WEAK";
  IPasswordStrengthIndicator[IPasswordStrengthIndicator["WEAK"] = 2] = "WEAK";
  IPasswordStrengthIndicator[IPasswordStrengthIndicator["SOLID"] = 3] = "SOLID";
  IPasswordStrengthIndicator[IPasswordStrengthIndicator["STRONG"] = 4] = "STRONG";
  IPasswordStrengthIndicator[IPasswordStrengthIndicator["VERY_STRONG"] = 5] = "VERY_STRONG";
})(exports.IPasswordStrengthIndicator || (exports.IPasswordStrengthIndicator = {}));

var getStrengthIndicatroColor = function getStrengthIndicatroColor(indicatorColorMap, strength) {
  return indicatorColorMap[strength] || indicatorColorMap[exports.IPasswordStrengthIndicator.NO_PASSWORD];
};
var getStrengthIndicatroWidth = function getStrengthIndicatroWidth(strength) {
  return 100 / (Object.values(exports.IPasswordStrengthIndicator).filter(function (value) {
    return typeof value == 'number';
  }).length - 1) * strength + "%";
};
var getStrengthIndicatrTranslationKey = function getStrengthIndicatrTranslationKey(indicatorTranslationKeysMap, strength) {
  return indicatorTranslationKeysMap[strength];
};
var hasOnlyOneRepeatingCharacter = function hasOnlyOneRepeatingCharacter(inputString) {
  if (!inputString.length) {
    return false;
  }
  var stringArray = inputString.split('');
  var firstChar = stringArray[0];
  return stringArray.every(function (_char) {
    return _char === firstChar;
  });
};
var ENTROPY_UP_TO_LIMITS = {
  VERY_WEAK: 35,
  WEAK: 45,
  SOLID: 55,
  STRONG: 65
};
var getPasswordStrength = function getPasswordStrength(_ref) {
  var _ref$poolSize = _ref.poolSize,
    poolSize = _ref$poolSize === void 0 ? 0 : _ref$poolSize,
    _ref$passwordLength = _ref.passwordLength,
    passwordLength = _ref$passwordLength === void 0 ? 0 : _ref$passwordLength,
    _ref$isOneRepeatingCh = _ref.isOneRepeatingChar,
    isOneRepeatingChar = _ref$isOneRepeatingCh === void 0 ? false : _ref$isOneRepeatingCh,
    _ref$passwordEntropy = _ref.passwordEntropy,
    passwordEntropy = _ref$passwordEntropy === void 0 ? 0 : _ref$passwordEntropy,
    _ref$isKnownPassword = _ref.isKnownPassword,
    isKnownPassword = _ref$isKnownPassword === void 0 ? false : _ref$isKnownPassword;
  if (!passwordLength || !poolSize) {
    return exports.IPasswordStrengthIndicator.NO_PASSWORD;
  }
  if (isKnownPassword || isOneRepeatingChar || passwordEntropy < ENTROPY_UP_TO_LIMITS.VERY_WEAK) {
    return exports.IPasswordStrengthIndicator.VERY_WEAK;
  }
  if (passwordEntropy < ENTROPY_UP_TO_LIMITS.WEAK) {
    return exports.IPasswordStrengthIndicator.WEAK;
  }
  if (passwordEntropy < ENTROPY_UP_TO_LIMITS.SOLID) {
    return exports.IPasswordStrengthIndicator.SOLID;
  }
  if (passwordEntropy < ENTROPY_UP_TO_LIMITS.STRONG) {
    return exports.IPasswordStrengthIndicator.STRONG;
  }
  if (passwordEntropy >= ENTROPY_UP_TO_LIMITS.STRONG) {
    return exports.IPasswordStrengthIndicator.VERY_STRONG;
  }
  return exports.IPasswordStrengthIndicator.NO_PASSWORD;
};
var sanitizeToNumber = function sanitizeToNumber(value) {
  var isRealNumber = typeof value === 'number' && isFinite(value) && !isNaN(value);
  return isRealNumber ? value : 0;
};
var PASSWORD_POOLSIZE_OPTIONS = [{
  regex: /[a-z]+/,
  poolSize: 26
}, {
  regex: /[A-Z]+/,
  poolSize: 26
}, {
  regex: /[0-9]+/,
  poolSize: 10
}, {
  regex: /[$@#&!*?%^()\-_+=<>{}[\]:;'"|~`,.\\ ]+/,
  poolSize: 32
}];

var usePasswordStrength = function usePasswordStrength() {
  var _React$useState = React.useState(exports.IPasswordStrengthIndicator.NO_PASSWORD),
    passwordStrength = _React$useState[0],
    setPasswordStrength = _React$useState[1];
  var handlePasswordChange = React.useCallback(function (password, isKnownPassword) {
    if (isKnownPassword === void 0) {
      isKnownPassword = false;
    }
    var poolSize = PASSWORD_POOLSIZE_OPTIONS.reduce(function (accSize, _ref) {
      var regex = _ref.regex,
        optionPoolSize = _ref.poolSize;
      return password.match(regex) ? accSize + optionPoolSize : accSize;
    }, 0);
    var passwordLength = password.length;
    var isOneRepeatingChar = hasOnlyOneRepeatingCharacter(password);
    var passwordEntropy = passwordLength * Math.log2(poolSize);
    var updatedStrength = getPasswordStrength({
      poolSize: poolSize,
      passwordLength: passwordLength,
      isOneRepeatingChar: isOneRepeatingChar,
      passwordEntropy: sanitizeToNumber(passwordEntropy),
      isKnownPassword: isKnownPassword
    });
    setPasswordStrength(updatedStrength);
  }, []);
  return {
    passwordStrength: passwordStrength,
    handlePasswordChange: handlePasswordChange
  };
};

exports.getStrengthIndicatrTranslationKey = getStrengthIndicatrTranslationKey;
exports.getStrengthIndicatroColor = getStrengthIndicatroColor;
exports.getStrengthIndicatroWidth = getStrengthIndicatroWidth;
exports.useBehaviorSubject = useBehaviorSubject;
exports.useGoogleMap = useGoogleMap$1;
exports.useLocalStorage = useLocalStorage;
exports.usePasswordStrength = usePasswordStrength;
exports.useQueryState = useQueryState;
exports.useSessionStorage = useSessionStorage;
//# sourceMappingURL=index.js.map
